import {
  type InfiniteData,
  type QueryClient,
  type QueryKey,
  useMutation,
  useInfiniteQuery,
  useQuery as useReactQuery,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { atom, useAtomValue, useSetAtom } from "jotai";
import { atomFamily, atomWithStorage, selectAtom } from "jotai/utils";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { api as cyranoApi, buildUrl } from "@/core/api/client";
import { useSession } from "@/core/auth/useSession";
import {
  liveTurnsAtom,
  pruneLiveTurns,
} from "@/panels/fan/trace/live";
import { useSSE } from "@/core/hooks/useSSE";
import { i18n } from "@/core/i18n";
import { langueDuChatteur } from "@/panels/fan/traduction/useTraduction";
import { useCreatorsList } from "@/panels/creators/atoms";
import { COLOR_PALETTE } from "@/panels/creators/mockData";

import {
  CANAL_FILTRE_DEFAUT,
  EMPTY_FILTERS,
  type FiltersState,
} from "./FiltersPopover";
import { type FanTagLite, type MockConversation, type Stage, type Author, type ContentMode, type SignalCategory, type MomentAReprendre } from "./mockData";

// `ContentMode` est DÉFINI dans `mockData` et ré-exporté ici : `ConvStatusBar`
// l'importe depuis ce module avec le reste de ses hooks, et un `import type`
// ne ré-exporte rien tout seul. Sans cette ligne, `tsc -b` casse (TS2459) et
// le build Dokploy échoue en silence — mesuré le 27-08-2026 : la PR qui a
// introduit cet import n'est jamais arrivée sur dozka.app.
export type { ContentMode };
import { useShiftActivityHeartbeat } from "./useShiftActivityHeartbeat";

/** Brouillons du composer PAR conversation : le texte tapé mais NON envoyé,
 *  mémorisé (localStorage, par chatter/appareil) et restauré quand on rouvre
 *  la discussion. Réactif (la liste pourra afficher un badge « Brouillon ») +
 *  synchronisé entre onglets. Clé = conversationId, valeur = texte courant.
 *  Vidé à l'envoi (le composer fait `setText("")` → suppression de la clé). */
export const conversationDraftsAtom = atomWithStorage<Record<string, string>>(
  "cockpit:conversation-drafts",
  {},
);

/**
 * 🎛️ Le filtre persisté — clé v3 depuis le 12-09-2026, quand les filtres
 * sont passés de quatorze à cinq (cf. `FiltersPopover.tsx`).
 *
 * 🩸 Une clé NEUVE, et les deux anciennes effacées au chargement : un profil
 * qui gardait « créatrice = Trista » ou « fenêtre = 24 h » sous la v2 aurait
 * continué de filtrer la liste sans plus aucune case pour le dire ni le
 * défaire — le filtre par défaut invisible, exactement ce que la réduction
 * cherche à retirer. On repart du défaut, qui est « aucun filtre ».
 *
 * Enveloppé : un localStorage indisponible ne doit pas empêcher le panneau
 * de s'ouvrir.
 */
// v4 (29-10-2026) : le défaut du canal passe à « tous » (les chatteurs vendent sur X) ;
// un v3 enregistré portait `telegram` comme défaut, donc il s'oublie, comme v1 et v2.
const CLE_FILTRES_V4 = "cockpit:conversation-filters:v4";
(function oublierLesAnciensFiltres() {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.removeItem("cockpit:conversation-filters:v1");
    window.localStorage.removeItem("cockpit:conversation-filters:v2");
    window.localStorage.removeItem("cockpit:conversation-filters:v3");
  } catch {
    // Stockage indisponible : rien à oublier.
  }
})();

export const conversationFiltersAtom = atomWithStorage<FiltersState>(
  CLE_FILTRES_V4,
  EMPTY_FILTERS,
);

/** Couleur deterministe derivee du slug du creator — meme algorithme que
 *  `creators/atoms.ts#colorFromSlug` (duplique ici tant que cet export
 *  n'est pas dans la branche). Garde le meme creator avec la meme couleur
 *  entre le Creators panel et le Conversations panel. */
function colorFromSlug(slug: string): string {
  let hash = 0;
  for (let i = 0; i < slug.length; i++) {
    hash = (hash * 31 + slug.charCodeAt(i)) >>> 0;
  }
  return COLOR_PALETTE[hash % COLOR_PALETTE.length];
}

/**
 * Liste des conversations partagee entre panels (Conversations + Fan).
 *
 * Phase 1 wiring : `useConversationsList()` hydrate cet atom depuis FastAPI
 * Cyrano `/api/conversations`. Le FanPanel lit l'atom pour retrouver le
 * contexte de la conversation cliquee.
 */
export const conversationsAtom = atom<MockConversation[]>([]);

/**
 * Sélecteur par conversation — renvoie LA conversation pour cet id (ou null).
 *
 * Pourquoi : `conversationsAtom` est un gros tableau lu par des composants
 * "toujours montés" (TabStrip de chaque groupe, FanPanel/FanSubViewPanel
 * ouverts). Si chacun lit le tableau entier via `useAtomValue(conversationsAtom)`
 * + `.find()`, le moindre heartbeat (refetch/SSE qui produit un NOUVEAU tableau)
 * les re-rend TOUS — alors qu'ils ne s'intéressent qu'à UNE conversation.
 *
 * `selectAtom` compare le résultat en `Object.is` : couplé à la stabilité
 * référentielle de `projected` (cf `useConversationsList` — un item inchangé
 * garde la MÊME référence d'un render à l'autre), un consommateur ne re-render
 * QUE quand SA conversation change réellement. `atomFamily` dédoublonne par id.
 */
/**
 * Conversations HYDRATÉES à la demande, hors page courante de la liste.
 *
 * Le panneau fan ne montre pas forcément une conversation que la liste
 * contient : la vue « toutes les conversations » laisse en ouvrir une de
 * juillet puis la retire dès qu'on referme le filtre, la table des commandes en
 * ouvre une qui n'a aucune raison d'être dans la page, et un onglet fan survit
 * au rechargement alors que la liste repart de zéro. Dans ces cas le panneau
 * affichait « clique sur un fan » à quelqu'un qui venait de cliquer.
 *
 * Rangées À PART de `conversationsAtom` — et pas fusionnées dedans — parce que
 * la GRILLE doit continuer à montrer exactement ce que les filtres ont demandé.
 * Une conversation hydratée est une réponse à « montre-moi celle-ci », jamais un
 * élément de liste.
 */
export const hydratedConversationsAtom = atom<Record<string, MockConversation>>({});

/** Range une conversation hydratée (no-op si la liste la porte déjà). */
export const hydrateConversationAtom = atom(
  null,
  (get, set, conv: MockConversation) => {
    if (get(conversationsAtom).some((c) => c.id === conv.id)) return;
    const known = get(hydratedConversationsAtom)[conv.id];
    // Comparaison par référence : `useQuery` garde la même donnée entre deux
    // renders, on ne veut pas re-notifier les lecteurs pour rien.
    if (known === conv) return;
    set(hydratedConversationsAtom, { ...get(hydratedConversationsAtom), [conv.id]: conv });
  },
);

export const conversationByIdAtom = atomFamily((conversationId: string) =>
  selectAtom(
    // La liste d'abord (elle est fraîche, le live SSE la patche), le fond
    // d'hydratation ensuite. UN seul point de lecture : le header, la barre de
    // statut et le menu contextuel passent tous par ici — les réparer un par un
    // rouvrirait le trou au prochain lecteur.
    atom((get) => ({
      liste: get(conversationsAtom),
      hydratees: get(hydratedConversationsAtom),
    })),
    ({ liste, hydratees }): MockConversation | null =>
      liste.find((c) => c.id === conversationId) ?? hydratees[conversationId] ?? null,
  ),
);

/* ====================================================================== */
/* Flow notice — notif flow ephemere par conversation                      */
/* ====================================================================== */

/**
 * Derniere notification flow recue via SSE (`type: "flow_notice"`), miroir
 * cockpit de l'action `notify_flow_group` cote backend. Le SSE est global
 * (une seule connexion, cf `useConversationEvents`) → on relaie la notice via
 * cet atom jusqu'a la `ConvStatusBar` de la conversation concernee, qui la
 * rend en banniere ephemere (meme flash que le STOP/GO, cf `useCyranoControl`).
 *
 * `nonce` strictement croissant : distingue deux notices au texte identique
 * pour que le consommateur (`useEffect`) re-declenche le flash a chaque fois.
 */
export interface FlowNotice {
  conversationId: string;
  content: string;
  nonce: number;
}

export const flowNoticeAtom = atom<FlowNotice | null>(null);

/**
 * Indicateur « le fan écrit… » : `conversation_id` → timestamp (ms) d'expiration.
 * Alimenté par les events SSE `typing` (backend MTProto `handle_incoming_typing`).
 * État UI éphémère (pas un message) ; les entrées expirées sont élaguées à
 * chaque set, et `useFanIsTyping` se re-rend pile à l'expiration.
 */
export const typingByConversationAtom = atom<Record<string, number>>({});

/** True tant que le fan de `conversationId` est en train d'écrire. Se met à
 *  jour tout seul à l'arrivée d'un event typing ET à son expiration. */
export function useFanIsTyping(conversationId: string | null): boolean {
  const map = useAtomValue(typingByConversationAtom);
  const until = conversationId ? (map[conversationId] ?? 0) : 0;
  const [, tick] = useState(0);
  useEffect(() => {
    const remaining = until - Date.now();
    if (remaining <= 0) return;
    const t = setTimeout(() => tick((n) => n + 1), remaining);
    return () => clearTimeout(t);
  }, [until]);
  return until > Date.now();
}

/* ====================================================================== */
/* Cyrano countdown — compte à rebours avant l'envoi du prochain message   */
/* ====================================================================== */

export type CyranoCountdownPhase = "generating" | "waiting" | "read" | "typing";

/**
 * Compte à rebours live de la timeline « délai humain » de Cyrano pour UNE
 * conversation. Alimenté par les events SSE `cyrano_scheduled/read/typing`
 * (backend `delay_timeline.py`). État UI éphémère (pas un message).
 *
 * **Anti-décalage** : toutes les échéances sont ancrées sur l'horloge CLIENT à
 * la réception (`Date.now()` + durée serveur `deliver_at − now_serveur`). Le
 * compteur est donc immunisé contre le clock skew client/serveur et ne dépend
 * que de la durée, calculée côté serveur.
 */
export interface CyranoCountdown {
  /** ms (horloge client) — début de l'attente (ancrage de la barre de progression). */
  startAt: number;
  /** ms (horloge client) — livraison prévue du message. */
  deliverAt: number;
  /** ms (horloge client) — moment du « vu ». */
  readAt: number;
  /** ms (horloge client) — moment où Cyrano commence à écrire. */
  typingAt: number;
  /** Phase confirmée par event (fallback : dérivée des jalons dans le hook). */
  phase: CyranoCountdownPhase;
  /** Texte du brouillon retenu (généré, éventuellement édité). null si l'event
   *  `cyrano_scheduled` a été manqué → à hydrater via GET .../cyrano-draft. */
  draftText: string | null;
}

export const cyranoCountdownByConversationAtom = atom<
  Record<string, CyranoCountdown>
>({});

/**
 * Horodatage (ms, horloge client) de la DERNIÈRE annulation Cyrano par
 * conversation — état UI éphémère, alimenté par l'event SSE `cyrano_cancelled`.
 *
 * Additif (SPEC §10 — La Régie) : le handler `cyrano_cancelled` supprime déjà le
 * compteur associé (comportement INCHANGÉ) ; on note EN PLUS l'instant de
 * l'annulation pour qu'un consommateur (le chip d'état agent de La Régie) puisse
 * afficher un « annulé » transitoire, sans nouveau fetch. Aucun code existant ne
 * lit cet atom → comportement de l'app Conversations strictement identique.
 */
export const cyranoCancelledAtByConversationAtom = atom<Record<string, number>>(
  {},
);

/** Grâce (ms) après `deliverAt` avant d'oublier un compteur non explicitement
 *  effacé (l'envoi émet normalement un `new_message` qui l'efface). */
const CYRANO_COUNTDOWN_GRACE_MS = 4_000;

/** Sécurité : durée max d'affichage de « Cyrano génère… » si aucun
 *  `cyrano_scheduled` ne suit (génération échouée / job abandonné). */
const CYRANO_GENERATING_TIMEOUT_MS = 45_000;

/**
 * Ancre une échéance serveur sur l'horloge client : renvoie `Date.now()` +
 * (deliverIso − nowIso), les deux bornes venant du serveur (donc sans skew).
 * Renvoie `NaN` si une des dates est invalide.
 */
export function anchorCyranoDeadline(
  deadlineIso: string | null | undefined,
  serverNowIso: string | null | undefined,
): number {
  if (!deadlineIso || !serverNowIso) return NaN;
  const deadline = Date.parse(deadlineIso);
  const serverNow = Date.parse(serverNowIso);
  if (Number.isNaN(deadline) || Number.isNaN(serverNow)) return NaN;
  return Date.now() + Math.max(0, deadline - serverNow);
}

/** Élague les compteurs dont `deliverAt` est dépassé (+ grâce). */
function pruneCountdowns(
  prev: Record<string, CyranoCountdown>,
): Record<string, CyranoCountdown> {
  const now = Date.now();
  const next: Record<string, CyranoCountdown> = {};
  for (const [k, v] of Object.entries(prev)) {
    if (v.deliverAt + CYRANO_COUNTDOWN_GRACE_MS > now) next[k] = v;
  }
  return next;
}

export interface CyranoCountdownView {
  phase: CyranoCountdownPhase;
  /** ms restantes avant livraison (≥ 0). */
  remainingMs: number;
  deliverAt: number;
  /** Progression 0–100 de l'attente (début → livraison). */
  percent: number;
  /** Texte du brouillon retenu (null si pas encore hydraté). */
  draftText: string | null;
}

/**
 * Compte à rebours live pour `conversationId`, ou `null` si aucun envoi n'est
 * programmé. Tick toutes les 500 ms tant qu'un compteur est actif ; s'auto-
 * efface `CYRANO_COUNTDOWN_GRACE_MS` après l'échéance (filet si l'event de
 * livraison n'est jamais arrivé).
 *
 * Perf : isolé par conversation via `selectAtom` (Object.is sur l'entrée) → une
 * carte de la grille ne re-render QUE quand SON compteur change, pas à chaque
 * event Cyrano d'une autre conversation.
 */
export function useCyranoCountdown(
  conversationId: string | null,
): CyranoCountdownView | null {
  const entryAtom = useMemo(
    () =>
      selectAtom(cyranoCountdownByConversationAtom, (m) =>
        conversationId ? m[conversationId] : undefined,
      ),
    [conversationId],
  );
  const rec = useAtomValue(entryAtom);
  const [, tick] = useState(0);
  useEffect(() => {
    if (!rec) return;
    const id = setInterval(() => tick((n) => n + 1), 500);
    return () => clearInterval(id);
  }, [rec]);
  if (!rec) return null;
  const now = Date.now();
  if (now > rec.deliverAt + CYRANO_COUNTDOWN_GRACE_MS) return null;
  // Phase : la plus avancée entre celle confirmée par event et celle déduite
  // des jalons (couvre le cas d'un event read/typing manqué).
  let phase: CyranoCountdownPhase = rec.phase;
  if (phase === "waiting" && now >= rec.readAt) phase = "read";
  if (now >= rec.typingAt) phase = "typing";
  const span = rec.deliverAt - rec.startAt;
  const percent =
    span > 0 ? Math.min(100, Math.max(0, ((now - rec.startAt) / span) * 100)) : 100;
  return {
    phase,
    remainingMs: Math.max(0, rec.deliverAt - now),
    deliverAt: rec.deliverAt,
    percent,
    draftText: rec.draftText,
  };
}

/**
 * Instant (ms, horloge client) de la dernière annulation Cyrano pour
 * `conversationId`, ou `null` si aucune. Isolé par conversation via `selectAtom`
 * (même patron que `useCyranoCountdown`) → un cancel sur une autre conversation
 * ne re-render pas ce consommateur. Alimenté par l'event SSE `cyrano_cancelled`.
 */
export function useCyranoCancelledAt(
  conversationId: string | null,
): number | null {
  const entryAtom = useMemo(
    () =>
      selectAtom(cyranoCancelledAtByConversationAtom, (m) =>
        conversationId ? m[conversationId] : undefined,
      ),
    [conversationId],
  );
  return useAtomValue(entryAtom) ?? null;
}

/** Injecte / met à jour le texte du brouillon dans le compteur d'une conv (après
 *  un GET .../cyrano-draft d'hydratation quand l'event initial a été manqué). */
export function useSetCyranoDraftText(): (conversationId: string, text: string) => void {
  const set = useSetAtom(cyranoCountdownByConversationAtom);
  return useCallback(
    (conversationId: string, text: string) => {
      set((prev) => {
        const rec = prev[conversationId];
        if (!rec || rec.draftText === text) return prev;
        return { ...prev, [conversationId]: { ...rec, draftText: text } };
      });
    },
    [set],
  );
}

/**
 * Retire le compteur d'une conversation : la relecture est CLOSE côté client.
 *
 * Appelé quand le backend a ACCEPTÉ (202) un « envoyer maintenant ». Sans lui,
 * la barre restait affichée avec son compte à rebours qui continue de tourner
 * jusqu'au `new_message` (out) — c'est-à-dire après TOUT le reste du pipeline
 * (re-génération stale, gardes, workflow, envoi Telegram) : plusieurs secondes
 * pendant lesquelles rien à l'écran ne distingue « c'est parti » de « mon clic
 * n'a rien fait ». `cyrano_cancelled` rend ce service à « Annuler » depuis le
 * serveur, et c'est exactement pourquoi Annuler passait pour le seul bouton qui
 * marche.
 */
export function useClearCyranoCountdown(): (conversationId: string) => void {
  const set = useSetAtom(cyranoCountdownByConversationAtom);
  return useCallback(
    (conversationId: string) => {
      set((prev) => {
        if (!(conversationId in prev)) return prev;
        const next = { ...prev };
        delete next[conversationId];
        return next;
      });
    },
    [set],
  );
}

/**
 * Derniere notice flow pour `conversationId`, ou `null` si la derniere notice
 * recue concerne une autre conversation. A consommer via `useEffect` sur le
 * `nonce` (cf `ConvStatusBar`).
 */
export function useFlowNotice(conversationId: string): FlowNotice | null {
  const notice = useAtomValue(flowNoticeAtom);
  return notice && notice.conversationId === conversationId ? notice : null;
}


/* ====================================================================== */
/* Décision proposée — carte de confirmation (mode PROPOSE)               */
/*                                                                        */
/* Cf PLAN_CYRANO_MODE_PROPOSE.md Annexe A. DORMANT tant que le backend   */
/* Phase 1 n'émet pas l'event SSE `decision_proposal` : l'atom reste vide, */
/* `useDecisionProposal` renvoie toujours null, aucune carte ne s'affiche.*/
/* ====================================================================== */

/** Alternative média d'un variant (dispatch v2 — cf CONTRAT RÉGIE DISPATCH
 *  "Variants — ajouts") : ≤4, EXCLUT le média choisi par le variant.
 *  `thumbPath` pointe `/api/vault/items/{id}/thumb`. */
export interface DecisionProposalMediaAlternative {
  mediaId: string;
  label: string;
  priceCents: number | null;
  rankScore: number;
  thumbPath: string;
  /** Étiquettes du ranker (mêmes slugs stables que `VaultItem.rank_reasons`,
   *  ex. "already_sent", "next_in_collection") — optionnel, absent tant que
   *  le backend dispatch ne les peuple pas. */
  reasons?: string[];
}

export interface DecisionProposalPriceBounds {
  minCents: number;
  maxCents: number;
}

/** Un variant proposé, normalisé pour la carte (miroir camelCase de
 *  `ApiDecisionVariant`). Les champs `mediaId`/`mediaAlternatives`/
 *  `priceBounds`/`messageText` sont les ajouts dispatch v2 (CONTRAT RÉGIE
 *  DISPATCH, "Variants — ajouts" : sous-ensemble STRICT identique en SSE et
 *  en GET /api/regie/jobs/mine) — optionnels, absents tant qu'un event/route
 *  ne les fournit pas (aujourd'hui : uniquement peuplés par le mapping
 *  dispatch, retirée avec La Régie le 08-10-2026). */
export interface DecisionProposalVariant {
  variantId: string;
  action: string;
  label: string;
  priceCents: number | null;
  mediaThumbUrl: string | null;
  mediaId?: string | null;
  mediaAlternatives?: DecisionProposalMediaAlternative[];
  priceBounds?: DecisionProposalPriceBounds | null;
  /** Étiquettes du ranker pour le média PROPOSÉ (`mediaId`) — même contrat
   *  que `DecisionProposalMediaAlternative.reasons`. Optionnel. */
  mediaReasons?: string[] | null;
  /** Variant "parole" (`action: "speech_reply"`) : texte EXACT du writer. */
  messageText?: string | null;
  /** Variant "parole" uniquement : signaux de forme détectés côté backend
   *  (ex. "vouvoiement") — ajout dispatch v2 (mission A2 « AMENDEMENTS
   *  07-10 soir », même statut que `messageText` : optionnel, absent tant
   *  qu'un event/route ne le fournit pas, aujourd'hui uniquement peuplé par
   *  le mapping dispatch de l'ancienne Régie, retirée le 08-10-2026). */
  speechFlags?: string[] | null;
}

/**
 * Proposition de décision ACTIVE pour une conversation (event SSE
 * `decision_proposal`, `status: "proposed"`). Une seule par conversation —
 * une nouvelle proposition REMPLACE l'ancienne (anti-fatigue, cf le plan
 * §1). État UI éphémère (pas persisté), miroir camelCase du payload SSE.
 */
export interface DecisionProposal {
  proposalId: string;
  /** Libellé lisible du stage du funnel (ex: "Fin de script1"). null si le
   *  backend ne l'a pas fourni — la carte retombe sur un libellé générique. */
  stageLabel: string | null;
  /** ISO d'expiration, si fournie par le backend. Sert uniquement à
   *  l'hygiène cliente (élagage sur reconnect SSE) — PAS affichée comme
   *  compte à rebours dans la carte (v1 = action seule, cf §4 du plan). */
  expiresAt: string | null;
  variants: DecisionProposalVariant[];
  /** Raisonnement du décisionnel (dispatch v2 — `PendingDecisionOut.reasoning`,
   *  cf CONTRAT RÉGIE DISPATCH). Optionnel, non peuplé par le chemin SSE v1. */
  reasoning?: string | null;
  /** État DRIFT-ZÉRO que Cyrano avait en fenêtre à la génération (sous-ensemble
   *  curé de `context_payload.fan`, figé sur le fil — `PendingDecisionOut.
   *  context_snapshot`). `null` hors dispatch / ancien fil. */
  contextSnapshot?: RegieContextSnapshot | null;
}

/** Miroir wire de `DecisionContextSnapshotOut` (`app/api/routes/decisions.py`) —
 *  passthrough snake_case. L'état décisionnel FIGÉ à la génération. */
export interface RegieContextSnapshot {
  purchase_signal: number | null;
  trust_level: number | null;
  lifetime_value_band: string | null;
  n_purchased_items: number | null;
  n_refused_items: number | null;
  has_pending_order: boolean | null;
  emotion: string | null;
  topic: string | null;
  profile: Record<string, unknown> | null;
}

export const decisionProposalByConversationAtom = atom<
  Record<string, DecisionProposal>
>({});

/**
 * Portefeuille de La Régie (mission A2, point 4) — dernier event SSE
 * `decision_outcome` reçu, miroir camelCase du payload wire. `nonce`
 * strictement croissant (même patron que `FlowNotice`) : distingue deux
 * events aux montants identiques pour que le consommateur (`useEffect` côté
 * consommateur) re-déclenche à chaque fois, même en rafale.
 * `null` tant qu'aucun event n'est encore arrivé cette session.
 */
export interface RegieDecisionOutcomeEvent {
  conversationId: string;
  proposalId: string | null;
  orderId: string | null;
  memberId: string | null;
  amountCents: number;
  currency: "USD" | "EUR";
  resolvedAt: string | null;
  paidAt: string | null;
  nonce: number;
}

export const regieDecisionOutcomeAtom = atom<RegieDecisionOutcomeEvent | null>(null);

/**
 * Nonce strictement croissant, incrémenté à CHAQUE reconnexion SSE du flux
 * `/api/conversations/events` (jamais la connexion initiale) — même patron
 * que `RegieDecisionOutcomeEvent.nonce` : un consommateur compare au dernier
 * nonce vu (`useRef`) pour ne réagir qu'aux NOUVELLES reconnexions, jamais au
 * montage. Additif (mission « heartbeat présence robuste ») : aujourd'hui lu
 * par la présence dispatch (retirée avec La Régie) pour déclencher un
 * heartbeat immédiat après une coupure au lieu d'attendre le prochain tick de
 * 30s — le refetch de `["regie","jobs","mine"]` est câblé juste en dessous,
 * dans le MÊME `onReconnect` (extension additive symétrique à l'invalidation
 * existante sur l'event `regie_job`). `0` tant qu'aucune reconnexion n'est
 * survenue cette session.
 */
export const sseReconnectNonceAtom = atom(0);

/** Élague les propositions dont `expiresAt` est dépassé. Les entrées sans
 *  échéance connue (`expiresAt: null`) sont conservées telles quelles — seul
 *  l'event dédié (`resolved`) ou le filet `new_message` sortant les efface. */
function pruneDecisionProposals(
  prev: Record<string, DecisionProposal>,
): Record<string, DecisionProposal> {
  const now = Date.now();
  const next: Record<string, DecisionProposal> = {};
  for (const [k, v] of Object.entries(prev)) {
    const expiresMs = v.expiresAt ? Date.parse(v.expiresAt) : NaN;
    if (Number.isNaN(expiresMs) || expiresMs > now) next[k] = v;
  }
  return next;
}

/** Proposition de décision active pour `conversationId`, ou `null`. Isolé par
 *  conversation via `selectAtom` (même patron que `useCyranoCountdown`) — une
 *  carte de la grille / le panneau Fan ne re-render que pour SA conversation. */
export function useDecisionProposal(
  conversationId: string | null,
): DecisionProposal | null {
  const entryAtom = useMemo(
    () =>
      selectAtom(decisionProposalByConversationAtom, (m) =>
        conversationId ? m[conversationId] : undefined,
      ),
    [conversationId],
  );
  const rec = useAtomValue(entryAtom);
  return rec ?? null;
}

/** Efface localement la proposition d'une conversation (filet 409 : « déjà
 *  résolue » — cf `DecisionProposalCard`). Le SSE reste la source de vérité
 *  nominale ; ceci ne fait qu'anticiper côté client. */
export function useClearDecisionProposal(): (conversationId: string) => void {
  const set = useSetAtom(decisionProposalByConversationAtom);
  return useCallback(
    (conversationId: string) => {
      set((prev) => {
        if (!(conversationId in prev)) return prev;
        const next = { ...prev };
        delete next[conversationId];
        return next;
      });
    },
    [set],
  );
}


/* ====================================================================== */
/* API types — shape de /api/conversations* (cf conversations.py backend)  */
/* ====================================================================== */

interface ApiInviteLinkSource {
  id: string;
  name: string | null;
}

export interface ApiConversationListItem {
  id: string;
  creator_id: string;
  creator_slug: string;
  creator_display_name: string;
  creator_avatar_url: string | null;
  user_id: string;
  fan_name: string;
  fan_avatar_url: string | null;
  /** 🟢 Statut Telegram du fan (29-10-2026) — absent tant que Cyrano n'a pas
   *  déployé le champ. */
  fan_presence?: string | null;
  fan_presence_vu_a?: string | null;
  telegram_chat_id: number | null;
  language: string | null;
  last_message: string | null;
  /** 🌍 `last_message` déjà traduit, par langue de chatteur (09-10-2026).
   *  Absent tant que Cyrano n'a pas déployé le champ. */
  last_message_traductions?: Record<string, string> | null;
  last_author: "fan" | "bot" | null;
  last_message_at: string | null;
  created_at: string;
  message_count: number;
  revenue_cents: number;
  has_active_order: boolean;
  currency: string;
  stage: string;
  /** 🐦 D'où vient la conversation : `telegram` (le défaut) ou `twitter`.
   *
   *  🩸 **Il MANQUAIT ici, et la pastille X de la grille n'a donc jamais pu
   *  s'allumer** — mesuré le 04-09-2026. Le champ était servi par
   *  `/api/conversations` depuis le 02-09 et la carte le lisait
   *  (`c.canal === "twitter"`), mais rien ne le faisait traverser : ni ce type,
   *  ni `projectToUi`. Une pastille qui ne s'affiche jamais ne lève aucune
   *  erreur et ne se voit que quand quelqu'un la cherche. */
  canal?: string | null;
  /** 🌉 Ce fan a une identité X, quel que soit le canal de sa conversation.
   *
   *  Kayla, 04-09-2026 : « si la personne est sur Telegram après, on peut voir
   *  qu'avant, elle était sur Twitter ». `canal` dit d'où la conversation vient
   *  et ne bouge plus ; celui-ci dit que le fan a un passé sur X — et qu'on
   *  peut encore lui y écrire. Optionnel : absent = backend pas encore
   *  déployé, donc aucune pastille. */
  aussi_sur_x?: boolean;
  /** Bot en pause pour cette conversation (Redis `bot_paused:{chat_id}`).
   *  null si pas de chat_id (pas pause-able). */
  bot_is_paused: boolean | null;
  /** Cyrano explicitement en pause sur la conv (DB column). C'est ce flag
   *  que la card grid affiche, synchro avec le bottom bar du fan panel. */
  is_cyrano_paused?: boolean;
  /** Nombre de messages entrants non lus, PARTAGÉ par toute l'équipe (calculé
   *  côté backend depuis le timestamp de lecture commun `admin_read:{id}`).
   *  0 = lu. Optionnel pour la rétro-compat pendant le rollout backend (un
   *  backend pas encore déployé ne renvoie pas le champ → 0). */
  unread_count?: number;
  /** Flow run actif (V2 backend) — quand present, la card conv affiche
   *  la progression au lieu du statut agent. */
  active_flow_run?: {
    flow_id: string;
    flow_name: string;
    progress_percent: number;
    completed_nodes: number;
    total_nodes: number;
    /** ISO : depuis quand le run est au step courant (« à ce step depuis X »). */
    current_node_entered_at?: string | null;
  } | null;
  /** Tags fan assignés (scope creator), hydratés en bulk côté backend. Triés
   *  par nom, archivés exclus. Optionnel pour la rétro-compat pendant le
   *  rollout (un backend pas encore déployé ne renvoie pas le champ). */
  tags?: FanTagLite[];
  /** Lien d'invitation tracké d'origine du fan (first-touch), si arrivé via un
   *  de nos liens cockpit. null/absent = pas de badge. Optionnel pour la
   *  rétro-compat pendant le rollout backend. */
  invite_link_source?: ApiInviteLinkSource | null;
  /** Le COMPTE Telegram de la créatrice par lequel le fan est entré — servi
   *  seulement quand elle en a plusieurs (Trista : `@tristavoid` depuis le
   *  15-09-2026). null/absent = rien à préciser. Optionnel (rétro-compat). */
  compte_d_arrivee?: ApiCompteDArrivee | null;
  /** Le fan est CHAUD et un message attend une réponse. Backend
   *  `conversations.needs_attention`. Optionnel (rétro-compat rollout). */
  needs_attention?: boolean;
  needs_attention_reason?: string | null;
  /** Signal détecté par Cyrano sur le dernier message du fan, lu par les
   *  panneaux de La Régie. Backend `conversations.live_signal`. Optionnel. */
  live_signal?: string | null;
  /** Niveau d'énergie du fan [0..1] au moment du signal. Optionnel. */
  live_signal_energy?: number | null;
  /** ── L'ATTENTION DU FAN, le chiffre de la carte ──────────────────────────
   *  À quel point il met de lui-même dans la conversation, comparé à SON
   *  propre rythme : son délai de réponse et la taille de son texte, cumulés
   *  sur une dizaine de tours. Backend `conversations.attention_niveau`
   *  (`app/domain/services/fan_signals/attention.py`).
   *
   *  ⚠️ Rien à voir avec `live_signal_energy` juste au-dessus, qui est un
   *  instantané figé au moment d'un flash de signal. Celle-ci est durable.
   *
   *  ABSENT = le backend ne le dit pas encore (le cockpit se déploie AVANT
   *  Cyrano) ; `null` = jamais mesurée. Les deux se rendent pareil : RIEN
   *  d'affiché. Jamais 0.5 — un « 50 % » se lirait comme une mesure. */
  attention?: number | null;
  attention_palier?: string | null;
  attention_tendance?: string | null;
  /** Combien de tours de réponse composent la mesure — l'écran s'en sert pour
   *  dire « estimation en cours » plutôt qu'un chiffre franc. */
  attention_observations?: number | null;
  /** ISO — horodatage de la dernière réponse MESURÉE, pas du calcul. */
  attention_mesure_le?: string | null;
  /** Mode de contenu Copilote (auto/relationnel/sexting) forcé sur la conv.
   *  Backend `conversations.content_mode`. Optionnel (rétro-compat rollout). */
  content_mode?: string;
  /** Décision proposée en attente (mode PROPOSE, cf
   *  PLAN_CYRANO_MODE_PROPOSE.md Annexe A). Backend
   *  `conversations.pending_decision` + event SSE `decision_proposal`.
   *  Optionnel — DORMANT tant que le backend Phase 1 n'est pas déployé
   *  (champ absent → `MockConversation.pending_decision` retombe à false). */
  pending_decision?: boolean;
  /** Libellé compact du variant recommandé (1er de la liste), pour le chip
   *  liste. Optionnel, même rétro-compat que `pending_decision`. */
  pending_decision_label?: string | null;
  /** « On peut encore écrire à ce fan » — dérivé côté backend des mêmes
   *  prédicats que le masquage des conversations injoignables. Sert la pastille
   *  de la vue « toutes les conversations » (`include_unreachable=true`).
   *  `null`/absent = indéterminé → AUCUNE pastille (le cockpit se déploie AVANT
   *  Cyrano : tant que l'image backend n'a pas basculé, le champ n'existe pas
   *  et on n'invente pas un statut). */
  is_reachable?: boolean | null;
  /** Le workflow ouvert sur cette conversation, hydraté en bulk côté backend
   *  depuis le dossier de parcours. `null` = aucun parcours ne tourne ;
   *  ABSENT = le backend ne sait pas encore le dire (même rétro-compat que
   *  `is_reachable` : le cockpit se déploie AVANT Cyrano). Les deux se rendent
   *  pareil — pas de bandeau — mais seul le premier est une mesure. */
  workflow_actif?: ApiWorkflowActif | null;
  /** 🔔 Le déclenchement NON PRIS. Cf. `ApiSignalWorkflow`. */
  signal_workflow?: ApiSignalWorkflow | null;
  /** 🔵 Ce qui a coupé Cyrano et que personne n'a repris. Cf.
   *  `MomentAReprendre`. ABSENT (backend pas encore déployé) et `null` (mesuré :
   *  rien à reprendre) se rendent pareil — pas de clignotement. */
  moment_a_reprendre?: string | null;
  /** 💶 L'OFFRE EN L'AIR — la commande `pending` la plus récente de la paire,
   *  montant, devise, et depuis quand. Ce que `has_active_order` (gardé) ne
   *  disait pas : une offre de 50 € d'il y a 4 min et une de 10 € d'hier se
   *  ressemblaient. ABSENT = backend pas encore déployé → la pastille d'avant. */
  offre_en_attente?: { montant_cents: number; devise: string; depuis: string } | null;
  /** 🎯 LA FILE DU CHATTEUR (Kayla, 08-09-2026). Servis SEULEMENT sur
   *  `sort=priorite` : 0 offre en l'air sans réponse · 1 le fan attend · 2 le
   *  reste ; et la phrase que la carte affiche telle quelle (« offre 50 € ·
   *  4 min sans réponse »). `null`/absent = pas de rang (autre tri, ou backend
   *  pas encore déployé) — la carte ne dit rien. */
  priorite_rang?: number | null;
  priorite_raison?: string | null;
  /** 🎯 `sort=signaux` : le NOM du signal le plus fort (`signaux_de_file.ORDRE`)
   *  et sa date. Absents sur les autres tris. */
  signal?: string | null;
  signal_le?: string | null;
  /** 📌 QUI se l'est réservée (son `workos_user_id`), et si la réservation
   *  TIENT — l'épingleur a une attribution ouverte : présent, pas en pause,
   *  pas dix minutes sans écrire. Kayla, 11-09-2026 : « qu'on voie les petites
   *  étoiles à côté du pseudo », et « quand c'est son shift, les gens ne
   *  peuvent pas y aller, sauf s'il n'est pas présent ». Absents = backend pas
   *  encore déployé → aucune étoile sur les autres, aucune porte fermée. */
  epinglee_par?: string | null;
  epingle_protegee?: boolean;
  /** 📋 QUI LA TIENT — le `workos_user_id` du chatteur dont la pile la porte
   *  en ce moment (attribution ouverte), qu'il l'ait épinglée ou non. Kayla,
   *  12-09-2026 : « quand c'est dans la file de quelqu'un, un autre chatter ne
   *  doit pas pouvoir y aller ». Absent = backend pas encore déployé → aucune
   *  porte fermée par ce champ. */
  tenue_par?: string | null;
}

/** 🔔 Un déclenchement de workflow que personne n'a encore PRIS.
 *
 *  Kayla, 16-08-2026 : « tant que j'ai pas cliqué dessus, il reste ».
 *
 *  ⛔ À ne pas confondre avec `ApiWorkflowActif` : celui-là dit qu'un parcours
 *  TOURNE, et il s'éteint quand le parcours se referme — tout seul, que
 *  quelqu'un ait regardé ou non. Le signal dit qu'une porte a MORDU et que
 *  personne ne l'a acquitté ; il survit à la fermeture du parcours, au
 *  redémarrage du pod et au changement d'appareil.
 *
 *  🪶 Un parcours `temoin` n'ouvre aucune instance : il n'a JAMAIS de
 *  `workflow_actif`, et c'est le signal qui porte tout son affichage.
 *
 *  ⚠️ Nommé `signal_workflow` et pas `signal` — `live_signal` existe déjà sur
 *  le même objet et désigne autre chose (une catégorie de conversation). */
export interface ApiSignalWorkflow {
  nom: string;
  declencheur: string;
  /** ISO. */
  depuis: string;
  /** `> 1` = ça a remordu entre deux regards ; un clic solde tout. */
  combien: number;
}

/** Le parcours ouvert sur une conversation, forme compacte pour la carte.
 *  La base garantit qu'il y en a AU PLUS UN par (créatrice, fan) — index
 *  partiel `uq_phase_dossier_une_seule_active` — donc c'est bien un objet et
 *  pas une liste. */
export interface ApiWorkflowActif {
  nom: string;
  etape?: string | null;
  ouvert_le?: string | null;
}

const SIGNAL_CATEGORIES: readonly SignalCategory[] = [
  "payment",
  "content",
  "objection",
  "arousal",
];

/** Coerce une valeur API en `SignalCategory` connue, sinon `null` (défensif :
 *  une catégorie inconnue n'allume rien plutôt que de casser le rendu). */
function asSignalCategory(raw: string | null | undefined): SignalCategory | null {
  return raw && (SIGNAL_CATEGORIES as readonly string[]).includes(raw)
    ? (raw as SignalCategory)
    : null;
}

export interface ApiConversationListResponse {
  conversations: ApiConversationListItem[];
  next_cursor: string | null;
  stats?: {
    conversations: number;
    messages: number;
    revenue_cents: number;
  } | null;
}

export interface ApiFanProfile {
  user_id: string;
  /** 🩸 `null` sur un fan venu des DM X — il n'a PAS de Telegram.
   *
   *  `users.telegram_id` est nullable depuis le 02-09-2026 ; le DTO backend
   *  l'exigeait encore et rendait 500 sur les 292 conversations Twitter, ce que
   *  le cockpit affichait en rond qui tourne. Le type suit maintenant la
   *  réalité de la colonne : un champ qui ment sur sa nullité fait écrire
   *  `String(x)` sans y penser, et l'écran montre « null ». */
  telegram_id: number | null;
  telegram_chat_id: number | null;
  username: string | null;
  first_name: string | null;
  avatar_url: string | null;
  is_premium: boolean;
  created_at: string;
  /** 🟢 Statut Telegram, déjà vieilli par le backend (29-10-2026) — absent
   *  tant que Cyrano n'a pas déployé le champ, `null` quand jamais relevé. */
  presence?: string | null;
  presence_vu_a?: string | null;
  /** La bio du fan sur son réseau (Telegram ou X). */
  bio?: string | null;
}

export interface ApiTraductionSortante {
  source: string;
  depuis: string | null;
  vers: string;
  provenance: string | null;
}

export interface ApiPaymentButton {
  label: string;
  url: string;
}

/** Un media VENDU (verrouille) attache a une bulle PPV. Source :
 *  `PaidMediaItem` dans `app/api/routes/conversations.py`. */
export interface ApiPaidMediaItem {
  content_item_id: string;
  /** URL proxy Vault `/api/vault/items/{id}/preview` — affichee floutee tant
   *  que `locked`, nette une fois `unlocked` / au clic. */
  media_url: string;
  /** "photo" | "video" | "voice" | "audio" — pour `mediaKindOf`. */
  message_type: string;
}

/** Etat PPV (paid media) d'un message sortant qui vend du contenu. Source :
 *  `MessagePaidMedia`. Present uniquement quand le message porte un order_id
 *  ET qu'il y a au moins un media a montrer (sinon le bouton suffit). */
export interface ApiPaidMedia {
  /** "locked" = pas encore paye ; "unlocked" = paye/livre. */
  state: "locked" | "unlocked";
  price_cents: number;
  currency: string;
  /** Les medias VENDUS (≠ teaser), floutes tant que locked. */
  items: ApiPaidMediaItem[];
  /** ISO du moment de deblocage (paiement/livraison), null tant que locked. */
  unlocked_at: string | null;
}

/** Une vente partie par un moyen d'encaissement DIRECT (hors Dropp). Source :
 *  `MessagePaiementManuel` dans `app/api/routes/conversations.py`.
 *
 *  ⛔ Le cockpit ne s'en sert plus depuis le 12-10-2026 : le bouton « il a
 *  paye » qu'il posait sous la bulle reglait une vente a la main, et l'argent
 *  ne se change plus dans le cockpit (Kayla). Le type reste, le serveur
 *  l'envoie toujours. */
export interface ApiManualPayment {
  /** La commande que la vente directe a ouverte. */
  order_id: string;
  /** "upay" | "paypal" | "iban". */
  moyen: string;
  /** "en_attente" | "regle" — derive de la commande, jamais du message. */
  etat: "en_attente" | "regle";
  price_cents: number;
  currency: string;
}

/** Un pack de parties vendu, et de quoi RENVOYER son lien au fan.
 *
 *  Le lien du jeu (`t.me/<bot>?start=<shortcode>`) part une fois, au paiement.
 *  S'il n'est jamais parti — mint injoignable, connexion Business tombee — ou si
 *  le fan l'a perdu, il n'existait AUCUN moyen de le renvoyer.
 *
 *  🩸 Renvoyer est SUR : le mint est idempotent sur la commande, donc le renvoi
 *  rend le MEME lien vers les MEMES parties. Deux clics n'offrent jamais deux
 *  packs. */
export interface ApiGamePack {
  /** La commande dont on renvoie le lien. */
  order_id: string;
  /** "en_attente" | "regle" — derive de la commande, jamais du message. Le
   *  bouton n'a de sens que sur "regle" : on ne livre pas des parties impayees. */
  etat: "en_attente" | "regle";
  /** Combien de parties — sert au libelle du bouton. */
  spins: number;
  /** Slug du jeu ("trista", "lilou"…), ou null sur une commande legacy. */
  game: string | null;
  /** 🏆 Un match de Puissance 4 : la bulle porte sa carte. Absent sur un
   *  Cyrano d'avant le 03-11-2026. */
  match?: boolean;
  /** La bulle du lien de LIVRAISON (pas la vente) : la carte n'y va pas. */
  livraison?: boolean;
}

/** 👁 L'attention du fan mesurée SUR UN MESSAGE — servie sur les entrants.
 *
 *  Kayla (22-08), en désignant la ligne sous une bulle où il y a déjà le geste
 *  (« défaut ») et « sexting » : « j'aimerais bien qu'on voie le niveau ».
 *  Cette ligne est vide sous une bulle du fan — un geste et un drapeau
 *  `flirty` ne décrivent que ce que NOUS envoyons. C'est la place libre, et
 *  la bonne.
 *
 *  Backend `messages.metadata.attention`
 *  (`app/domain/services/fan_signals/attention.py`). */
export interface ApiMessageAttention {
  /** [0..1] — la jauge cumulée après ce tour-là, pas l'instant brut. */
  niveau: number;
  palier: "bas" | "moyen" | "haut";
  /** Combien de tours composent la mesure à cet instant. Sous une poignée, le
   *  chiffre est encore collé au neutre par construction → rendu en gris. */
  observations: number;
}

export interface ApiMessage {
  id: string;
  direction: "in" | "out";
  message_type: string;
  content: string | null;
  /** Sur une bulle MEDIA sortante : est-ce que `content` est ce que le fan a
   *  VRAIMENT lu ?
   *
   *  Les deux cas se ressemblent en base et ne se ressemblent pas du tout :
   *    - `true`  -> `content` est la legende collee au media sur Telegram.
   *    - `false` -> `content` est la NOTE DE CONTEXTE de Cyrano. Le media est
   *                 parti MUET ; personne ne l'a lue. Kayla l'a demandee le
   *                 18-08 (« une petite legende simple dans son CONTEXTE »),
   *                 et le backend la marque `legende_vue_par_le_fan: false`.
   *    - absent / `null` -> on ne sait pas. Bulle d'avant ce champ, ou API pas
   *                 encore deployee. On rend comme avant : le cockpit sort
   *                 TOUJOURS avant Cyrano, ce champ doit donc pouvoir manquer.
   *
   *  🩸 Sans ce drapeau au rendu, une note se relit comme une phrase envoyee.
   *  Kayla a supprime une bulle le 04-08 en la prenant pour un message parti,
   *  et a re-signale la meme chose le 21-08 sur une note qu'aucun fan n'avait
   *  lue. Le backend portait deja l'information ; personne ne l'affichait. */
  legende_vue_par_le_fan?: boolean | null;
  /** 🎙️ Sur une bulle sortante `voice`/`audio` : ce que le vocal DIT, sa
   *  transcription (Whisper, la même que celle vectorisée dans le Vault).
   *  Absent / `null` = on ne sait pas — bulle d'avant ce champ, backend pas
   *  encore déployé, ou vocal sans transcription. Le cockpit sort TOUJOURS
   *  avant Cyrano ; ce champ doit donc pouvoir manquer et le rendu retombe
   *  alors sur celui d'avant (cf `ChatItem.vocalTexte`, `VocalDit`). */
  vocal_texte?: string | null;
  media_file_id: string | null;
  /** Proxy URL `/api/conversations/{cid}/messages/{mid}/media` quand le
   *  message porte un media. Null pour text-only. Utilisable directement
   *  dans `<img src=>`, `<video src=>`, `<audio src=>`. */
  media_url: string | null;
  is_ai_generated: boolean;
  /** Origine explicite cote backend (cf MessageSenderSource).
   *  - "fan"             : INCOMING (le client a ecrit)
   *  - "cyrano"          : OUTGOING genere par l'IA
   *  - "operator"        : OUTGOING envoye depuis le cockpit
   *  - "telegram_manual" : OUTGOING capte via MTProto (le createur a
   *                        ecrit DIRECTEMENT depuis l'app Telegram)
   *  - "twitter"         : passe par un DM X — le CANAL, pas l'auteur. Vrai des
   *                        entrants du fan comme des sortants. Qui a ecrit se
   *                        lit alors sur `is_ai_generated` (Cyrano-X) ou sur
   *                        l'identite d'operateur ; sans l'un ni l'autre, on ne
   *                        sait pas, et l'ecran le dit (cf `buildChatItem`).
   *  Le frontend affiche un badge "Manuel" / "Telegram" / "Twitter".
   *  Nullable pour la compat avec les rows OUTGOING anterieurs a la
   *  migration sender_source (theoriquement aucun, mais on reste defensif). */
  sender_source:
    | "fan"
    | "cyrano"
    | "operator"
    | "telegram_manual"
    | "flow"
    | "twitter"
    | null;
  /** Pour les messages flow (sender_source="flow"), id du flow source.
   *  Le cockpit resout le nom via useFlowsList. None pour autres sources. */
  flow_id: string | null;
  /** 🏷️ Le geste qui a ecrit ce message (`messages.metadata.geste`) —
   *  `relationnel`, `default`, `ultimatum`... `null` sur tout message humain,
   *  et sur tout sortant d'avant le 27-08-2026. Le libelle se fabrique a
   *  l'ecran (`LIBELLE_DU_GESTE`), jamais au backend. */
  geste?: string | null;
  /** 👁 Le niveau d'attention du fan sur CE message. `null` sur tout sortant,
   *  et sur tout entrant qu'aucune mesure ne couvre. */
  attention?: ApiMessageAttention | null;
  /** 🤖 Sortant seulement : la variante de Francis d'où vient cette bulle
   *  (`messages.metadata.francis_variante`, posée par le composeur à l'envoi).
   *  Le panneau Francis la lit pour griser la carte déjà partie — zéro redite
   *  au fan. Absent / `null` = tapé à la main, ou backend d'avant : au
   *  20-10-2026, `_message_to_item` ne projette PAS encore cette clé, le
   *  cockpit sort avant Cyrano et ce champ doit donc pouvoir manquer. */
  francis_variante?: { echange_id: string; i: number } | null;
  created_at: string;
  /** UUID local du message auquel celui-ci repond, ou null. Le cockpit
   *  resout le parent dans `detail.messages` pour afficher une citation
   *  en tete de bulle (style reply Telegram). */
  reply_to_message_id: string | null;
  /**
   * 🔑 L'accuse de transport Telegram — `messages.telegram_message_id`.
   *
   * C'est la SEULE cle par laquelle le journal du cycle connait une bulle
   * (`MessageSent.payload.message_id`), pendant que les Coulisses la
   * connaissent par son UUID (`CoulisseMessageDto.message_id`). Porter les
   * deux cles sur la MEME bulle est ce qui permet de joindre un cout — sans
   * ca, les deux registres ne se rencontrent jamais et tout s'affiche
   * « cout inconnu ». Voir `jointureDesCouts` dans `FanPanel`.
   *
   * `null` est exact et frequent : message pas encore parti, entrant capte
   * hors bot, ou bulle supprimee cote Telegram.
   */
  telegram_message_id: number | null;
  /** Outgoing only : false tant que Telegram n'a pas accuse reception
   *  (telegram_message_id null cote backend). Incoming = toujours true.
   *  Le cockpit grise la bulle tant que delivered === false. */
  delivered: boolean;
  /** Outgoing only : bouton inline rendu sous la bulle quand le message
   *  est un PPV (paid content). Click → ouvre l'URL de paiement Dropp.fans. */
  payment_button: ApiPaymentButton | null;
  /** 🌍 Sortant traduit avant de partir (16-09-2026) : `content` est ce que
   *  le fan a LU, `traduction.source` ce que le chatteur — ou le script du
   *  flow — avait ÉCRIT. Absent/`null` = partie telle qu'écrite, ou backend
   *  d'avant. */
  traduction?: ApiTraductionSortante | null;
  /** 🌍 Incoming only (26-09-2026) : ce message du fan DÉJÀ traduit, par
   *  langue de chatteur (`{ fr: "…" }`) — posé par Cyrano à la persistance,
   *  ou mémorisé par un appel d'avant. Le fil l'affiche sans rien demander ;
   *  il ne redemande que ce qui manque. Absent/`null` = rien de mémorisé, ou
   *  backend d'avant. */
  traductions?: Record<string, string> | null;
  /** 🌍 Incoming only : la langue dans laquelle CE message est écrit (code
   *  ISO, estampille Jev) — le drapeau sous la bulle traduite. Absent/`null`
   *  = pas estampillé, ou trop court pour trancher. */
  langue?: string | null;
  /** Outgoing only : etat PPV (paid media) si ce message vend du contenu.
   *  Porte l'etat verrouille/debloque + prix + medias vendus (a flouter tant
   *  que locked). Le cockpit rend 3 etats distincts (verrouille / gratuit /
   *  debloque). Null pour les messages normaux/gratuits. */
  paid_media: ApiPaidMedia | null;
  /** Outgoing only : encaissement DIRECT (hors Dropp). Non-null seulement sur
   *  les ventes parties par UPay / PayPal / virement. Null sur Dropp (le
   *  webhook tranche) et partout ailleurs. Plus lu par le fil (12-10-2026). */
  manual_payment: ApiManualPayment | null;
  /** Outgoing only : un pack de parties vendu. Porte la commande et son etat,
   *  pour que le fil puisse RENVOYER le lien du jeu. Null partout ailleurs. */
  game_pack: ApiGamePack | null;
  /** Outgoing manuel (is_ai_generated=false) uniquement : identite de
   *  l'operateur qui a frappe le message depuis le cockpit. Le frontend
   *  resout name + avatar via Convex `users.getManyByWorkosIds` pour
   *  distinguer dans le thread Cyrano (BOT) vs Bratz / autre operateur.
   *  Null pour Cyrano ou pour les envois cockpit historiques pre-feature. */
  operator_workos_user_id: string | null;
  operator_email: string | null;
  /** ISO timestamp de la dernière édition (extra_metadata.edited_at).
   *  Null si jamais édité. Le cockpit affiche un petit "(édité)" sous la
   *  bulle quand non-null. */
  edited_at: string | null;
  /** ISO timestamp de la suppression (soft-delete). Null si non-supprimé.
   *  Le cockpit rend une placeholder grise "Message supprimé" si non-null. */
  deleted_at: string | null;
  /** Emoji unique posé via setMessageReaction (Bot API, "as bot"). Null
   *  si aucune réaction. Phase 6 ajoutera un champ pour les réactions
   *  MTProto "as creator" et les premium emojis. */
  bot_reaction: string | null;
  /** Poll/quiz metadata si le message est un poll envoyé via /send-poll.
   *  Null pour les messages texte/media réguliers. */
  poll: ApiPollMetadata | null;
  /** Entities Telegram (formatting + custom_emoji Premium + liens).
   *  Vide [] si pas d'entities. Le cockpit rend via <TelegramText>. */
  entities: ApiConvMessageEntity[];
  /** Inline keyboard (rows × cols) attache au message via reply_markup.
   *  null si pas d'inline keyboard. V1 DM Business : URL et web_app
   *  cliquables direct, callback affichés grisés (Bot API ne permet pas
   *  de simuler un click sur un bouton d'un message tiers), switch_inline
   *  copy clipboard. */
  inline_keyboard: ApiConvInlineButton[][] | null;
}

export interface ApiConversationEvent {
  type:
    | "new_message"
    | "message_edited"
    | "message_deleted"
    | "message_reacted"
    // Notif flow ephemere : miroir cockpit de l'action `notify_flow_group`
    // (backend `flow_executor._publish_flow_notice`). Porte son texte dans
    // `content`. Affichee transitoirement dans `ConvStatusBar`, jamais
    // persistee comme message. Cf `flowNoticeAtom` / `useFlowNotice`.
    | "flow_notice"
    // Le fan tape dans le DM.
    // Porte `until` (ISO) : l'indicateur « écrit… » s'auto-efface après.
    | "typing"
    // Le fan a PAYE un PPV (backend `dropp_realtime_worker._process_dropp_payment`).
    // Porte `message_id` (bulle vendeuse) + `unlocked_at`/`price_cents`/`currency`.
    // Le cockpit bascule `paid_media.state` -> "unlocked" en direct.
    | "ppv_unlocked"
    // Changement d'état d'une conversation poussé par le backend (canal
    // `conv:events:{creator_id}`). Aujourd'hui : bascule `needs_attention`
    // (fan chaud à reprendre / repris). Porte le nouvel état + la cause.
    | "conversation_state_changed"
    // Cyrano a détecté un signal sur le dernier message du fan (backend
    // `finalize` → `apply_signal_flash`). Porte `category`
    // ("payment"|"content"|"objection"|"arousal") ou `category: null` pour
    // éteindre. Lu par les panneaux de La Régie (pastille de signal).
    | "signal_flash"
    // ── Timeline « délai humain » de Cyrano (backend `delay_timeline.py`) ──
    // Compte à rebours live avant l'envoi du prochain message IA.
    //   scheduled : programmation (porte deliver_at/read_at/typing_at/scheduled_at)
    //   read      : le « vu » vient de tomber (porte deliver_at + now serveur)
    //   typing    : Cyrano commence à écrire (porte deliver_at + now serveur)
    //   generating: Cyrano génère la réponse (avant même le délai — fin boîte noire)
    //   cancelled : reset (un message fan est arrivé) → efface le compteur
    // `agent_thinking` (backend preparation_phase) : signal FIABLE « Cyrano
    // réfléchit » émis dès qu'un job proceed ; on le rend comme la phase generating.
    | "agent_thinking"
    // Une ETAPE du tour vient de se fermer, PENDANT que la pipeline tourne
    // (backend `span_collector.on_span_closed`, flag PIPELINE_LIVE_STEPS_ENABLED,
    // Francis#954). Ferme la boite noire de ~10-15 s entre `cyrano_generating`
    // et `cyrano_scheduled`. Ephemere : consomme par la vue Observation, jamais
    // persiste — le tour complet reste celui que `finalize` ecrit.
    | "pipeline_step"
    | "cyrano_generating"
    | "cyrano_scheduled"
    | "cyrano_read"
    | "cyrano_typing"
    | "cyrano_edited"
    | "cyrano_cancelled"
    // Décision proposée par le décisionnel (mode PROPOSE, cf
    // PLAN_CYRANO_MODE_PROPOSE.md Annexe A — backend Phase 1 PAS ENCORE
    // déployé : côté front ce type reste DORMANT tant qu'aucun event
    // n'arrive). Un seul type pour tout le cycle de vie : `status`
    // "proposed" pose/remplace la proposition active de la conversation,
    // "resolved" l'éteint (confirm/reject/supersede/expiry, précisé par
    // `resolution`).
    | "decision_proposal"
    // Dispatch v2 (CONTRAT RÉGIE DISPATCH point 2) : un job a été créé,
    // (ré)assigné, résolu, expiré ou annulé (canal `conv:events:{creator_id}`
    // existant, réutilisé — AUCUNE nouvelle connexion SSE). Le cockpit ne lit
    // PAS le payload en détail : tout event de ce type déclenche juste un
    // refetch débouncé de la file "mine" (cf `useConversationEvents`
    // ci-dessous), le serveur restant l'autorité sur ce qui appartient au
    // caller.
    | "regie_job"
    // Portefeuille de La Régie (mission A2 « AMENDEMENTS 07-10 soir »,
    // point 4) : une vente a été résolue/payée et attribuée à un chatteur
    // (`member_id`). Un seul type pour l'event — le cockpit ne filtre PAS
    // par identité au transport (même choix que `regie_job`) : TOUT event de
    // ce type est relayé via `regieDecisionOutcomeAtom`, c'est
    // le consommateur (côté portefeuille)
    // qui décide s'il s'agit de l'utilisateur courant et si la vente est
    // assez fraîche pour une célébration.
    | "decision_outcome";
  conversation_id: string;
  message?: Partial<ApiMessage> & {
    direction?: string | null;
    telegram_message_id?: number | null;
    metadata?: Record<string, unknown>;
  };
  message_id?: string;
  content?: string | null;
  edited_at?: string | null;
  deleted_at?: string | null;
  emoji?: string | null;
  /** Pour `typing` : ISO d'expiration de l'indicateur « écrit… ». */
  until?: string | null;
  /** Pour `ppv_unlocked` : moment de deblocage (ISO) + prix paye. */
  unlocked_at?: string | null;
  price_cents?: number | null;
  currency?: string | null;
  /** Pour `conversation_state_changed` : nouvel état "à reprendre" + cause. */
  needs_attention?: boolean | null;
  needs_attention_reason?: string | null;
  /** Pour `signal_flash` : catégorie de signal détecté ("payment"|"content"|
   *  "objection"|"arousal"), ou null pour éteindre le clignotement. */
  category?: string | null;
  /** Pour `signal_flash` : niveau d'énergie [0..1] → vitesse de clignotement. */
  energy?: number | null;
  /** Pour `conversation_state_changed` : nouveau mode Cyrano on-demand
   *  ("off"|"relationnel"|"sexting"), présent sur start/stop/auto-stop. Absent
   *  quand l'event ne concerne que `needs_attention`. */
  content_mode?: string | null;
  /** Pour `conversation_state_changed` émis par le moteur de flows quand un run
   *  se termine ("completed"). La carte doit clignoter `flow_termine` tout de
   *  suite : on refait la liste plutôt que de deviner le moment côté client. */
  flow_run?: string | null;
  /** Timeline Cyrano (cyrano_scheduled/read/typing). ISO (UTC). `deliver_at` =
   *  heure de livraison prévue ; `read_at`/`typing_at` = jalons ; `scheduled_at`
   *  = horloge serveur au moment de la programmation ; `now` = horloge serveur à
   *  l'émission (read/typing) — sert à recaler le compte à rebours sans clock
   *  skew. `reason` sur cyrano_cancelled. */
  deliver_at?: string | null;
  read_at?: string | null;
  typing_at?: string | null;
  scheduled_at?: string | null;
  now?: string | null;
  delay_s?: number | null;
  reason?: string | null;
  /** Timeline Cyrano (cyrano_scheduled/cyrano_edited) : le texte du brouillon
   *  généré (et éventuellement édité) que Cyrano s'apprête à envoyer. */
  draft_text?: string | null;
  /** cyrano_edited : identifiant WorkOS du chatter qui a édité. */
  by?: string | null;
  /** `pipeline_step` : nom de l'etape qui vient de se fermer, son type, sa
   *  duree reelle, le modele si c'est un appel LLM, et si elle a leve. */
  name?: string | null;
  span_type?: string | null;
  duration_ms?: number | null;
  model?: string | null;
  errored?: boolean | null;
  ended_at?: string | null;
  pipeline_run_id?: string | null;
  /** decision_proposal : identifiant de la proposition (idempotence — un 409
   *  sur confirm/reject veut dire qu'elle n'est déjà plus "proposed"). */
  proposal_id?: string | null;
  /** decision_proposal : "proposed" (nouvelle proposition, remplace
   *  l'active) ou "resolved" (voir `resolution` pour la cause). Élargi pour
   *  `regie_job` (dispatch v2) : `queued`|`ready`|`resolved`|`expired`|
   *  `cancelled` — le cockpit ne lit PAS cette valeur pour `regie_job`
   *  aujourd'hui (tout event de ce type déclenche juste un refetch, cf
   *  CONTRAT RÉGIE DISPATCH point 2) ; typé ici pour rester fidèle au
   *  payload documenté. */
  status?: "proposed" | "resolved" | "queued" | "ready" | "expired" | "cancelled" | null;
  /** decision_proposal, status "resolved" : cause ("accepted"|"rejected"|
   *  "superseded_by_new_context"|"superseded_by_human"|"expired" — la liste
   *  exacte appartient au backend, le front ne fait qu'éteindre la carte). */
  resolution?: string | null;
  /** decision_proposal : libellé lisible du stage du funnel (ex: "Fin de
   *  script1"). Affiché en tête de la `DecisionProposalCard`. */
  stage_label?: string | null;
  /** decision_proposal ET regie_job : ISO d'expiration (proposition ou job). */
  expires_at?: string | null;
  /** decision_proposal, status "proposed" : 1-3 actions recommandées. */
  variants?: ApiDecisionVariant[] | null;
  /** regie_job : identifiant du job (dispatch v2, CONTRAT RÉGIE DISPATCH
   *  point 2). Non consommé aujourd'hui — le cockpit refetch `["regie","jobs",
   *  "mine"]` sur tout event `regie_job`, le serveur restant l'autorité sur
   *  ce qui appartient au caller. */
  job_id?: string | null;
  /** regie_job : `workos_user_id` du chatteur à qui le job est assigné, ou
   *  `null` si non assigné. Non consommé aujourd'hui (même raison). */
  assigned_to?: string | null;
  /** decision_outcome (mission A2) : identifiant de la commande payée. */
  order_id?: string | null;
  /** decision_outcome : `workos_user_id` du chatteur crédité de la vente,
   *  ou `null` si le backend ne l'a pas résolu. */
  member_id?: string | null;
  /** decision_outcome : montant payé, en cents — nom DISTINCT de
   *  `price_cents` (offre proposée) : ceci est le montant RÉELLEMENT payé,
   *  porté par le contrat backend tel quel (`amount_cents`). */
  amount_cents?: number | null;
  /** decision_outcome : ISO de résolution de la décision (confirm) et de
   *  paiement effectif — l'écart pilote la fenêtre de célébration
   *  (≤ 15 min). */
  resolved_at?: string | null;
  paid_at?: string | null;
}

/** Un variant proposé par le décisionnel (mode PROPOSE) — cf
 *  `ApiConversationEvent.variants`. */
interface ApiDecisionVariant {
  variant_id: string;
  /** Classe d'action catalogue (ex: "send_menu", "launch_flow",
   *  "payment_cta"). Informatif côté front (tooltip) — la sélection reste
   *  100% backend (décisionnel). */
  action: string;
  /** Libellé humain de l'action (ex: "Une vidéo", "Lancer script1"). Le
   *  front lui ajoute `price_cents` formaté quand présent (cf
   *  `DecisionProposalCard`). */
  label: string;
  price_cents?: number | null;
  media_thumb_url?: string | null;
}

/** Un bouton d'inline_keyboard d'un message DM. Source : `ApiConvInlineButton`
 *  dans `app/api/routes/conversations.py`. `kind` discrimine le payload. */
export interface ApiConvInlineButton {
  text: string;
  kind:
    | "url"
    | "callback"
    | "switch_inline"
    | "switch_inline_current"
    | "web_app";
  url?: string | null;
  callback_data?: string | null;
  switch_inline_query?: string | null;
}

/** Entity Telegram persistee depuis le webhook ou envoyee depuis le
 *  composer. `document_id` est en string (precision i64). */
export interface ApiConvMessageEntity {
  type: string;
  offset: number;
  length: number;
  document_id?: string | null;
  url?: string | null;
  user_id?: string | null;
  language?: string | null;
}

export interface ApiPollMetadata {
  type: "regular" | "quiz";
  question: string;
  options: string[];
  is_anonymous: boolean;
  allows_multiple_answers: boolean;
  correct_option_id: number | null;
  explanation: string | null;
}

export interface ApiConversationMediaResponse {
  media: ApiMessage[];
  next_cursor: string | null;
}

export interface ApiMemoryFact {
  id: string;
  subject: string;
  predicate: string;
  object: string;
  category: string;
  importance: number;
  confidence: number;
  created_at: string;
  last_mentioned_at: string;
  mention_count: number;
}

export interface ApiMemoryCommitment {
  id: string;
  commitment_type: string;
  who: string;
  description: string;
  importance: number;
  due_at: string | null;
  status: string;
  created_at: string;
}

/** Fiche structuree d'un fan saisie dans le bloc "Fan" du panneau Info > Identite.
 *  Tous les champs sont optionnels (string | null) — `null` ou absent =
 *  champ vide. Stockee en JSONB sur fan_notes.fan_info, editable via
 *  PATCH /conversations/{id}/fan-info. */
export interface ApiFanInfo {
  first_name?: string | null;
  nickname?: string | null;
  age?: string | null;
  job?: string | null;
  city?: string | null;
  country?: string | null;
  language?: string | null;
  civil_status?: string | null;
  birthday?: string | null;
  hobbies?: string | null;
  calls_creator?: string | null;
}

export interface ApiFanNote {
  /** Texte libre saisi par l'operateur (vide = jamais ecrit ou efface). */
  body: string;
  /** Fiche structuree (prenom, ville, langue, ...). Toujours presente
   *  (objet vide tant que rien n'a ete saisi). */
  fan_info: ApiFanInfo;
  /** WorkOS user id du dernier editeur (null si jamais ecrit). */
  updated_by_workos_user_id: string | null;
  /** ISO timestamp du dernier upsert (null si jamais ecrit). */
  updated_at: string | null;
}

/** Definition d'un tag fan (vocabulaire creator-scope). */
export interface ApiFanTag {
  id: string;
  creator_id: string;
  name: string;
  /** Hex "#rrggbb" ou slug palette historique (primary/success/...). */
  color: string;
  /** True = gere par le systeme (ex tag PAY), non renommable/archivable.
   *  🪤 Ne dit PAS si on peut le POSER — PIN et Time Waster sont systeme et
   *  posables. Pour ca, `assignable`. */
  is_system: boolean;
  /** Ce tag peut-il être posé/retiré à la main ? Dérivé côté backend
   *  (`est_posable_a_la_main`) — voir `estPosableALaMain` pour le repli.
   *  Optionnel : absent tant que Cyrano n'a pas déployé le champ. */
  assignable?: boolean;
  /** ISO timestamp d'archivage (null = tag actif). */
  archived_at: string | null;
  created_at: string;
  updated_at: string;
}

/** Tag assigne a un fan + metadata d'assignation. */
export interface ApiFanTagAssignment {
  tag: ApiFanTag;
  assigned_at: string;
  assigned_by_workos_user_id: string | null;
}

/** Où en est le parcours du lot d'un fan — 15 tours, image au 5e, CTA à la fin.
 *
 *  Kayla, 22-08-2026 : « s'il y a le lot, on n'est pas sur Cyrano mode défaut,
 *  on est sur Cyrano lot. » Ce que ce type rend visible, c'est cet état-là. */
export interface ApiParcoursDuLot {
  /** Les messages du fan depuis l'ouverture. */
  tours: number;
  /** La longueur du parcours. */
  sur: number;
  /** Le tour où part la petite image. */
  tour_du_media: number;
  /** Elle est déjà partie. */
  media_offert: boolean;
  /** Les tours sont faits : il ne reste que le CTA du jeu. */
  termine: boolean;
}

export interface ApiConversationDetail {
  id: string;
  /** D'OÙ vient la conversation : `telegram` (défaut) ou `twitter`. Optionnel :
   *  absent tant que l'image backend n'a pas basculé (le cockpit part AVANT). */
  canal?: string;
  /** 📡 Par où un HUMAIN peut écrire à ce fan, canal de la conversation en tête.
   *
   *  ⛔ `undefined` ≠ liste vide. `undefined` = le backend ne sait pas encore
   *  répondre (déploiement en cours) → le composeur se comporte EXACTEMENT
   *  comme avant ce chantier et n'envoie aucun canal. Liste vide = mesuré, et
   *  personne n'est joignable. Confondre les deux ferait afficher « aucun
   *  canal » sur toutes les conversations pendant le rollout. */
  canaux_d_envoi?: string[];
  /** 🌉 Le PONT vers Telegram depuis le fil X de ce fan.
   *
   *  `null` / absent = pas de fil X, donc rien à ponter : le bouton ne
   *  s'affiche pas du tout. `possible: false` = le lien est DÉJÀ parti, et il
   *  ne se repasse pas — le jeton est idempotent mais la phrase ne l'est pas,
   *  et « j'arrête de répondre ici » envoyé deux fois se lit comme un bot qui
   *  bégaie. */
  relais_twitter?: { possible: boolean; passe_at: string | null } | null;
  creator_id: string;
  user_id: string;
  fan: ApiFanProfile;
  last_message_at: string | null;
  message_count: number;
  created_at: string;
  revenue_cents: number;
  has_active_order: boolean;
  /** Quand true, l'AgentWorker skip les AgentJobs pour cette conv —
   *  Cyrano n'ecrit pas tant que la pause n'est pas levee. Toggle via
   *  PATCH /api/conversations/{id}/cyrano-paused. */
  is_cyrano_paused: boolean;
  /** Le parcours OUVERT sur cette conversation, ou `null`. Optionnel : absent
   *  tant que l'image backend n'a pas basculé (le cockpit se déploie AVANT
   *  Cyrano) → la barre n'affiche alors rien plutôt qu'un « aucun parcours »
   *  qu'elle n'a pas mesuré. */
  workflow_actif?: ApiWorkflowActif | null;
  /** 🎁 Le parcours du LOT qui court, ou `null`.
   *
   *  ⛔ Un AUTRE système que `workflow_actif` juste au-dessus : celui-là dérive
   *  d'un `phase_dossier`, celui-ci du jeu, et il se compte en TOURS. Les
   *  confondre ferait croire qu'arrêter l'un arrête l'autre.
   *
   *  Optionnel : absent tant que l'image backend n'a pas basculé → la barre
   *  n'affiche alors rien plutôt qu'un « aucun parcours » qu'elle n'a pas
   *  mesuré. */
  parcours_du_lot?: ApiParcoursDuLot | null;
  /** 🔔 Le déclenchement NON PRIS. Cf. `ApiSignalWorkflow`. */
  signal_workflow?: ApiSignalWorkflow | null;
  /** Mode de contenu Copilote (auto/relationnel/sexting). Toggle via PATCH
   *  /api/conversations/{id}/content-mode. Optionnel (rétro-compat rollout). */
  content_mode?: string;
  /** 🗂️ L'état de triage — écarté jusqu'à, et les deux compteurs. Aplati dans
   *  le détail exactement comme il l'est côté Cyrano. Tous optionnels : absents
   *  tant que l'image backend n'a pas basculé (le cockpit part AVANT). Cf.
   *  `TriageDeLaConversation`, `estEcartee`, `peutRepousser`. */
  ecarte_jusqua?: string | null;
  ecarts_total?: number;
  repousses_total?: number;
  repousse_le?: string | null;
  messages: ApiMessage[];
  profile: Record<string, unknown> | null;
  narrative: string | null;
  memory_facts: ApiMemoryFact[];
  memory_commitments: ApiMemoryCommitment[];
  /** Note operateur libre attachee a la paire (creator, user). Toujours
   *  presente (body="" tant que rien n'a ete saisi). */
  notes: ApiFanNote;
  /** Lien d'invitation tracké d'origine du fan (first-touch). null = pas de
   *  badge. (Le header lit la valeur projetée depuis la liste ; ce champ est
   *  exposé ici pour complétude/usage futur.) */
  invite_link_source?: ApiInviteLinkSource | null;
  /** Le compte Telegram par lequel le fan est entré (créatrice à plusieurs
   *  comptes). null/absent = rien à préciser. */
  compte_d_arrivee?: ApiCompteDArrivee | null;
}

export interface ApiCompteDArrivee {
  business_connection_id: string;
  /** `@` du compte tel que Telegram le rend ; null s'il n'a pas répondu. */
  username: string | null;
}


/* ====================================================================== */
/* Projection API → MockConversation                                       */
/* ====================================================================== */

const STAGE_VALUES: Stage[] = [
  "new", "fan_low", "fan_mid", "fan_high",
  "customer_low", "customer_mid", "customer_high", "gfe", "unknown",
];

function asStage(s: string | null | undefined): Stage {
  if (!s) return "unknown";
  return (STAGE_VALUES as string[]).includes(s) ? (s as Stage) : "unknown";
}

function asAuthor(a: string | null | undefined): Author {
  return a === "bot" ? "bot" : "fan";
}

function asCurrency(c: string | undefined): "USD" | "EUR" {
  return c === "EUR" ? "EUR" : "USD";
}

/** Normalise un `content_mode` venu de l'API (une chaîne libre) vers le type
 *  fermé. Exportée : `views.tsx` la passe à `ConvStatusBar`, qui n'accepte que
 *  `ContentMode` — sans elle, la conversion se ferait à coups de `as`, et une
 *  valeur inconnue traverserait l'écran sans que rien ne la ramène à « off ». */
export function asContentMode(m: string | null | undefined): ContentMode {
  return m === "relationnel" || m === "sexting" ? m : "off";
}

interface CreatorLookup {
  /** Couleur Convex (creators.color) — la seule donnee Convex-only. */
  color: string;
}

/**
 * La ligne servie par `/api/conversations` devient la carte de la grille.
 *
 * 🩸 **Exportée le 04-09-2026, et pour une raison précise.** Deux champs servis
 * par le backend (`canal`, puis `aussi_sur_x`) ne traversaient pas : la carte
 * les lisait, personne ne les lui donnait, et la pastille X n'a jamais pu
 * s'allumer pendant deux jours. Rien ne l'a dit — un champ optionnel oublié
 * dans une projection ne casse ni le typage ni un test de rendu, puisque
 * celui-ci passe la carte à la main.
 *
 * ⇒ C'est ICI que se garde « ce que le backend sert arrive à l'écran ».
 */
export function projectToUi(
  api: ApiConversationListItem,
  creator: CreatorLookup | undefined,
): MockConversation {
  const lastTsRaw = api.last_message_at ?? api.created_at;
  // Couleur : si Convex a une couleur explicite pour ce creator, on la
  // prend ; sinon fallback déterministe sur le slug (même algo que le
  // Creators panel — assure la cohérence visuelle inter-panneaux).
  const resolvedColor =
    creator?.color ?? colorFromSlug(api.creator_slug ?? api.creator_id);
  return {
    id: api.id,
    fan_name: api.fan_name,
    fan_avatar_url: api.fan_avatar_url,
    fan_presence: api.fan_presence ?? null,
    fan_presence_vu_a: api.fan_presence_vu_a ?? null,
    creator_id: api.creator_id,
    creator_slug: api.creator_slug,
    creator_name: api.creator_display_name,
    creator_avatar_url: api.creator_avatar_url,
    creator_color: resolvedColor,
    language: api.language ? api.language.toUpperCase() : null,
    last_message: api.last_message ?? "",
    last_message_traductions: api.last_message_traductions ?? null,
    last_author: asAuthor(api.last_author),
    last_message_at: Date.parse(lastTsRaw),
    created_at: Date.parse(api.created_at),
    // Compteur PARTAGÉ équipe renvoyé par le backend (admin_read commun). Le
    // localStorage par-opérateur n'est plus la source du "lu" sur la grille.
    unread_count: api.unread_count ?? 0,
    stage: asStage(api.stage),
    has_active_order: api.has_active_order,
    message_count: api.message_count,
    revenue_cents: api.revenue_cents,
    currency: asCurrency(api.currency),
    bot_is_paused: api.bot_is_paused ?? null,
    is_cyrano_paused: api.is_cyrano_paused ?? false,
    active_flow_run: api.active_flow_run ?? null,
    tags: api.tags ?? [],
    invite_link_source: api.invite_link_source ?? null,
    compte_d_arrivee: api.compte_d_arrivee ?? null,
    needs_attention: api.needs_attention ?? false,
    needs_attention_reason: api.needs_attention_reason ?? null,
    live_signal: asSignalCategory(api.live_signal),
    live_signal_energy: api.live_signal_energy ?? null,
    // `?? null` partout : « le backend ne dit rien » et « jamais mesurée » se
    // rendent pareil — aucune jauge. On n'invente pas un niveau neutre.
    attention: api.attention ?? null,
    attention_palier: api.attention_palier ?? null,
    attention_tendance: api.attention_tendance ?? null,
    attention_observations: api.attention_observations ?? null,
    attention_mesure_le: api.attention_mesure_le ?? null,
    content_mode: asContentMode(api.content_mode),
    pending_decision: api.pending_decision ?? false,
    pending_decision_label: api.pending_decision_label ?? null,
    // `?? null` et NON `?? true` : un backend qui ne renvoie pas encore le
    // champ doit donner « je ne sais pas », pas « joignable ».
    is_reachable: api.is_reachable ?? null,
    // 🐦 Les deux traversent ici, et c'est la ligne qui manquait : la carte les
    // lisait déjà, personne ne les lui donnait. `?? null` / `?? false` — absent
    // veut dire « on ne sait pas », donc aucune pastille, jamais un défaut.
    canal: api.canal ?? null,
    aussi_sur_x: api.aussi_sur_x ?? false,
    workflow_actif: api.workflow_actif ?? null,
    signal_workflow: api.signal_workflow ?? null,
    moment_a_reprendre: asMomentAReprendre(api.moment_a_reprendre),
    // 💶🎯 Absent ou null se rendent pareil : ni badge, ni raison, ni rang.
    offre_en_attente: api.offre_en_attente ?? null,
    priorite_rang: typeof api.priorite_rang === "number" ? api.priorite_rang : null,
    priorite_raison: api.priorite_raison ?? null,
    signal: api.signal ?? null,
    signal_le: api.signal_le ?? null,
    // 📌 `?? null` / `?? false` : absent veut dire « on ne sait pas », donc
    //    personne ne se l'est réservée et la porte est ouverte — jamais l'inverse.
    epinglee_par: api.epinglee_par ?? null,
    epingle_protegee: api.epingle_protegee ?? false,
    tenue_par: api.tenue_par ?? null,
  };
}

/** Les moments qui font clignoter une carte. Le MÊME vocabulaire fermé que les
 *  bandes du fil (backend `coulisses_du_fil`). */
const MOMENTS_A_REPRENDRE: readonly MomentAReprendre[] = [
  "ultimatum",
  "relance_du_jeu",
  // 🆕 22-08 : un lot est arrivé, Cyrano s'est tu, et personne ne l'a encore
  //    lancé. ⛔ Sans cette ligne le backend l'envoie et `asMomentAReprendre`
  //    le jette : la carte ne clignote pas, et rien ne le dit.
  "lot_a_lancer",
  // 🆕 01-09 : les trois moments où Cyrano RÉPOND puis rend la main (Kayla :
  //    « on laisse répondre, ensuite on met Cyrano en stop, en pause. Il faut
  //    qu'il clignote »). ⛔ Même piège que la ligne du dessus : sans ces
  //    trois-là, le backend envoie le moment et ce portier le jette en
  //    silence — la carte reste éteinte, et rien ne dit pourquoi.
  "annonce_payante",
  "question_de_prix",
  "accusation_bot",
  // 🆕 02-09 : le quatrième moment où Cyrano rend la main, quand le fan
  //    RÉCLAME un média. ⛔ Même piège que les lignes du dessus, et il a déjà
  //    mordu deux fois : sans cette ligne le backend envoie le moment et
  //    `asMomentAReprendre` le jette en silence — la carte reste éteinte, et
  //    rien ne dit pourquoi.
  "demande_de_contenu",
  // 🧵 02-09 : un flow s'est tu (fini sur la réponse du fan, ou arrêté sur son
  //    silence) et personne n'a repris. Même piège que les lignes du dessus.
  "flow_termine",
];

/** Coerce une valeur API en `MomentAReprendre` connu, sinon `null`.
 *
 *  ⛔ **Exportée pour être TESTÉE, et c'est une leçon du 22-08.** Le test de
 *  rendu de la carte passe le moment en dur au composant : il saute donc ce
 *  portier, et retirer une valeur de `MOMENTS_A_REPRENDRE` le laissait VERT
 *  sur un cockpit qui n'allumait plus rien. Un portier qu'aucun test ne
 *  traverse ne garde rien.
 *
 *  ⛔ Défensif comme `asSignalCategory` : un moment ajouté côté backend et pas
 *  encore ici ne doit pas faire clignoter une carte d'une couleur que personne
 *  n'a choisie — `MOMENT_COLOR[inconnu]` rendrait `undefined`, donc un
 *  clignotement invisible sur une carte pourtant animée. */
export function asMomentAReprendre(
  raw: string | null | undefined,
): MomentAReprendre | null {
  return raw && (MOMENTS_A_REPRENDRE as readonly string[]).includes(raw)
    ? (raw as MomentAReprendre)
    : null;
}

function normalizeDirection(raw: string | null | undefined): ApiMessage["direction"] {
  const value = String(raw ?? "").toLowerCase();
  return value === "incoming" || value === "in" ? "in" : "out";
}

function normalizeSenderSource(
  raw: unknown,
  direction: ApiMessage["direction"],
): ApiMessage["sender_source"] {
  const value = typeof raw === "string" ? raw.toLowerCase() : "";
  // ⛔ Toute source ABSENTE de cette liste est écrasée en `null`, et le message
  // retombe alors sur le repli legacy (« Envoi manuel »). C'est par ici que
  // `twitter` se perdait sur les messages poussés en direct par le SSE, alors
  // qu'il arrivait correctement par le polling — deux rendus pour la même
  // bulle, selon le chemin qui l'avait apportée.
  if (
    ["fan", "cyrano", "operator", "telegram_manual", "flow", "twitter"].includes(
      value,
    )
  ) {
    return value as ApiMessage["sender_source"];
  }
  return direction === "in" ? "fan" : null;
}

function normalizeMessageEvent(
  conversationId: string,
  message: ApiConversationEvent["message"],
): ApiMessage | null {
  if (!message?.id || !message.created_at) return null;
  const direction = normalizeDirection(message.direction);
  const metadata = message.metadata && typeof message.metadata === "object"
    ? message.metadata
    : {};
  return {
    id: String(message.id),
    direction,
    message_type: String(message.message_type ?? "text"),
    content: message.content ?? null,
    media_file_id: message.media_file_id ?? null,
    media_url: message.media_url ?? (
      message.media_file_id
        ? `/api/conversations/${conversationId}/messages/${message.id}/media`
        : null
    ),
    is_ai_generated: Boolean(message.is_ai_generated),
    sender_source: normalizeSenderSource(message.sender_source, direction),
    flow_id: typeof message.flow_id === "string" ? message.flow_id : null,
    // 🏷️ Le SSE le porte comme le GET : sans cette ligne, un message arrive en
    //    direct n'aurait pas d'etiquette et en gagnerait une au rechargement.
    geste: typeof message.geste === "string" ? message.geste : null,
    created_at: String(message.created_at),
    reply_to_message_id: message.reply_to_message_id
      ? String(message.reply_to_message_id)
      : null,
    telegram_message_id:
      typeof message.telegram_message_id === "number"
        ? message.telegram_message_id
        : null,
    // Un message IA (Cyrano) est envoyé par le BACKEND : quand la bulle arrive
    // via SSE, il est DÉJÀ parti sur Telegram → jamais « Envoi en cours… ».
    // (Sans ça, une copie de la double-publication sans telegram_message_id
    //  restait grisée « Envoi en cours » alors que le message était livré.)
    delivered:
      direction === "in" ||
      Boolean(message.is_ai_generated) ||
      Boolean(message.delivered ?? message.telegram_message_id),
    payment_button: message.payment_button ?? null,
    traduction: message.traduction ?? null,
    paid_media: message.paid_media ?? null,
    manual_payment: message.manual_payment ?? null,
    game_pack: message.game_pack ?? null,
    operator_workos_user_id:
      message.operator_workos_user_id ??
      (typeof metadata.operator_workos_user_id === "string"
        ? metadata.operator_workos_user_id
        : null),
    operator_email:
      message.operator_email ??
      (typeof metadata.operator_email === "string" ? metadata.operator_email : null),
    edited_at: message.edited_at ?? null,
    deleted_at: message.deleted_at ?? null,
    bot_reaction: message.bot_reaction ?? null,
    poll: message.poll ?? null,
    entities: message.entities ?? [],
    inline_keyboard: message.inline_keyboard ?? null,
  };
}

/**
 * Le SSE backend publie sur `conv:events:{creator_id}` — un client reçoit donc
 * TOUS les events de TOUTES ses créatrices, jamais filtrés par conversation
 * (finding C1 : amplification ~30-100× à 1M convos). On filtre côté client : un
 * event qui ne concerne AUCUNE conversation chargée (ni détail ouvert, ni page
 * de liste en cache) est droppé AVANT toute mutation `setQueriesData` — laquelle
 * clone toutes les pages de toutes les variantes de filtre en cache (→ re-render
 * inutile de la grille) même quand aucune carte ne matche.
 *
 * Lecture en PARTIAL match (`getQueriesData(["conversations","list"])`) pour
 * couvrir simultanément toutes les variantes de filtre/recherche en cache (la
 * clé porte un objet `serverFilters`). Early-exit au premier match.
 *
 * « Chargée » = présente dans le cache React Query (pages fetchées), PAS
 * « visible à l'écran » : la grille est virtualisée mais toutes les pages
 * fetchées restent en cache, donc une conv scrollée hors-vue est bien mise à
 * jour. `onReconnect` réinvalide la liste + le détail → rien n'est durablement
 * manqué (et le chemin `new_message` ne PRÉPEND jamais de nouvelle conv : dropper
 * un event de conv inconnue est sans régression, la conv apparaît au refetch).
 */
export function conversationIsLoaded(
  queryClient: QueryClient,
  conversationId: string,
): boolean {
  // Détail complet OU payload léger chat-lite (le chat ouvert n'hydrate que le
  // second — cf `useConversationChatLite`). Vérifier les deux évite de dropper
  // les events SSE d'une conv dont seul le chat-lite est en cache.
  for (const key of conversationDetailKeys(conversationId)) {
    if (queryClient.getQueryData(key)) return true;
  }
  const entries = queryClient.getQueriesData<
    InfiniteData<ApiConversationListResponse, string | null>
  >({ queryKey: ["conversations", "list"] });
  for (const [, data] of entries) {
    for (const page of data?.pages ?? []) {
      for (const conv of page.conversations ?? []) {
        if (conv.id === conversationId) return true;
      }
    }
  }
  return false;
}

/* ====================================================================== */
/* Caches jumeaux d'une conversation : détail complet + chat-lite          */
/* ====================================================================== */

/**
 * Une conversation ouverte a DEUX caches React Query qui partagent la même
 * shape `ApiConversationDetail` :
 *   - `["conversations","detail",id]`    : payload complet (Info/Stats — fan
 *     profile, mémoire, notes, narrative…).
 *   - `["conversations","chat-lite",id]` : payload léger pour ouvrir le chat
 *     vite (mêmes `messages` / `message_count` / `last_message_at` /
 *     `is_cyrano_paused` / `creator_id`, champs lourds vidés côté backend —
 *     cf `get_conversation_chat_lite`).
 *
 * Selon la sous-vue montée (chat vs info/stats), l'un OU l'autre est hydraté.
 * Tout update « live » au niveau message (SSE new_message / edit / delete /
 * react / ppv, et les optimistic sends) doit donc patcher LES DEUX pour rester
 * cohérent quelle que soit la vue — sinon un message arrivé pendant qu'on est
 * sur le chat (lite) n'apparaîtrait pas si l'utilisateur bascule sur Info, et
 * vice-versa. Comme la shape est identique, un updater message-level s'applique
 * tel quel aux deux clés.
 */
export function conversationDetailKeys(conversationId: string): QueryKey[] {
  return [
    ["conversations", "detail", conversationId],
    ["conversations", "chat-lite", conversationId],
  ];
}

/** Applique le MÊME updater au cache détail complet ET au cache chat-lite.
 *  No-op silencieux sur une clé non hydratée (l'updater reçoit `undefined` et
 *  renvoie `undefined`). */
function patchConversationCaches(
  queryClient: QueryClient,
  conversationId: string,
  updater: (
    prev: ApiConversationDetail | undefined,
  ) => ApiConversationDetail | undefined,
): void {
  for (const key of conversationDetailKeys(conversationId)) {
    queryClient.setQueryData<ApiConversationDetail | undefined>(key, updater);
  }
}

/** Annule les fetches en vol des DEUX caches avant un optimistic update —
 *  qu'une réponse réseau en retard n'écrase pas le patch optimiste. */
async function cancelConversationCaches(
  queryClient: QueryClient,
  conversationId: string,
): Promise<void> {
  await Promise.all(
    conversationDetailKeys(conversationId).map((queryKey) =>
      queryClient.cancelQueries({ queryKey }),
    ),
  );
}

/**
 * Une mutation dont le `conversationId` est FIGÉ au moment de l'appel.
 *
 * 🩸 **Jamais de `conversationId` lu depuis le render dans une mutation.**
 * Signalé par Kayla le 20-08-2026 : « j'envoie un message, je clique sur une
 * autre discussion, et il part dans deux discussions, ou dans une autre. »
 * C'était vrai, et jusqu'à l'URL du POST — le fan qui recevait n'était pas
 * celui à qui elle écrivait.
 *
 * Un composer survit au changement de conversation. Deux onglets fan sont deux
 * instances du même panneau au même endroit de l'arbre : React réutilise le
 * composant et lui passe un autre id (`shell/render/TabGroup.tsx`), et un
 * panneau détaché — celui que l'ouverture d'un fan pose à droite par défaut —
 * SUIT la conversation active de son groupe sans jamais se démonter
 * (`FanSubViewPanel`). Il n'y a donc aucun démontage sur lequel compter.
 *
 * Et une mutation EN VOL adopte les options du dernier render — query-core 5.x,
 * `mutationObserver.setOptions` :
 *
 *     else if (this.#currentMutation?.state.status === "pending")
 *       this.#currentMutation.setOptions(this.options)
 *
 * `mutation.execute` lit `options.mutationFn` APRÈS l'`await` de `onMutate`, et
 * `options.onSuccess` APRÈS l'`await` du POST. Deux fenêtres, donc : le message
 * part chez le mauvais fan, ou la bulle rendue s'écrit dans la conversation
 * qu'on vient d'ouvrir pendant que la bulle grise reste orpheline dans l'autre
 * — « le message s'envoie dans deux discussions ».
 *
 * Ici l'id voyage dans les VARIABLES de la mutation, capturées à l'appel et
 * immuables. Chaque handler le reçoit en DERNIER argument (dernier : un
 * handler qui s'en passe garde ses positions).
 *
 * ⚠️ **Le hook appelant nomme son paramètre `conversationIdAffichee`, pas
 * `conversationId`** : le nom `conversationId` n'existe alors dans le hook que
 * comme paramètre figé d'un handler. Un handler qui l'utilise sans le déclarer
 * ne compile pas — c'est ce qui rend la faute impossible, pas la vigilance.
 * Même chose pour ce qu'on en DÉRIVE : une clé de cache se calcule dans le
 * handler, depuis l'id figé (cf `cleDesTags`), jamais au render.
 */
function useConversationMutation<TInput, TResult, TCtx = unknown>(
  conversationIdAffichee: string | undefined,
  handlers: {
    mutationFn: (input: TInput, conversationId: string) => Promise<TResult>;
    onMutate?: (
      input: TInput,
      conversationId: string,
    ) => Promise<TCtx> | TCtx;
    onSuccess?: (
      data: TResult,
      input: TInput,
      ctx: TCtx | undefined,
      conversationId: string,
    ) => void;
    onError?: (
      error: Error,
      input: TInput,
      ctx: TCtx | undefined,
      conversationId: string,
    ) => void;
    onSettled?: (
      data: TResult | undefined,
      error: Error | null,
      input: TInput,
      ctx: TCtx | undefined,
      conversationId: string,
    ) => void;
  },
): {
  mutateAsync: (input: TInput) => Promise<TResult>;
  /** L'envoi vers une conversation NOMMÉE — pour un geste qui en enchaîne un
   *  autre et doit lui passer l'id qu'il a lui-même figé (cf `useSendTheMenu`,
   *  dont les bulles suivent la carte). Un tel appelant ne peut pas se
   *  contenter du hook lié au render : c'est justement ce qui bougeait. */
  mutateAsyncDans: (conversationId: string, input: TInput) => Promise<TResult>;
  isPending: boolean;
  error: Error | null;
} {
  interface Vars {
    conversationId: string;
    input: TInput;
  }
  const { mutationFn, onMutate, onSuccess, onError, onSettled } = handlers;
  const mutation = useMutation<TResult, Error, Vars, TCtx>({
    mutationFn: (vars) => mutationFn(vars.input, vars.conversationId),
    ...(onMutate
      ? { onMutate: (vars) => onMutate(vars.input, vars.conversationId) }
      : {}),
    ...(onSuccess
      ? {
          onSuccess: (data, vars, ctx) =>
            onSuccess(data, vars.input, ctx, vars.conversationId),
        }
      : {}),
    ...(onError
      ? {
          onError: (error, vars, ctx) =>
            onError(error, vars.input, ctx, vars.conversationId),
        }
      : {}),
    ...(onSettled
      ? {
          onSettled: (data, error, vars, ctx) =>
            onSettled(data, error, vars.input, ctx, vars.conversationId),
        }
      : {}),
  });
  const { mutateAsync } = mutation;
  const lancer = useCallback(
    (input: TInput): Promise<TResult> => {
      if (!conversationIdAffichee) {
        return Promise.reject(
          new Error("conversationId is required for this mutation"),
        );
      }
      return mutateAsync({ conversationId: conversationIdAffichee, input });
    },
    [conversationIdAffichee, mutateAsync],
  );
  const lancerDans = useCallback(
    (conversationId: string, input: TInput): Promise<TResult> =>
      mutateAsync({ conversationId, input }),
    [mutateAsync],
  );
  return {
    mutateAsync: lancer,
    mutateAsyncDans: lancerDans,
    isPending: mutation.isPending,
    error: mutation.error,
  };
}

/** Lit la conversation depuis le 1er cache hydraté (détail OU chat-lite).
 *  Sert aux mutations qui ont besoin d'un champ (ex: `creator_id`) sans savoir
 *  quelle sous-vue est montée. */
function readConversationDetailCache(
  queryClient: QueryClient,
  conversationId: string,
): ApiConversationDetail | undefined {
  for (const key of conversationDetailKeys(conversationId)) {
    const cached = queryClient.getQueryData<ApiConversationDetail>(key);
    if (cached) return cached;
  }
  return undefined;
}

/** Ajoute un message serveur aux DEUX caches (détail + chat-lite), idempotent
 *  par id (no-op si déjà présent — ex: SSE est arrivé avant le onSuccess).
 *  Pattern `onSuccess` partagé par les envois non-optimistes (poll, sticker,
 *  gif, game-pack…). */
function appendMessageToConversationCaches(
  queryClient: QueryClient,
  conversationId: string,
  saved: ApiMessage,
): void {
  patchConversationCaches(queryClient, conversationId, (prev) => {
    if (!prev) return prev;
    if (prev.messages.some((m) => m.id === saved.id)) return prev;
    return {
      ...prev,
      last_message_at: saved.created_at,
      message_count: (prev.message_count ?? 0) + 1,
      messages: [...prev.messages, saved],
    };
  });
}

/* ====================================================================== */
/* Optimistic outgoing — bulle locale immédiate (media/voice/payment)      */
/* ====================================================================== */

/** Construit un message optimiste sortant (`optimistic-*`, `delivered:false`,
 *  `sender_source:"operator"`). Un `media_file_id` non-null + un `message_type`
 *  média + un `media_url` (blob upload OU preview Vault) → la bulle montre
 *  l'aperçu DÈS le clic (cf `buildChatItem` côté views, qui gate le rendu média
 *  sur `media_file_id`). `created_at` n'utilise pas `Date.now()` directement
 *  pour rester cohérent avec le placeholder texte existant. */
export function buildOptimisticOutgoing(
  fields: Partial<ApiMessage> & { message_type: string },
): ApiMessage {
  return {
    id: `optimistic-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
    direction: "out",
    message_type: fields.message_type,
    content: fields.content ?? null,
    media_file_id: fields.media_file_id ?? null,
    media_url: fields.media_url ?? null,
    is_ai_generated: false,
    sender_source: "operator",
    flow_id: null,
    created_at: fields.created_at ?? new Date().toISOString(),
    reply_to_message_id: fields.reply_to_message_id ?? null,
    // Une bulle optimiste n'est pas encore partie : elle n'a aucun accuse de
    // transport, et `null` le dit. Le refetch la remplacera par la vraie.
    telegram_message_id: null,
    delivered: false,
    payment_button: fields.payment_button ?? null,
    paid_media: fields.paid_media ?? null,
    manual_payment: fields.manual_payment ?? null,
    game_pack: fields.game_pack ?? null,
    operator_workos_user_id: fields.operator_workos_user_id ?? null,
    operator_email: fields.operator_email ?? null,
    edited_at: null,
    deleted_at: null,
    bot_reaction: null,
    poll: fields.poll ?? null,
    entities: fields.entities ?? [],
    inline_keyboard: fields.inline_keyboard ?? null,
  };
}

/** Insère un placeholder en fin de thread dans les DEUX caches — bulle « Envoi
 *  en cours… » affichée immédiatement. (exporté pour tests) */
export function insertOptimisticMessage(
  queryClient: QueryClient,
  conversationId: string,
  placeholder: ApiMessage,
): void {
  patchConversationCaches(queryClient, conversationId, (prev) => {
    if (!prev) return prev;
    return {
      ...prev,
      last_message_at: placeholder.created_at,
      message_count: (prev.message_count ?? 0) + 1,
      messages: [...prev.messages, placeholder],
    };
  });
}

/** Remplace le placeholder `tempId` par le message serveur `saved` (ou l'ajoute
 *  s'il a déjà disparu), et garantit qu'il n'en reste **qu'une seule copie**.
 *
 *  🩸 **Le doublon que ça ferme, signalé par Kayla le 23-08-2026** : « sur
 *  Telegram on le voit une fois, dans le cockpit on le voit en double » — sur
 *  un cadeau vidéo. Le fan n'avait bien reçu qu'un média (un seul `Message` en
 *  base, une seule `ContentDelivery` : vérifié), c'est le cockpit qui l'affichait
 *  deux fois.
 *
 *  La cause tient à l'ordre : pendant qu'une VIDÉO monte vers Telegram (des
 *  secondes, là où une photo est instantanée), le rafraîchissement du fil pose
 *  déjà le message serveur dans le cache. Au retour du POST, ce code remplaçait
 *  le placeholder par `saved` — qui était donc présent DEUX fois. La garde
 *  `!messages.some(…)` ne servait que le cas inverse (placeholder disparu), et
 *  l'en-tête de cette fonction annonçait pourtant l'idempotence.
 *
 *  ⛔ Ne pas « corriger » ça en retirant le placeholder plus tôt : c'est lui qui
 *  tient la bulle « envoi en cours » pendant tout l'upload. C'est bien le
 *  dédoublonnage par `saved.id` qui est la propriété, et il vaut pour TOUS les
 *  envois — le picker autant que le bouton Cadeau. */
export function reconcileOptimisticMessage(
  queryClient: QueryClient,
  conversationId: string,
  tempId: string | null | undefined,
  saved: ApiMessage,
): void {
  patchConversationCaches(queryClient, conversationId, (prev) => {
    if (!prev) return prev;
    let replaced = false;
    const messages = prev.messages.map((m) => {
      if (tempId && m.id === tempId) {
        replaced = true;
        return saved;
      }
      return m;
    });
    if (!replaced && !messages.some((m) => m.id === saved.id)) {
      messages.push(saved);
    }
    // UNE seule copie de `saved`, quel que soit le chemin par lequel il est
    // arrivé. On garde la PREMIÈRE occurrence : c'est celle qui tient la place
    // du placeholder, donc la bulle ne saute pas en bas de la liste.
    let vu = false;
    const dedupes = messages.filter((m) => {
      if (m.id !== saved.id) return true;
      if (vu) return false;
      vu = true;
      return true;
    });
    return { ...prev, last_message_at: saved.created_at, messages: dedupes };
  });
}

/** Retire le placeholder `tempId` (rollback d'un envoi échoué). (exporté pour tests) */
export function rollbackOptimisticMessage(
  queryClient: QueryClient,
  conversationId: string,
  tempId: string | null | undefined,
): void {
  if (!tempId) return;
  patchConversationCaches(queryClient, conversationId, (prev) => {
    if (!prev) return prev;
    return {
      ...prev,
      messages: prev.messages.filter((m) => m.id !== tempId),
      message_count: Math.max(0, (prev.message_count ?? 1) - 1),
    };
  });
}

/** Débounce de l'invalidation `regie_job` (CONTRAT RÉGIE DISPATCH point 2) —
 *  cf `createCoalescingTrigger` juste au-dessus. */
const REGIE_JOBS_INVALIDATE_DEBOUNCE_MS = 500;

/**
 * Au plus UN rafraîchissement de la liste toutes les 10 s, par chatteur.
 *
 * 📏 Mesuré en prod le 11-10-2026 : `GET /api/conversations?limit=100&sort=signaux
 * &canaux=telegram` tombait 277 fois/min pour 9 chatteurs (1,2 appel/s pour UN
 * seul), 230 Ko la réponse, 600 Mo en 10 min ; l'API et Postgres à 100 % CPU,
 * la file des chatteurs mettait 1 à 4 s à s'afficher. Cause : le backend envoie
 * TOUS les événements de l'organisation à chaque chatteur, donc un `new_message`
 * hors des pages chargées arrivait sans arrêt, et chacun invalidait la liste
 * entière (toutes les pages chargées, relues l'une après l'autre) toutes les
 * 1,5 s.
 */
export const DELAI_RAFRAICHISSEMENT_LISTE_MS = 10_000;

/**
 * Une même conversation hors des pages chargées ne relance la liste qu'une
 * fois toutes les 2 min. Un nouveau fan apparaît toujours tout de suite (son
 * 1ᵉʳ message passe) ; une conversation que le filtre du chatteur écarte
 * (autre canal, payeurs…) n'y entre jamais, et sans ce délai CHACUN de ses
 * messages relisait la liste pour rien.
 *
 * 📏 Mesuré en prod le 03-11-2026 : `GET /api/conversations` 4 255 fois en
 * 30 min (2,4/s, ~200 ms de Postgres chacun), le pool de l'API épuisé, les
 * workers uvicorn qui mouraient toutes les 15 à 30 min.
 */
export const DELAI_CONV_HORS_LISTE_MS = 120_000;
const convsHorsListeSignalees = new Map<string, number>();

export function doitRafraichirPourConvHorsListe(conversationId: string, now: number): boolean {
  const derniere = convsHorsListeSignalees.get(conversationId);
  if (derniere !== undefined && now - derniere < DELAI_CONV_HORS_LISTE_MS) return false;
  convsHorsListeSignalees.set(conversationId, now);
  return true;
}

/**
 * Ce que la liste ne fait JAMAIS d'elle-même : relire toutes ses pages. Ni au
 * remontage de l'écran (changer d'onglet, revenir sur Conversations), ni au
 * retour du réseau, ni au focus. Le live passe par `rafraichirLaListe` (page 1)
 * et le SSE ; seul le bouton « Rafraîchir » recharge tout.
 *
 * 📏 Mesuré en prod le 12-10-2026, après #1187 : 3 chatteurs sur 16 faisaient
 * 60 % des appels — une quinzaine de pages chargées (~1 500 conversations),
 * toutes relues ensemble toutes les ~30 s.
 */
export const RAFRAICHISSEMENT_DE_LA_LISTE = {
  staleTime: 10_000,
  refetchOnWindowFocus: false,
  refetchOnMount: false,
  refetchOnReconnect: false,
} as const;

/** Les filtres que la liste envoie au serveur (3ᵉ élément de sa queryKey). */
export type FiltresServeurListe = {
  showTimeWasters: boolean;
  pinned: boolean;
  payeurs: boolean;
  canalFiltre: FiltersState["canalFiltre"];
  q: string;
};
export type TriServeurListe = "priorite" | "signaux" | "chrono" | "oldest" | null;

/**
 * UNE page de `/api/conversations`. Partagée par la liste (`useInfiniteQuery`,
 * toutes les pages) et par le rafraîchissement de la page 1 seule — sinon les
 * deux dériveraient (un filtre ajouté à l'un et oublié dans l'autre).
 */
export function fetchPageDeConversations(
  serverFilters: FiltresServeurListe,
  triServeur: TriServeurListe,
  cursor: string | null,
): Promise<ApiConversationListResponse> {
  return cyranoApi.get<ApiConversationListResponse>("/api/conversations", {
    params: {
      limit: 100,
      cursor,
      // 🎯 Absent = défaut serveur (newest) : on n'envoie le tri que quand
      // il change quelque chose, comme les filtres au-dessous.
      sort: triServeur ?? undefined,
      // Réaffiche les Time Waster uniquement quand la case est cochée
      // (sinon absent → défaut serveur = masqués).
      include_time_wasters: serverFilters.showTimeWasters ? true : undefined,
      // Épinglées / payeurs : absents quand décochés (défaut serveur = pas
      // de restriction). 🪤 `payeurs` est un paramètre que Cyrano peut ne
      // pas encore connaître (le cockpit part toujours en premier) : il
      // l'ignore alors, et `filterPayeurs` tient la page chargée vraie.
      pinned: serverFilters.pinned ? true : undefined,
      payeurs: serverFilters.payeurs ? true : undefined,
      // 🐦 Absent SEULEMENT sur « tous » : le défaut serveur est « tous les
      // canaux », donc ne rien envoyer est exactement ce que « tous » veut
      // dire. On n'envoie jamais `canaux=telegram,twitter` : ça reviendrait
      // au même aujourd'hui et figerait la liste des canaux ici — le jour
      // où il y en a un troisième, il serait masqué sans que personne l'ait
      // demandé.
      canaux:
        serverFilters.canalFiltre === "tous"
          ? undefined
          : serverFilters.canalFiltre,
      // 🎯 Plus de `masquer_prises` depuis le 11-09 (Kayla : « je veux les
      // réafficher, juste qu'ils voient qui y a dans la conversation ») :
      // la liste montre tout, et chaque ligne dit qui la tient — le
      // cadenas de l'onglet, et l'étoile de celui qui se l'est réservée.
      q: serverFilters.q || undefined,
    },
  });
}

/** Les rafraîchissements en vol, par client puis par clé de requête : un
 *  second ne part pas tant que le précédent n'a pas rendu (les deux montages
 *  du SSE, ou un déclencheur qui rattrape un fetch lent, ne doublent pas). */
const rafraichissementsEnVol = new WeakMap<QueryClient, Set<string>>();

/**
 * Relit la PAGE 1 SEULE de chaque liste active et la remplace dans le cache ;
 * les pages suivantes restent telles quelles (donc plus de relecture de toutes
 * les pages chargées, une par une). Une erreur réseau est ignorée : la liste
 * reste ce qu'elle était, le prochain événement réessaiera.
 *
 * Une conversation qui vient de remonter en page 1 peut encore traîner dans la
 * page 2 déjà chargée : on la retire d'en bas, sinon elle apparaîtrait deux
 * fois (même `key` React).
 */
export async function rafraichirPage1DeLaListe(queryClient: QueryClient): Promise<void> {
  let enVol = rafraichissementsEnVol.get(queryClient);
  if (!enVol) {
    enVol = new Set();
    rafraichissementsEnVol.set(queryClient, enVol);
  }
  const dejaEnVol = enVol;
  const actives = queryClient
    .getQueryCache()
    .findAll({ queryKey: ["conversations", "list"], type: "active" });
  await Promise.all(
    actives.map(async (query) => {
      const clef = query.queryKey;
      const serverFilters = clef[2] as FiltresServeurListe | undefined;
      const triServeur = (clef[3] ?? null) as TriServeurListe;
      if (!serverFilters) return;
      const idEnVol = `rafraichir-liste-page-1-seule:${query.queryHash}`;
      if (dejaEnVol.has(idEnVol)) return;
      dejaEnVol.add(idEnVol);
      try {
        const fraiche = await fetchPageDeConversations(serverFilters, triServeur, null);
        if (!Array.isArray(fraiche?.conversations)) return;
        const idsFrais = new Set(fraiche.conversations.map((c) => c.id));
        queryClient.setQueryData<InfiniteData<ApiConversationListResponse, string | null>>(
          clef,
          (ancien) => {
            if (!ancien || ancien.pages.length === 0) return ancien;
            const suivantes = ancien.pages.slice(1).map((page) => ({
              ...page,
              conversations: (page.conversations ?? []).filter((c) => !idsFrais.has(c.id)),
            }));
            return {
              pages: [fraiche, ...suivantes],
              pageParams: [null, ...ancien.pageParams.slice(1)],
            };
          },
        );
      } catch {
        // La liste reste ce qu'elle était.
      } finally {
        dejaEnVol.delete(idEnVol);
      }
    }),
  );
}

/**
 * Le déclencheur du rafraîchissement : le PREMIER appel relit tout de suite, les
 * suivants pendant les `delaiMs` qui viennent sont absorbés et n'en font qu'UN,
 * au bout du délai (pas un débounce qui se réarme : sous un flux continu il ne
 * partirait jamais). `annuler()` au démontage.
 */
export function creerRafraichisseurDeListe(
  queryClient: QueryClient,
  delaiMs: number = DELAI_RAFRAICHISSEMENT_LISTE_MS,
): { (): void; annuler: () => void } {
  let minuteur: ReturnType<typeof setTimeout> | null = null;
  let enAttente = false;
  const lancer = () => {
    void rafraichirPage1DeLaListe(queryClient);
    minuteur = setTimeout(() => {
      minuteur = null;
      if (enAttente) {
        enAttente = false;
        lancer();
      }
    }, delaiMs);
  };
  const declencher = (() => {
    if (minuteur) {
      enAttente = true;
      return;
    }
    lancer();
  }) as { (): void; annuler: () => void };
  declencher.annuler = () => {
    if (minuteur) clearTimeout(minuteur);
    minuteur = null;
    enAttente = false;
  };
  return declencher;
}

/** UN déclencheur par client — cf `rafraichirLaListe`. */
const rafraichisseursParClient = new WeakMap<
  QueryClient,
  ReturnType<typeof creerRafraichisseurDeListe>
>();

/**
 * Remet la liste à jour après un geste ou un event : la PAGE 1 seule, au plus
 * une fois toutes les 10 s (le premier appel part tout de suite), avec UN seul
 * déclencheur par client — partagé par les deux écrans qui montent la liste
 * (Conversations, Discussions libres) et par tous les gestes du chatteur
 * (envoi, lu, pause, écarter…).
 *
 * 📏 Mesuré en prod le 12-10-2026, après #1179 : encore 6 640 appels à
 * `/api/conversations` en 30 min (232 Ko chacun, 1,4 Go), un chatteur toutes
 * les 1 à 3 s, la moitié sur les pages 2 et suivantes — chaque geste invalidait
 * la liste ENTIÈRE, toutes pages chargées relues. L'API répondait en 3 à 5 s au
 * p95 pendant les shifts. Le bouton « Rafraîchir » garde, lui, le
 * rechargement complet : il est rare et voulu.
 */
export function rafraichirLaListe(queryClient: QueryClient): void {
  let rafraichir = rafraichisseursParClient.get(queryClient);
  if (!rafraichir) {
    rafraichir = creerRafraichisseurDeListe(queryClient);
    rafraichisseursParClient.set(queryClient, rafraichir);
  }
  rafraichir();
}

/**
 * Fabrique un déclencheur "coalescé" : la 1ʳᵉ invocation arme un timer de
 * `delayMs` ; toute invocation supplémentaire pendant que le timer est en vol
 * est absorbée (le timer n'est PAS réarmé) — une rafale d'appels ne produit
 * qu'UN seul `fn()`. Même sémantique que le `pendingListRefetch` de
 * `useConversationEvents` (event `new_message` sur conv non chargée) ;
 * EXTRAIT ici (testable sans monter tout `useConversationEvents`/SSE) pour le
 * nouvel event `regie_job` — cf CONTRAT RÉGIE DISPATCH point 2. `cancel()`
 * annule un timer en vol (cleanup d'unmount).
 */
export function createCoalescingTrigger(
  fn: () => void,
  delayMs: number,
): { (): void; cancel: () => void } {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const trigger = (() => {
    if (timer) return;
    timer = setTimeout(() => {
      timer = null;
      fn();
    }, delayMs);
  }) as { (): void; cancel: () => void };
  trigger.cancel = () => {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  };
  return trigger;
}

function useConversationEvents(enabled: boolean) {
  const queryClient = useQueryClient();
  const setFlowNotice = useSetAtom(flowNoticeAtom);
  const setTyping = useSetAtom(typingByConversationAtom);
  const setCyranoCountdown = useSetAtom(cyranoCountdownByConversationAtom);
  const setLiveTurns = useSetAtom(liveTurnsAtom);
  const setCyranoCancelledAt = useSetAtom(cyranoCancelledAtByConversationAtom);
  const setDecisionProposal = useSetAtom(decisionProposalByConversationAtom);
  const setRegieDecisionOutcome = useSetAtom(regieDecisionOutcomeAtom);
  const setSseReconnectNonce = useSetAtom(sseReconnectNonceAtom);

  // Refetch de la PAGE 1 de la liste pour faire remonter une conversation NON
  // chargée (nouveau fan, ou conv hors des pages chargées) qui reçoit un
  // message : le filtre fan-out C1 droppe sinon son event. Le déclencheur est
  // celui du client (`rafraichirLaListe`), partagé avec l'autre montage de la
  // liste et avec les gestes : il n'est pas annulé au démontage.
  const rafraichirLaListeDuClient = () => rafraichirLaListe(queryClient);

  // Invalidation DÉBOUNCÉE de la file dispatch "mine" sur event `regie_job`
  // (CONTRAT RÉGIE DISPATCH point 2, extension ADDITIVE — n'affecte aucun
  // event existant). Créée UNE FOIS (lazy ref init : le trigger doit garder
  // son timer en vol à travers les renders, cf `createCoalescingTrigger`),
  // jamais recréée tant que le composant vit.
  const regieJobsInvalidateRef = useRef<ReturnType<typeof createCoalescingTrigger> | null>(
    null,
  );
  if (!regieJobsInvalidateRef.current) {
    regieJobsInvalidateRef.current = createCoalescingTrigger(() => {
      void queryClient.invalidateQueries({ queryKey: ["regie", "jobs", "mine"] });
    }, REGIE_JOBS_INVALIDATE_DEBOUNCE_MS);
  }

  useEffect(
    () => () => {
      regieJobsInvalidateRef.current?.cancel();
    },
    [],
  );

  useSSE<ApiConversationEvent>({
    url: buildUrl("/api/conversations/events"),
    enabled,
    eventName: "conversation",
    reconnectDelay: 1_500,
    // Ferme le stream quand l'app passe en arriere-plan (onglet caché / PWA
    // backgroundée) : zero traitement d'events + zero fan-out tant que l'user
    // ne regarde pas, et `onReconnect` rattrape ce qui a été publié pendant la
    // coupure au retour. Gain batterie/CPU mobile sur le stream le plus chargé.
    closeWhenHidden: true,
    onEvent: (event) => {
      if (!event?.conversation_id) return;

      // Notif flow ephemere (miroir de notify_flow_group). Pas un message :
      // on ne touche pas au cache des conversations, juste l'atom relaye vers
      // la ConvStatusBar concernee (nonce croissant pour re-declencher meme
      // si deux notices ont le meme texte).
      if (event.type === "flow_notice") {
        const content = (event.content ?? "").trim();
        if (!content) return;
        setFlowNotice((prev) => ({
          conversationId: event.conversation_id,
          content,
          nonce: (prev?.nonce ?? 0) + 1,
        }));
        return;
      }

      // Décision proposée (mode PROPOSE, cf PLAN_CYRANO_MODE_PROPOSE.md
      // Annexe A). Traité AVANT le filtre « conversation chargée » (même
      // raison que flow_notice juste au-dessus) : une décision peut viser
      // une conversation hors des pages actuellement chargées — le badge de
      // LISTE (`pending_decision`) doit s'allumer/s'éteindre quand même.
      if (event.type === "decision_proposal") {
        const convId = event.conversation_id;
        const proposalId = event.proposal_id ?? null;
        const resolved = event.status === "resolved";
        const variants: DecisionProposalVariant[] = (event.variants ?? []).map(
          (v) => ({
            variantId: v.variant_id,
            action: v.action,
            label: v.label,
            priceCents: v.price_cents ?? null,
            mediaThumbUrl: v.media_thumb_url ?? null,
          }),
        );
        const topLabel = variants[0]?.label ?? null;

        setDecisionProposal((prev) => {
          const pruned = pruneDecisionProposals(prev);
          if (resolved) {
            // Idempotence par proposal_id : une résolution en retard ne doit
            // pas effacer une proposition PLUS RÉCENTE déjà arrivée entre-
            // temps pour la même conversation (anti-fatigue : 1 seule
            // active, remplacée par la suivante).
            const existing = pruned[convId];
            if (!existing || (proposalId && existing.proposalId !== proposalId)) {
              return pruned;
            }
            const next = { ...pruned };
            delete next[convId];
            return next;
          }
          if (!proposalId) return pruned; // payload invalide — ignore plutôt que crasher
          return {
            ...pruned,
            [convId]: {
              proposalId,
              stageLabel: event.stage_label ?? null,
              expiresAt: event.expires_at ?? null,
              variants,
            },
          };
        });

        // Champ liste (patron `conversation_state_changed` plus bas) :
        // allume/éteint `pending_decision` même pour une conv non chargée en
        // detail/chat-lite — on ne touche QUE le cache liste ici.
        queryClient.setQueriesData<
          InfiniteData<ApiConversationListResponse, string | null>
        >({ queryKey: ["conversations", "list"] }, (old) => {
          if (!old) return old;
          let touched = false;
          const pages = old.pages.map((page) => {
            let pageTouched = false;
            const conversations = page.conversations.map((conv) => {
              if (conv.id !== convId) return conv;
              const nextPending = !resolved;
              const nextLabel = nextPending ? topLabel : null;
              if (
                (conv.pending_decision ?? false) === nextPending &&
                (conv.pending_decision_label ?? null) === nextLabel
              )
                return conv;
              pageTouched = true;
              touched = true;
              return {
                ...conv,
                pending_decision: nextPending,
                pending_decision_label: nextLabel,
              };
            });
            return pageTouched ? { ...page, conversations } : page;
          });
          if (!touched) return old;
          return { ...old, pages };
        });
        return;
      }

      // Dispatch v2 (CONTRAT RÉGIE DISPATCH point 2, extension ADDITIVE) : un
      // job a été créé/(ré)assigné/résolu/expiré/annulé. On NE FILTRE PAS par
      // identité (`assigned_to`) — tout event `regie_job` déclenche un
      // refetch DÉBOUNCÉ (coalesce les rafales en UN seul refetch, même
      // sémantique que `pendingListRefetch` ci-dessus pour `new_message`) de
      // la file "mine" ; le serveur reste l'autorité sur ce qui appartient au
      // caller (GET /api/regie/jobs/mine re-scope à chaque refetch). Traité
      // AVANT le filtre "conversation chargée" (même raison que
      // decision_proposal/flow_notice ci-dessus : un job peut viser une
      // conversation hors des pages de la liste actuellement chargées).
      if (event.type === "regie_job") {
        regieJobsInvalidateRef.current?.();
        return;
      }

      // Portefeuille de La Régie (mission A2 point 4, extension ADDITIVE) :
      // une vente a été résolue/payée. Relayé TEL QUEL (camelCase, `nonce`
      // incrémenté) via `regieDecisionOutcomeAtom` — c'est le consommateur
      // qui décide de célébrer ou non (identité + fraîcheur). Traité AVANT le
      // filtre "conversation chargée" (même raison que decision_proposal/
      // regie_job ci-dessus : la vente peut concerner une conversation hors
      // des pages de la liste actuellement chargées — le portefeuille n'a de
      // toute façon rien à voir avec le cache liste).
      if (event.type === "decision_outcome") {
        setRegieDecisionOutcome((prev) => ({
          conversationId: event.conversation_id,
          proposalId: event.proposal_id ?? null,
          orderId: event.order_id ?? null,
          memberId: event.member_id ?? null,
          amountCents: event.amount_cents ?? 0,
          currency: (event.currency as "USD" | "EUR" | undefined) ?? "EUR",
          resolvedAt: event.resolved_at ?? null,
          paidAt: event.paid_at ?? null,
          nonce: (prev?.nonce ?? 0) + 1,
        }));
        return;
      }

      // Filtre fan-out SSE (C1) : ignore les events des conversations non
      // chargées (le backend ne shard pas par conversation → on reçoit tout).
      // Placé APRÈS flow_notice (traité au-dessus, jamais filtré : il vise une
      // ConvStatusBar qui peut concerner une conv hors des pages de la liste).
      if (!conversationIsLoaded(queryClient, event.conversation_id)) {
        // Mais un `new_message` sur une conv non chargée peut être une NOUVELLE
        // conversation (nouveau fan) — sinon elle n'apparaît qu'au poll 60s.
        // On programme un refetch débouncé de la liste pour la surfacer vite,
        // sans pousser une carte incomplète dans le cache.
        if (
          event.type === "new_message" &&
          doitRafraichirPourConvHorsListe(event.conversation_id, Date.now())
        ) {
          rafraichirLaListeDuClient();
        }
        return;
      }

      // Indicateur « le fan écrit… » : event éphémère. On stampe l'expiration
      // dans l'atom (et on élague les entrées expirées). Pas un message → on ne
      // touche jamais au cache des conversations.
      if (event.type === "typing") {
        const untilMs = event.until ? Date.parse(event.until) : NaN;
        if (Number.isNaN(untilMs) || untilMs <= Date.now()) return;
        const convId = event.conversation_id;
        setTyping((prev) => {
          const now = Date.now();
          const next: Record<string, number> = {};
          for (const [k, v] of Object.entries(prev)) {
            if (v > now) next[k] = v;
          }
          next[convId] = untilMs;
          return next;
        });
        return;
      }

      // ── Timeline Cyrano : compte à rebours avant l'envoi du prochain msg ──
      // Events éphémères (pas des messages) → on ne touche jamais au cache des
      // conversations, juste l'atom dédié. L'envoi effectif efface le compteur
      // via le `new_message` (out) plus bas ; cyrano_cancelled l'efface aussi.
      if (event.type === "cyrano_cancelled") {
        const convId = event.conversation_id;
        setLiveTurns((prev) => {
          if (!(convId in prev)) return prev;
          const next = { ...prev };
          delete next[convId];
          return next;
        });
        setCyranoCountdown((prev) => {
          if (!(convId in prev)) return prev;
          const next = { ...prev };
          delete next[convId];
          return next;
        });
        // Additif (SPEC §10 — La Régie) : note l'instant d'annulation pour le
        // chip « annulé » transitoire. N'altère PAS la suppression du compteur
        // ci-dessus ; aucun consommateur existant ne lit cet atom.
        setCyranoCancelledAt((prev) => ({ ...prev, [convId]: Date.now() }));
        return;
      }
      // Une etape du tour EN COURS. On accumule ; la vue Observation affiche la
      // progression. Rien n'est persiste : `finalize` reste la source de verite.
      if (event.type === "pipeline_step") {
        const convId = event.conversation_id;
        setLiveTurns((prev) => {
          const base = pruneLiveTurns(prev);
          const cur = base[convId];
          const step = {
            name: event.name || "(etape sans nom)",
            spanType: event.span_type ?? null,
            durationMs: event.duration_ms ?? null,
            model: event.model ?? null,
            errored: Boolean(event.errored),
            endedAt: event.ended_at ?? null,
          };
          // Un run_id different = un nouveau tour : on repart de zero plutot
          // que d'empiler deux tours dans la meme carte.
          const sameRun =
            cur && (!event.pipeline_run_id || cur.runId === event.pipeline_run_id);
          if (!cur || !sameRun) {
            return {
              ...base,
              [convId]: {
                runId: event.pipeline_run_id ?? null,
                startedAt: Date.now(),
                steps: [step],
                done: false,
              },
            };
          }
          return {
            ...base,
            [convId]: { ...cur, steps: [...cur.steps, step], done: false },
          };
        });
        return;
      }
      if (event.type === "cyrano_generating" || event.type === "agent_thinking") {
        // Cyrano commence à traiter le message (avant même le délai). Deux sources :
        //   - `agent_thinking` : émis de façon FIABLE par le worker (preparation_phase)
        //     dès qu'un job proceed → c'est le signal principal.
        //   - `cyrano_generating` : émis juste avant la génération (secondaire).
        // Pas d'échéance encore ; on pose un compteur en phase "generating" qui
        // s'auto-efface après un délai de sécurité si rien ne suit. `cyrano_scheduled`
        // (si émis) le remplace ; sinon le `new_message` (out) l'efface à la livraison.
        const convId = event.conversation_id;
        const now = Date.now();
        setCyranoCountdown((prev) => ({
          ...pruneCountdowns(prev),
          [convId]: {
            startAt: now,
            deliverAt: now + CYRANO_GENERATING_TIMEOUT_MS,
            readAt: now + CYRANO_GENERATING_TIMEOUT_MS,
            typingAt: now + CYRANO_GENERATING_TIMEOUT_MS,
            phase: "generating",
            draftText: null,
          },
        }));
        return;
      }
      if (event.type === "cyrano_edited") {
        // Le brouillon a été réécrit par un chatter → maj du texte affiché.
        const convId = event.conversation_id;
        const text = event.draft_text ?? "";
        setCyranoCountdown((prev) => {
          const rec = prev[convId];
          if (!rec) return prev;
          return { ...prev, [convId]: { ...rec, draftText: text } };
        });
        return;
      }
      if (
        event.type === "cyrano_scheduled" ||
        event.type === "cyrano_read" ||
        event.type === "cyrano_typing"
      ) {
        const convId = event.conversation_id;
        // Le tour a rendu son brouillon : la progression est terminee. On la
        // marque `done` plutot que de l'effacer — l'operateur voit la derniere
        // cascade quelques secondes avant qu'elle s'efface d'elle-meme.
        if (event.type === "cyrano_scheduled") {
          setLiveTurns((prev) => {
            const cur = prev[convId];
            if (!cur || cur.done) return prev;
            return { ...prev, [convId]: { ...cur, done: true } };
          });
        }
        // scheduled : horloge serveur = scheduled_at ; read/typing : now.
        const serverNow = event.scheduled_at ?? event.now ?? null;
        const deliverAt = anchorCyranoDeadline(event.deliver_at, serverNow);
        if (Number.isNaN(deliverAt)) return;
        setCyranoCountdown((prev) => {
          const pruned = pruneCountdowns(prev);
          const existing = pruned[convId];
          if (event.type === "cyrano_scheduled") {
            return {
              ...pruned,
              [convId]: {
                startAt: Date.now(),
                deliverAt,
                readAt: anchorCyranoDeadline(event.read_at, serverNow) || deliverAt,
                typingAt:
                  anchorCyranoDeadline(event.typing_at, serverNow) || deliverAt,
                phase: "waiting",
                draftText: event.draft_text ?? null,
              },
            };
          }
          // read / typing : faire avancer la phase. Si le scheduled a été
          // manqué (cockpit ouvert en cours de délai), on crée un compteur
          // minimal ancré sur deliver_at (draftText hydraté ensuite via GET).
          const phase: CyranoCountdownPhase =
            event.type === "cyrano_typing" ? "typing" : "read";
          const base: CyranoCountdown = existing ?? {
            startAt: Date.now(),
            deliverAt,
            readAt: Date.now(),
            typingAt: deliverAt,
            phase,
            draftText: null,
          };
          return {
            ...pruned,
            [convId]: {
              ...base,
              deliverAt,
              ...(event.type === "cyrano_typing"
                ? { typingAt: Math.min(base.typingAt, Date.now()) }
                : { readAt: Math.min(base.readAt, Date.now()) }),
              phase,
            },
          };
        });
        return;
      }

      if (event.type === "conversation_state_changed") {
        // 🧵 Un flow vient de se terminer sur cette conversation : le moment
        //    `flow_termine` se dérive côté serveur (dernier sortant + aucun run
        //    vivant), donc on RELIT la liste et les runs — pas de patch local.
        if (event.flow_run) {
          rafraichirLaListe(queryClient);
          void queryClient.invalidateQueries({ queryKey: ["flow-last-run"] });
          void queryClient.invalidateQueries({ queryKey: ["flow-active-run"] });
        }
        // État conversation poussé par le backend. Deux dimensions, patchées
        // INDÉPENDAMMENT (l'event ne porte que les champs concernés) :
        //   - `needs_attention` (fan chaud à reprendre / repris)
        //   - `content_mode` (start/stop/auto-stop) → carte + barre actif↔idle
        // On ne touche QUE les champs présents dans l'event (un start ne porte
        // pas needs_attention → ne doit pas l'effacer, et inversement).
        const hasFlag = event.needs_attention !== undefined;
        const nextFlag = event.needs_attention === true;
        const nextReason = event.needs_attention_reason ?? null;
        const nextMode =
          typeof event.content_mode === "string"
            ? asContentMode(event.content_mode)
            : null;
        // Detail / chat-lite : la ConvStatusBar lit content_mode depuis le payload
        // monté → bascule les boutons live.
        if (nextMode !== null) {
          patchConversationCaches(queryClient, event.conversation_id, (old) =>
            old ? { ...old, content_mode: nextMode } : old,
          );
        }
        queryClient.setQueriesData<
          InfiniteData<ApiConversationListResponse, string | null>
        >({ queryKey: ["conversations", "list"] }, (old) => {
          if (!old) return old;
          let touched = false;
          const pages = old.pages.map((page) => {
            let pageTouched = false;
            const conversations = page.conversations.map((conv) => {
              if (conv.id !== event.conversation_id) return conv;
              const flagChanges =
                hasFlag && (conv.needs_attention ?? false) !== nextFlag;
              const modeChanges =
                nextMode !== null && (conv.content_mode ?? "off") !== nextMode;
              if (!flagChanges && !modeChanges) return conv;
              pageTouched = true;
              touched = true;
              return {
                ...conv,
                ...(hasFlag
                  ? { needs_attention: nextFlag, needs_attention_reason: nextReason }
                  : {}),
                ...(nextMode !== null ? { content_mode: nextMode } : {}),
              };
            });
            return pageTouched ? { ...page, conversations } : page;
          });
          if (!touched) return old;
          return { ...old, pages };
        });
        return;
      }

      if (event.type === "signal_flash") {
        // Cyrano a détecté un signal sur le dernier message du fan → patch
        // `live_signal` dans le cache liste → la carte clignote de la couleur
        // correspondante (ou s'éteint si `category: null`, ex. un humain a
        // répondu). Même mécanisme que conversation_state_changed →
        // needs_attention : source unique = le cache, survit au reload.
        const nextSignal = asSignalCategory(event.category ?? null);
        const nextEnergy = nextSignal ? (event.energy ?? null) : null;
        queryClient.setQueriesData<
          InfiniteData<ApiConversationListResponse, string | null>
        >({ queryKey: ["conversations", "list"] }, (old) => {
          if (!old) return old;
          let touched = false;
          const pages = old.pages.map((page) => {
            let pageTouched = false;
            const conversations = page.conversations.map((conv) => {
              if (conv.id !== event.conversation_id) return conv;
              if (
                (conv.live_signal ?? null) === (nextSignal ?? null) &&
                (conv.live_signal_energy ?? null) === (nextEnergy ?? null)
              )
                return conv;
              pageTouched = true;
              touched = true;
              return { ...conv, live_signal: nextSignal, live_signal_energy: nextEnergy };
            });
            return pageTouched ? { ...page, conversations } : page;
          });
          if (!touched) return old;
          return { ...old, pages };
        });
        return;
      }

      if (event.type === "new_message") {
        const message = normalizeMessageEvent(event.conversation_id, event.message);
        if (!message) return;

        // Message IA livré → efface un éventuel compte à rebours Cyrano en
        // cours pour cette conversation (l'attente est terminée).
        if (message.direction === "out") {
          const convId = event.conversation_id;
          setCyranoCountdown((prev) => {
            if (!(convId in prev)) return prev;
            const next = { ...prev };
            delete next[convId];
            return next;
          });
          // Filet anti-carte-fantôme : un message sortant (humain ou Cyrano)
          // éteint aussi une éventuelle carte de décision locale. La source
          // de vérité reste l'event dédié `decision_proposal` (resolved) —
          // ceci ne fait qu'anticiper côté client (cf Annexe A « Supersede »,
          // envoi manuel humain → `superseded_by_human`).
          setDecisionProposal((prev) => {
            if (!(convId in prev)) return prev;
            const next = { ...prev };
            delete next[convId];
            return next;
          });
        }

        patchConversationCaches(queryClient, event.conversation_id, (prev) => {
            if (!prev) return prev;
            // Déjà présent par id (POST onSuccess a déjà réconcilié) → no-op.
            if (prev.messages.some((m) => m.id === message.id)) return prev;
            // Réconciliation avec un placeholder optimiste : un envoi cockpit
            // lent (ex. repli MTProto quand le bcid a lapsé : Business échoue →
            // purge → MTProto, ~2-4s) fait arriver CE message via SSE AVANT que
            // le POST /send ne réponde. Sans ce match, on ajoutait un 2e bubble
            // et le placeholder « Envoi en cours… » restait collé à côté du vrai
            // message. On REMPLACE donc le placeholder sortant non livré au même
            // contenu plutôt que d'empiler un doublon.
            if (message.direction === "out") {
              // Réconciliation par TYPE + caption (pas seulement contenu texte) :
              // couvre aussi un envoi média/voix/PPV optimiste dont la bulle est
              // arrivée via SSE avant le onSuccess (le `message_type` distingue un
              // placeholder photo d'un placeholder texte au même caption null).
              const optIdx = prev.messages.findIndex(
                (m) =>
                  m.id.startsWith("optimistic-") &&
                  m.direction === "out" &&
                  m.delivered === false &&
                  m.message_type === message.message_type &&
                  (m.content ?? "") === (message.content ?? ""),
              );
              if (optIdx !== -1) {
                const messages = prev.messages.slice();
                messages[optIdx] = message;
                return { ...prev, last_message_at: message.created_at, messages };
              }

              // Anti-doublon des réponses IA : le backend publie parfois le MÊME
              // message sortant plusieurs fois (chemins d'envoi + persistence,
              // casse de direction différente). Si une bulle IA au même
              // contenu+type existe déjà, on n'ajoute pas la seconde.
              if (
                message.is_ai_generated &&
                prev.messages.some(
                  (m) =>
                    m.direction === "out" &&
                    m.is_ai_generated &&
                    m.message_type === message.message_type &&
                    (m.content ?? "") === (message.content ?? ""),
                )
              ) {
                return prev;
              }
            }
            return {
              ...prev,
              last_message_at: message.created_at,
              message_count: (prev.message_count ?? prev.messages.length) + 1,
              messages: [...prev.messages, message],
            };
        });

        queryClient.setQueriesData<
          InfiniteData<ApiConversationListResponse, string | null>
        >({ queryKey: ["conversations", "list"] }, (old) => {
          if (!old) return old;
          let touched = false;
          const pages = old.pages.map((page) => {
            let pageTouched = false;
            const conversations = page.conversations.map((conv) => {
              if (conv.id !== event.conversation_id) return conv;
              pageTouched = true;
              touched = true;
              const alreadyApplied = conv.last_message_at === message.created_at;
              // 🌍 L'événement ne porte pas la traduction, la base si : elle
              // est posée dans la transaction du message, donc écrite quand
              // l'événement part. Un fan étranger sans traduction servie ⇒ on
              // relit la liste, sinon son aperçu resterait en VO jusqu'au poll.
              const langueDuFan = conv.language?.slice(0, 2).toLowerCase();
              if (
                !message.traductions &&
                langueDuFan &&
                langueDuFan !== langueDuChatteur(i18n)
              ) {
                rafraichirLaListeDuClient();
              }
              return {
                ...conv,
                last_message: message.content,
                last_message_traductions: message.traductions ?? null,
                last_author: message.direction === "in" ? "fan" as const : "bot" as const,
                last_message_at: message.created_at,
                message_count: alreadyApplied
                  ? conv.message_count
                  : (conv.message_count ?? 0) + 1,
              };
            });
            return pageTouched ? { ...page, stats: page.stats, conversations } : page;
          });
          if (!touched) return old;
          return {
            ...old,
            pages,
          };
        });
        return;
      }

      if (!event.message_id) return;
      patchConversationCaches(queryClient, event.conversation_id, (prev) => {
          if (!prev) return prev;
          return {
            ...prev,
            messages: prev.messages.map((m) => {
              if (m.id !== event.message_id) return m;
              if (event.type === "message_edited") {
                return {
                  ...m,
                  content: event.content ?? m.content,
                  edited_at: event.edited_at ?? new Date().toISOString(),
                };
              }
              if (event.type === "message_deleted") {
                return {
                  ...m,
                  content: null,
                  deleted_at: event.deleted_at ?? new Date().toISOString(),
                };
              }
              if (event.type === "message_reacted") {
                return { ...m, bot_reaction: event.emoji ?? null };
              }
              if (event.type === "ppv_unlocked") {
                // Le fan a paye : bascule la bulle PPV verrouillee -> debloquee
                // en direct (sans refetch). Si le message n'a pas de paid_media
                // (cache pas encore a jour), on laisse tel quel — le prochain
                // fetch portera l'etat unlocked.
                if (!m.paid_media) return m;
                return {
                  ...m,
                  paid_media: {
                    ...m.paid_media,
                    state: "unlocked",
                    unlocked_at:
                      event.unlocked_at ??
                      m.paid_media.unlocked_at ??
                      new Date().toISOString(),
                    price_cents: event.price_cents ?? m.paid_media.price_cents,
                    currency: event.currency ?? m.paid_media.currency,
                  },
                };
              }
              return m;
            }),
          };
      });
    },
    onReconnect: () => {
      // SSE reconnecté (typiquement après un rollout backend) : les events
      // publiés pendant la coupure sont perdus (Redis pub/sub ne rejoue pas).
      // On relit la page 1 de la liste (pas toutes les pages : après un
      // rollout, tous les chatteurs reconnectent ensemble) + le détail/chat-lite
      // ouvert pour rattraper les messages manqués.
      rafraichirLaListe(queryClient);
      void queryClient.invalidateQueries({ queryKey: ["conversations", "detail"] });
      void queryClient.invalidateQueries({ queryKey: ["conversations", "chat-lite"] });
      // Anti « carte fantôme » (décision) : le champ liste `pending_decision`
      // se corrige via la relecture ci-dessus (page 1), mais l'atom éphémère de la
      // carte n'a pas de requête à ré-invalider — une résolution publiée
      // pendant la coupure serait sinon perdue et la carte resterait figée.
      // On élague au minimum les propositions dont l'échéance connue est
      // dépassée ; le reste s'auto-corrige au prochain event ou message.
      setDecisionProposal((prev) => pruneDecisionProposals(prev));
      // Mission « heartbeat présence robuste », extension ADDITIVE (même
      // esprit que `regieJobsInvalidateRef` sur l'event `regie_job` : un job
      // (ré)assigné/résolu pendant la coupure serait sinon manqué jusqu'au
      // prochain event) : refetch immédiat de la file dispatch "mine" + bump
      // du nonce de reconnexion, lu par la présence dispatch
      // pour un heartbeat immédiat sans attendre le tick
      // de 30s.
      void queryClient.invalidateQueries({ queryKey: ["regie", "jobs", "mine"] });
      setSseReconnectNonce((n) => n + 1);
    },
  });
}


/* ====================================================================== */
/* Mark read — état "lu" PARTAGÉ équipe (serveur, pas localStorage)        */
/* ====================================================================== */

/** Patch optimiste : passe `unread_count` à `value` pour les conversations
 *  `ids` dans toutes les pages de la liste en cache (mise à jour instantanée
 *  avant la confirmation serveur). */
function patchListUnread(
  queryClient: QueryClient,
  ids: string[],
  value: number,
): void {
  const idSet = new Set(ids);
  queryClient.setQueriesData<
    InfiniteData<ApiConversationListResponse, string | null>
  >({ queryKey: ["conversations", "list"] }, (old) => {
    if (!old) return old;
    return {
      ...old,
      pages: old.pages.map((page) => ({
        ...page,
        conversations: page.conversations.map((conv) =>
          idSet.has(conv.id) ? { ...conv, unread_count: value } : conv,
        ),
      })),
    };
  });
}

/**
 * Marque une ou plusieurs conversations comme lues pour TOUTE l'équipe :
 * POST /api/conversations/{id}/mark-read (écrit le timestamp de lecture PARTAGÉ
 * `admin_read:{id}` côté backend). Optimiste (unread_count → 0 immédiatement)
 * puis relit la page 1 de la liste (`rafraichirLaListe`) pour resynchroniser le
 * compteur serveur.
 *
 * Accepte un tableau d'IDs → sert l'action groupée « Marquer comme lu » ET le
 * mark-on-leave du FanPanel (tableau d'un seul). Best-effort : un POST en échec
 * n'empêche pas les autres (Promise.allSettled), la relecture réconcilie.
 */
export function useMarkConversationsRead(): (ids: string[]) => Promise<void> {
  const queryClient = useQueryClient();
  return useCallback(
    async (conversationIds: string[]) => {
      if (conversationIds.length === 0) return;
      patchListUnread(queryClient, conversationIds, 0);
      await Promise.allSettled(
        conversationIds.map((id) =>
          cyranoApi.post<void>(
            `/api/conversations/${encodeURIComponent(id)}/mark-read`,
            {},
          ),
        ),
      );
      rafraichirLaListe(queryClient);
    },
    [queryClient],
  );
}

/* ====================================================================== */
/* useConversationsList                                                    */
/* ====================================================================== */

/**
 * Liste des conversations visibles pour la session WorkOS, hydratee depuis
 * `/api/conversations`. Polling 15s pour donner un effet "live" sans le
 * cout d'un WebSocket. Sync le resultat dans `conversationsAtom` pour que
 * le FanPanel puisse retrouver la conversation cliquee.
 *
 * Le creator_name + creator_color sont resolus via `useCreatorsList()`
 * (Convex) — c'est la jointure cote client.
 */
export function useConversationsList(
  filters?: FiltersState,
  search = "",
  now = Date.now(),
  /** 🎯 Le tri demandé au SERVEUR. `"priorite"` = la file du chatteur (le rang
   *  se calcule en SQL pour pouvoir paginer dessus) ; `null` = le défaut
   *  serveur (par date), et le tri se fait côté client comme avant. */
  triServeur: "priorite" | "signaux" | "chrono" | "oldest" | null = null,
): {
  conversations: MockConversation[];
  stats: { conversations: number; messages: number; revenue_cents: number } | null;
  fetchNextPage: () => void;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  isLoading: boolean;
  error: Error | null;
} {
  const { user } = useSession();
  const isAuthenticated = user !== null;
  const creators = useCreatorsList();
  const queryClient = useQueryClient();
  useConversationEvents(isAuthenticated);

  // 🪦 `now` n'entre plus dans la queryKey : la fenêtre d'arrivée (24 h /
  // semaine / mois) est partie avec les filtres du 12-09. Le paramètre reste
  // pour les appelants ; il ne re-clé rien.
  void now;
  const serverFilters = useMemo(() => {
    // Time Waster masqués par défaut côté serveur ; cocher « Afficher les
    // Time Waster » les rappelle (showTimeWasters=true).
    const showTimeWasters = filters?.showTimeWasters ?? false;
    // Se fait côté SERVEUR : une punaise se compte en dizaines sur plusieurs
    // milliers de conversations, donc filtrer la page déjà chargée rendrait
    // une liste vide alors que la réponse existe deux pages plus loin.
    const pinned = filters?.pinned ?? false;
    // 💶 Même raison : les payeurs sont une minorité de la liste.
    const payeurs = filters?.payeurs ?? false;
    // 🐦 Même famille, même raison : ~380 conversations Twitter sur ~6 000.
    //
    // Le repli est `CANAL_FILTRE_DEFAUT` (« tous » depuis le 29-10-2026 : les
    // chatteurs vendent sur X, un fan X doit être vu). Le filtre persiste en
    // localStorage sous une clé versionnée : un défaut qui change = une clé neuve.
    const canalFiltre = filters?.canalFiltre ?? CANAL_FILTRE_DEFAUT;
    return {
      showTimeWasters,
      pinned,
      payeurs,
      canalFiltre,
      q: search.trim(),
    };
  }, [filters?.showTimeWasters, filters?.pinned, filters?.payeurs, filters?.canalFiltre, search]);

  const { data, isLoading, error, fetchNextPage, hasNextPage, isFetchingNextPage } = useInfiniteQuery({
    queryKey: ["conversations", "list", serverFilters, triServeur],
    queryFn: ({ pageParam }): Promise<ApiConversationListResponse> =>
      fetchPageDeConversations(serverFilters, triServeur, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
    enabled: isAuthenticated,
    // PAS de maxPages : il plafonnait a 500 conversations (fenetre glissante)
    // -> sur une grosse org tu ne pouvais pas depasser 500, ET le compteur
    // (porte par la 1re page) "sautait" quand cette page se faisait ejecter au
    // scroll (incident user 2026-06-19). On garde toutes les pages chargees ->
    // toutes les convos accessibles + compteur stable.
    // PAS de refetchInterval / refetchOnWindowFocus : sans plafond un refetch
    // re-tirerait TOUTES les pages chargees (mini-tempete). Le live passe par le
    // stream SSE (useConversationEvents) ; le bouton refresh force un refetch.
    ...RAFRAICHISSEMENT_DE_LA_LISTE,
    // 🎯 CHANGER D'ONGLET NE DOIT PAS VIDER LA LISTE (Kayla, 10-09-2026 :
    // « j'aimerais bien que ce soit bien fluide, j'ai l'impression qu'il y a
    // un peu de latences »). `triServeur` est dans la queryKey — « Ma file »
    // demande `signaux`, « Toutes » demande `chrono` —, donc chaque bascule
    // ouvrait une clé NEUVE, sans cache : la liste se vidait et l'écran
    // attendait la réponse. 📏 Mesuré dans le pod le 10-09 : 0,9 à 2,7 s pour
    // la liste, plus l'aller-retour WAN. On garde la page précédente à
    // l'écran pendant que la nouvelle arrive — l'indicateur de rafraîchissement
    // (`useIsFetching`) dit déjà que ça travaille.
    placeholderData: keepPreviousData,
    // Connexions faibles / drops Neon intermittents : un retry evite qu'un
    // seul echec de page casse toute la liste.
    retry: 2,
  });

  // Au montage, ou quand on revient sur des filtres déjà en cache : la page 1
  // seule, par le déclencheur partagé — au lieu de la relecture de toutes les
  // pages que `refetchOnMount` faisait (cf `RAFRAICHISSEMENT_DE_LA_LISTE`).
  // Sans cache, la requête charge sa première page d'elle-même.
  const cleDeLaListe = JSON.stringify([serverFilters, triServeur]);
  useEffect(() => {
    if (!isAuthenticated) return;
    const enCache = queryClient.getQueryData([
      "conversations",
      "list",
      serverFilters,
      triServeur,
    ]);
    if (enCache !== undefined) rafraichirLaListe(queryClient);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- la clé sérialisée suffit
  }, [isAuthenticated, cleDeLaListe, queryClient]);

  // Jointure par slug : le `creator_id` Cyrano (UUID Postgres) ne matche pas
  // le Convex `_id`. La cle stable partagee entre les deux data planes est
  // le slug (cf creator.name cote Cyrano = creator.slug cote Convex).
  // Lookup case-insensitive + trim pour absorber les divergences de saisie
  // (ex: "Mayumi" vs "mayumi" entre les deux backends).
  const creatorBySlug = useMemo(() => {
    const map = new Map<string, CreatorLookup>();
    for (const c of creators) {
      const key = c.slug.trim().toLowerCase();
      map.set(key, { color: c.color });
    }
    return map;
  }, [creators]);

  // Cache de projection : conserve la MÊME référence d'objet UI pour une
  // conversation dont la ligne API n'a pas changé. React Query préserve la
  // référence des lignes inchangées (structuralSharing au refetch ; le handler
  // SSE ne remplace que la conv touchée) → on ne re-projette que ce qui bouge.
  // Sans ça, `projectToUi` recrée TOUS les objets à chaque event et casse le
  // bailout `React.memo` des cards (toute la liste re-render sur un seul msg).
  const projectionCacheRef = useRef<
    Map<
      string,
      { api: ApiConversationListItem; creator: CreatorLookup | undefined; ui: MockConversation }
    >
  >(new Map());
  const prevProjectedRef = useRef<MockConversation[]>([]);

  const projected = useMemo(() => {
    // Defensive : si le backend renvoie autre chose que la shape attendue
    // (HTML d'erreur d'un ingress, deploy en cours, etc.), no-op au lieu
    // de crash le panel.
    const pages = data?.pages ?? [];
    if (pages.length === 0) {
      projectionCacheRef.current = new Map();
      // Garde une référence de tableau vide stable (ne re-set l'atom qu'une fois).
      if (prevProjectedRef.current.length !== 0) prevProjectedRef.current = [];
      return prevProjectedRef.current;
    }
    const prevCache = projectionCacheRef.current;
    const nextCache = new Map<
      string,
      { api: ApiConversationListItem; creator: CreatorLookup | undefined; ui: MockConversation }
    >();
    const out: MockConversation[] = [];
    for (const page of pages) {
      if (!Array.isArray(page.conversations)) continue;
      for (const c of page.conversations) {
        const slugKey = (c.creator_slug ?? "").trim().toLowerCase();
        const creator = creatorBySlug.get(slugKey);
        const prev = prevCache.get(c.id);
        // Réutilise l'objet UI précédent si la ligne API ET la créatrice résolue
        // sont référentiellement identiques (rien n'a changé pour cette conv).
        const ui =
          prev && prev.api === c && prev.creator === creator
            ? prev.ui
            : projectToUi(c, creator);
        nextCache.set(c.id, { api: c, creator, ui });
        out.push(ui);
      }
    }
    projectionCacheRef.current = nextCache;
    // Stabilise aussi la référence du TABLEAU : si tous les items sont
    // identiques (même longueur + mêmes références), renvoie le tableau
    // précédent → l'effet `setConversations` devient un no-op (pas de re-render
    // des lecteurs de `conversationsAtom` sur un heartbeat sans changement).
    const prevArr = prevProjectedRef.current;
    if (prevArr.length === out.length && prevArr.every((p, i) => p === out[i])) {
      return prevArr;
    }
    prevProjectedRef.current = out;
    return out;
  }, [data, creatorBySlug]);

  // Sync into shared atom (FanPanel reads from atom by conversationId).
  const setConversations = useSetAtom(conversationsAtom);
  useEffect(() => {
    setConversations(projected);
  }, [projected, setConversations]);

  return {
    conversations: projected,
    // `find` (pas pages[0]) : robuste si la 1re page est momentanement en
    // refetch -> le compteur ne flicke pas. (Le backend ne met les stats QUE
    // sur la page sans cursor = la 1re.)
    stats: data?.pages.find((p) => p.stats)?.stats ?? null,
    fetchNextPage: () => {
      void fetchNextPage();
    },
    hasNextPage: Boolean(hasNextPage),
    isFetchingNextPage,
    isLoading,
    error: error as Error | null,
  };
}


/* ====================================================================== */
/* useConversationCardById — hydratation hors page                         */
/* ====================================================================== */

/**
 * UNE conversation, par son id, dans la forme EXACTE de la liste.
 *
 * `GET /api/conversations?ids=…` — la même route que la grille, avec les
 * filtres d'AFFICHAGE neutralisés côté serveur : on redemande une conversation
 * qu'on a déjà à l'écran, pas une page. C'est ce qui permet au panneau fan de
 * montrer une conversation que la page courante ne contient pas.
 *
 * Pourquoi pas `/conversations/{id}` (le détail) : il ne porte ni le slug du
 * créateur, ni le stage, ni les tags, ni les non-lus. Il faudrait les déduire,
 * et une déduction se trompe un jour — c'est justement le genre de raccourci
 * qui a produit le panneau vide qu'on répare.
 *
 * `staleTime: Infinity` : une hydratation est un rattrapage ponctuel. Le live
 * (SSE + refetch de la liste) reste le chemin des mises à jour ; refetcher ici
 * ne ferait qu'ajouter des requêtes pour un fan qu'on regarde déjà.
 */
export function useConversationCardById(
  conversationId: string | undefined,
  enabled: boolean,
) {
  const { user } = useSession();
  const creators = useCreatorsList();
  const creatorBySlug = useMemo(() => {
    const map = new Map<string, CreatorLookup>();
    for (const c of creators) map.set(c.slug.trim().toLowerCase(), { color: c.color });
    return map;
  }, [creators]);

  return useReactQuery({
    queryKey: ["conversations", "by-id", conversationId] as const,
    enabled: Boolean(conversationId) && enabled && user !== null,
    staleTime: Infinity,
    retry: 1,
    queryFn: async (): Promise<MockConversation | null> => {
      const res = await cyranoApi.get<ApiConversationListResponse>(
        "/api/conversations",
        { params: { ids: conversationId, limit: 1 } },
      );
      const row = Array.isArray(res?.conversations)
        ? res.conversations.find((c) => c.id === conversationId)
        : undefined;
      // `null` (et non une erreur) quand elle est introuvable : conversation
      // supprimée, ou hors du scope ACL de la session. Un onglet persisté peut
      // désigner l'une ou l'autre — c'est un état normal, pas une panne.
      if (!row) return null;
      const slugKey = (row.creator_slug ?? "").trim().toLowerCase();
      return projectToUi(row, creatorBySlug.get(slugKey));
    },
  });
}

/* ====================================================================== */
/* useCartesDeLaPile — hydratation en LOT pour la pile du chatteur          */
/* ====================================================================== */

/**
 * Les cartes de PLUSIEURS conversations d'un coup, dans la forme de la liste.
 *
 * La pile d'une reprise de shift porte ce qui a attendu toute la nuit ; le
 * tri de la liste (`sort=priorite`) range le rang « le fan attend » du plus
 * RÉCENT au plus ancien, donc les plus vieux — ceux que la pile met en tête —
 * ne sont pas dans la page chargée. Sans ce rattrapage, l'écran est vide
 * pendant que la pile est pleine.
 *
 * Un seul appel pour tout le lot (`ids=` en accepte 50), et le résultat va
 * dans le fond d'hydratation : la GRILLE continue de montrer exactement ce
 * que les filtres ont demandé (cf. `hydratedConversationsAtom`).
 */
export function useCartesDeLaPile(ids: readonly string[]): MockConversation[] {
  const { user } = useSession();
  const creators = useCreatorsList();
  const creatorBySlug = useMemo(() => {
    const map = new Map<string, CreatorLookup>();
    for (const c of creators) map.set(c.slug.trim().toLowerCase(), { color: c.color });
    return map;
  }, [creators]);

  // La clé est l'ensemble TRIÉ : deux piles qui portent les mêmes fans dans
  // un ordre différent (le rang a bougé) ne relancent pas la requête.
  const cle = useMemo(() => [...ids].sort().join(","), [ids]);

  const { data } = useReactQuery({
    queryKey: ["conversations", "pile-cards", cle] as const,
    enabled: cle.length > 0 && user !== null,
    staleTime: 60_000,
    retry: 1,
    queryFn: async (): Promise<MockConversation[]> => {
      const res = await cyranoApi.get<ApiConversationListResponse>(
        "/api/conversations",
        { params: { ids: cle, limit: 50 } },
      );
      const rows = Array.isArray(res?.conversations) ? res.conversations : [];
      return rows.map((row) => {
        const slugKey = (row.creator_slug ?? "").trim().toLowerCase();
        return projectToUi(row, creatorBySlug.get(slugKey));
      });
    },
  });

  const hydrater = useSetAtom(hydrateConversationAtom);
  useEffect(() => {
    for (const carte of data ?? []) hydrater(carte);
  }, [data, hydrater]);

  return data ?? EMPTY_CARTES;
}

const EMPTY_CARTES: MockConversation[] = [];


/* ====================================================================== */
/* useConversationDetail                                                   */
/* ====================================================================== */

/**
 * Detail d'une conversation : fan profile + 50 derniers messages + mémoire,
 * notes, narrative, profil structuré (payload COMPLET). À réserver aux
 * sous-vues Info/Stats — le chat ouvre via `useConversationChatLite` (léger).
 *
 * `enabled` (défaut true) permet de ne PAS tirer ce payload lourd tant que la
 * sous-vue active n'en a pas besoin (ex: on est sur le chat → seul chat-lite
 * est fetché). Refetch 60s tant que visible (live tail simple) + SSE.
 */
export function useConversationDetail(
  conversationId: string | undefined,
  enabled = true,
): {
  detail: ApiConversationDetail | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const { user } = useSession();
  const isAuthenticated = user !== null;

  const { data, isLoading, isFetching, error, refetch } = useReactQuery({
    queryKey: ["conversations", "detail", conversationId],
    queryFn: async (): Promise<ApiConversationDetail> => {
      return cyranoApi.get<ApiConversationDetail>(
        `/api/conversations/${conversationId}`,
      );
    },
    enabled: enabled && isAuthenticated && Boolean(conversationId),
    // Le live passe par SSE (`useConversationEvents` pousse new_message / edit /
    // delete / react dans CE cache, + `onReconnect` invalide au retour). Le poll
    // n'est qu'un filet anti-trou, et seulement quand l'onglet est visible —
    // coupé en arrière-plan pour ne pas re-tirer le détail complet (≈50 msgs +
    // URLs média) en boucle sur mobile. `refetchOnReconnect` rattrape le réseau.
    staleTime: 1_000,
    refetchInterval: () => (document.hidden ? false : 60_000),
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  });

  // Defensive : valide la shape avant de l'exposer (cf useConversationsList).
  const detail =
    data && Array.isArray((data as ApiConversationDetail).messages)
      ? (data as ApiConversationDetail)
      : undefined;

  return {
    detail,
    isLoading,
    isFetching,
    error: error as Error | null,
    refetch: () => {
      void refetch();
    },
  };
}


/* ====================================================================== */
/* useConversationChatLite — payload léger pour ouvrir le chat              */
/* ====================================================================== */

/**
 * Variante LÉGÈRE de `useConversationDetail` : tape `GET /conversations/{id}/
 * chat-lite` (backend `get_conversation_chat_lite`) qui renvoie la MÊME shape
 * `ApiConversationDetail` mais avec les champs lourds vidés (profile, narrative,
 * memory_facts, memory_commitments, notes). On garde donc `messages` /
 * `message_count` / `last_message_at` / `is_cyrano_paused` / `creator_id` —
 * tout ce dont la `ChatView` a besoin — sans payer le coût des jointures
 * mémoire/notes à l'ouverture (gain réseau + parse sur mobile 4G).
 *
 * Query key DISTINCTE (`["conversations","chat-lite",id]`) du détail complet :
 * ne JAMAIS mélanger les deux dans une même clé (cache incohérent — l'un a la
 * mémoire, l'autre non). Le live (SSE + optimistic sends) patche les DEUX clés
 * via `patchConversationCaches`, donc le thread reste à jour même si la sous-vue
 * Info (détail complet) est aussi montée.
 *
 * `enabled` (défaut true) : ne tire le payload que quand la sous-vue chat est
 * active (cf FanPanel / FanSubViewPanel). `staleTime` allongé (le live passe par
 * SSE) → rouvrir une conv réaffiche le thread stale sans refetch systématique.
 */
export function useConversationChatLite(
  conversationId: string | undefined,
  enabled = true,
): {
  detail: ApiConversationDetail | undefined;
  isLoading: boolean;
  isFetching: boolean;
  error: Error | null;
  refetch: () => void;
} {
  const { user } = useSession();
  const isAuthenticated = user !== null;

  const { data, isLoading, isFetching, error, refetch } = useReactQuery({
    queryKey: ["conversations", "chat-lite", conversationId],
    queryFn: async (): Promise<ApiConversationDetail> => {
      return cyranoApi.get<ApiConversationDetail>(
        `/api/conversations/${conversationId}/chat-lite`,
      );
    },
    enabled: enabled && isAuthenticated && Boolean(conversationId),
    // Live = SSE (`useConversationEvents` patche CE cache aussi via
    // `patchConversationCaches`) + filet poll 60s quand visible seulement.
    // `staleTime` plus long que le détail complet : rouvrir/rebasculer sur le
    // chat ne re-tire pas la fenêtre de 50 messages tant que SSE la tient à
    // jour (économie réseau mobile). `refetchOnReconnect` rattrape les trous.
    staleTime: 30_000,
    refetchInterval: () => (document.hidden ? false : 60_000),
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  });

  // Defensive : valide la shape avant de l'exposer (cf useConversationDetail).
  const detail =
    data && Array.isArray((data as ApiConversationDetail).messages)
      ? (data as ApiConversationDetail)
      : undefined;

  return {
    detail,
    isLoading,
    isFetching,
    error: error as Error | null,
    refetch: () => {
      void refetch();
    },
  };
}

/**
 * Préchauffe le cache chat-lite d'une conversation SANS la monter. Appelé sur
 * l'INTENTION d'ouvrir (pointerdown sur la carte) → le `GET /chat-lite` part
 * AVANT que le tap soit validé et le panneau monté ; les messages sont donc
 * déjà là (ou en vol) quand la ChatView s'affiche → plus de « … » à l'ouverture.
 *
 * MÊME queryKey + queryFn + staleTime que `useConversationChatLite` → le hook
 * récupère la donnée préchargée depuis le cache. `prefetchQuery` respecte
 * `staleTime` (30s) : no-op si la conv a été ouverte récemment, et les requêtes
 * en vol sont dédupliquées par clé. Sûr en 4G : 1 requête, sur intention (pas
 * un préchargement de masse qui saturerait le lien).
 */
export function prefetchConversationChatLite(
  queryClient: QueryClient,
  conversationId: string | undefined,
): void {
  if (!conversationId) return;
  void queryClient.prefetchQuery({
    queryKey: ["conversations", "chat-lite", conversationId],
    queryFn: async (): Promise<ApiConversationDetail> =>
      cyranoApi.get<ApiConversationDetail>(
        `/api/conversations/${conversationId}/chat-lite`,
      ),
    staleTime: 30_000,
  });
}


/* ====================================================================== */
/* useConversationHistory — pagination historique (scroll vers le haut)    */
/* ====================================================================== */

export interface ApiMessagesHistoryResponse {
  /** Page de messages en ordre CHRONOLOGIQUE (ASC), tous plus anciens que
   *  le curseur `before` demande. */
  messages: ApiMessage[];
  /** created_at (ISO) du plus ancien message de la page → curseur `before`
   *  de la page suivante. Null = plus rien de plus ancien. */
  next_cursor: string | null;
}

/**
 * Pagination de l'historique : charge des pages de messages PLUS ANCIENS que
 * ceux deja affiches (endpoint `GET /conversations/{id}/messages?before=`).
 *
 * `useConversationDetail` ne ramene que les ~50 derniers messages. Pour qu'un
 * operateur puisse REPRENDRE une conversation, il faut pouvoir remonter tout
 * le fil — c'est ce hook qui le fournit, page par page (50), a la demande.
 *
 * **Paresseux** (`enabled:false`) : RIEN n'est tire tant que l'operateur ne
 * remonte pas le fil. `fetchOlder` (declenche au scroll pres du haut, cf
 * ChatView) tire la page suivante. Les pages sont fusionnees EN AMONT du
 * detail cote ChatView → le rendu reste fenetre (windowing), seul l'historique
 * a l'ecran est peint. On ne charge donc jamais "tout d'un coup".
 *
 * @param oldestLoadedAt created_at (ISO) du plus ancien message deja charge
 *   (= `detail.messages[0].created_at`). Sert de 1er curseur `before` pour que
 *   la 1re page soit STRICTEMENT plus ancienne (backend : `created_at < before`)
 *   → aucun chevauchement avec le detail.
 */
export function useConversationHistory(
  conversationId: string | undefined,
  oldestLoadedAt: string | undefined,
): {
  /** Messages plus anciens, en ordre CHRONOLOGIQUE (ASC), a prepend au detail. */
  olderMessages: ApiMessage[];
  /** Tire la page de messages plus anciens suivante (no-op si deja en cours). */
  fetchOlder: () => void;
  /** Reste-t-il de l'historique a charger ? (true avant tout fetch s'il existe
   *  un curseur, puis pilote par `next_cursor`). */
  hasOlder: boolean;
  /** Une page d'historique est-elle en cours de chargement ? */
  isFetchingOlder: boolean;
} {
  const { user } = useSession();
  const isAuthenticated = user !== null;

  const { data, fetchNextPage, hasNextPage, isFetchingNextPage } =
    useInfiniteQuery({
      queryKey: ["conversations", "history", conversationId],
      queryFn: async ({
        pageParam,
      }): Promise<ApiMessagesHistoryResponse> => {
        return cyranoApi.get<ApiMessagesHistoryResponse>(
          `/api/conversations/${conversationId}/messages`,
          { params: { limit: 50, before: pageParam ?? undefined } },
        );
      },
      // 1er curseur = plus ancien message du detail. `enabled:false` → ce param
      // n'est lu qu'au 1er `fetchOlder`, moment ou le detail est charge.
      initialPageParam: oldestLoadedAt ?? undefined,
      getNextPageParam: (lastPage) => lastPage.next_cursor ?? undefined,
      // Paresseux : pas de pre-chargement. L'historique se tire au scroll.
      enabled: false,
      // L'historique ancien est immuable → jamais stale, pas de refetch. Le
      // live (messages recents) passe par le detail + SSE, pas par ce hook.
      staleTime: Infinity,
      gcTime: 60_000,
    });

  // Pages tirees recent→ancien ; chaque page est ASC en interne. Pour un fil
  // ASC complet a prepend : aplatir dans l'ordre [pageN ... page1].
  const olderMessages = useMemo(() => {
    const pages = data?.pages ?? [];
    if (pages.length === 0) return [] as ApiMessage[];
    const out: ApiMessage[] = [];
    for (let i = pages.length - 1; i >= 0; i--) {
      out.push(...pages[i].messages);
    }
    return out;
  }, [data]);

  const hasFetched = (data?.pages?.length ?? 0) > 0;
  // Avant tout fetch, `hasNextPage` est false (aucune page) → on autorise la
  // 1re tire des qu'on a un curseur. Ensuite, on suit `next_cursor`.
  const hasOlder = isFetchingNextPage
    ? false
    : hasFetched
      ? hasNextPage
      : Boolean(isAuthenticated && conversationId && oldestLoadedAt);

  return {
    olderMessages,
    fetchOlder: () => {
      void fetchNextPage();
    },
    hasOlder,
    isFetchingOlder: isFetchingNextPage,
  };
}


/* ====================================================================== */
/* useConversationMedia — onglet "Médias" du FanPanel                      */
/* ====================================================================== */

/**
 * Liste paginee des messages porteurs de media pour cette conversation.
 * Utilise par MediaView du FanPanel pour la grille type Telegram/Discord.
 *
 * Refetch 30s : nouveaux medias rares, polling leger suffit.
 */
export function useConversationMedia(conversationId: string | undefined): {
  media: ApiMessage[];
  isLoading: boolean;
  error: Error | null;
} {
  const { user } = useSession();
  const isAuthenticated = user !== null;

  const { data, isLoading, error } = useReactQuery({
    queryKey: ["conversations", "media", conversationId],
    queryFn: async (): Promise<ApiConversationMediaResponse> => {
      return cyranoApi.get<ApiConversationMediaResponse>(
        `/api/conversations/${conversationId}/media`,
        { params: { limit: 60 } },
      );
    },
    enabled: isAuthenticated && Boolean(conversationId),
    // Nouveaux médias rares : filet léger quand visible, rien en arrière-plan.
    staleTime: 15_000,
    refetchInterval: () => (document.hidden ? false : 60_000),
    refetchOnWindowFocus: false,
    refetchOnReconnect: true,
  });

  const media = useMemo(() => {
    if (!data || !Array.isArray((data as ApiConversationMediaResponse).media)) {
      return [];
    }
    return (data as ApiConversationMediaResponse).media;
  }, [data]);

  return {
    media,
    isLoading,
    error: error as Error | null,
  };
}


/* ====================================================================== */
/* useSendMessage / useSendMedia                                           */
/* ====================================================================== */

export interface SendMessageInput {
  text: string;
  /** 📡 Sur quel canal le message part — `telegram` | `twitter`.
   *
   *  Kayla, 02-09-2026 : le chatteur SÉLECTIONNE, il n'y a pas de défaut
   *  implicite. Omis (ou `null`) = le backend prend le canal de la
   *  conversation, ce qui est le comportement d'avant ce chantier et le seul
   *  qui marche contre une image backend en retard. */
  canal?: string | null;
  reply_to_message_id?: string | null;
  /** Entities optionnelles (custom emoji Premium + formatting Telegram).
   *  `document_id` est en string (precision i64). Le bot owner doit etre
   *  Telegram Premium pour envoyer des custom emojis non-free. */
  entities?: SendMessageEntityInput[];
  /** 🌍 Ce que le chatteur avait ÉCRIT, quand `text` en est la traduction.
   *  Le backend le garde en métadonnée et le fil le montre sous la bulle.
   *  Rien de ce champ n'est envoyé au fan. */
  traduction?: ApiTraductionSortante | null;
  /** 🤖 La variante de Francis d'où vient ce texte, quand le chatteur l'a
   *  insérée depuis le panneau. Le backend la garde en métadonnée
   *  (`francis_variante`) ; rien n'en part au fan. Absente = tapé à la main. */
  francis_variante?: { echange_id: string; i: number } | null;
}

export interface SendMessageEntityInput {
  type: string;
  offset: number;
  length: number;
  document_id?: string | null;
  url?: string | null;
  user_id?: string | null;
  language?: string | null;
}

/** Indice d'aperçu optimiste : fourni par l'appelant qui dispose déjà du
 *  VaultItem / fichier, pour que la bulle « Envoi en cours… » montre le média
 *  tout de suite (`message_type` pilote le rendu, `mediaUrl` = preview Vault ou
 *  blob local). Facultatif : sans lui, le placeholder reste textuel. */
export interface OptimisticMediaHint {
  messageType: string;
  mediaUrl: string | null;
}

export interface SendMediaInput {
  content_item_id: string;
  caption?: string | null;
  /** Cents. > 0 = paid (cree Order + payment link). Absent ou 0 = gratuit. */
  price_cents?: number | null;
  /** Le produit du catalogue — exigé par le backend dès que `price_cents > 0`
   *  (08-09-2026) ; ignoré sur un envoi gratuit. */
  offer_key?: string | null;
  currency?: string;
  reply_to_message_id?: string | null;
  /** Aperçu optimiste facultatif (cf OptimisticMediaHint). */
  optimistic?: OptimisticMediaHint;
  /** Confirme un renvoi malgré un 409 `already_sent_to_fan` précédent — sans
   *  ça, le backend rejette un envoi qui duplique un média déjà livré à ce
   *  fan. Poser `true` UNIQUEMENT après confirmation explicite de l'opérateur
   *  (cf dialogue « Renvoyer quand même » dans `Composer`, panels/fan/views.tsx). */
  resend_confirmed?: boolean;
}

interface SendMediaResult {
  message: ApiMessage;
  order_id: string | null;
  payment_url: string | null;
}

export interface RelaisTwitterResult {
  ok: boolean;
  url: string;
  bot_username: string;
  conversation_id: string;
  envoye_chiffre: boolean;
  passe_at: string | null;
}

/**
 * 🌉 Mutation : POST /api/conversations/{id}/relais-twitter.
 *
 * Kayla, 03-09-2026 : « rajoute le bouton pour les rediriger sur Telegram. »
 *
 * ⛔ Le fan reçoit le lien du bot d'accueil et le fil se ferme de notre côté :
 * Cyrano-X n'y répond plus. Ce n'est pas un bouton de lecture, et il ne se
 * rejoue pas — le backend refuse un second passage (`relais_deja_passe`).
 *
 * On invalide le DÉTAIL et la liste : la bulle du lien vient d'être posée dans
 * la conversation, et `relais_twitter.possible` passe à faux.
 */
export function useEnvoyerLeRelaisTwitter(conversationId: string | undefined) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (): Promise<RelaisTwitterResult> => {
      if (!conversationId) throw new Error("conversation inconnue");
      return cyranoApi.post<RelaisTwitterResult>(
        `/api/conversations/${conversationId}/relais-twitter`,
        {},
      );
    },
    onSuccess: () => {
      if (conversationId) {
        // Le détail ET le chat-lite : la bulle du lien vient d'être posée, et
        // `relais_twitter.possible` passe à faux. Les deux caches servent le
        // même écran selon la façon dont il a été ouvert.
        for (const queryKey of conversationDetailKeys(conversationId)) {
          void queryClient.invalidateQueries({ queryKey });
        }
      }
      rafraichirLaListe(queryClient);
    },
  });
}

/**
 * Mutation : POST /api/conversations/{id}/send.
 *
 * Invalide la conversation detail au success pour rafraichir le thread
 * immediatement (le polling 10s rattraperait sinon, mais c'est plus snappy).
 */
export function useSendMessage(conversationIdAffichee: string | undefined): {
  mutateAsync: (input: SendMessageInput) => Promise<ApiMessage>;
  mutateAsyncDans: (
    conversationId: string,
    input: SendMessageInput,
  ) => Promise<ApiMessage>;
  isPending: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();
  const { user } = useSession();
  // Shift gate 2026-06-21 : un envoi compte comme activité (Lot B), fire-and-
  // forget. Le chrono anti-camping, lui, est tenu pour TOUS les envois par
  // `ConversationLockController` (cf core/api/ecritureAuFan.ts).
  const bumpShiftActivity = useShiftActivityHeartbeat();
  return useConversationMutation(conversationIdAffichee, {
    mutationFn: async (input: SendMessageInput, conversationId: string): Promise<ApiMessage> => {
      return cyranoApi.post<ApiMessage>(
        `/api/conversations/${conversationId}/send`,
        {
          text: input.text,
          // ⛔ Absent quand l'appelant n'en a pas : `{canal: null}` et une clé
          // manquante disent la même chose au backend, mais on n'invente pas
          // un canal ici — c'est le composeur qui le tient, jamais ce hook.
          canal: input.canal ?? null,
          reply_to_message_id: input.reply_to_message_id ?? null,
          entities: input.entities ?? [],
          traduction: input.traduction ?? null,
          // ⛔ La clé n'est posée que quand il y a quelque chose : un backend
          // d'avant ce champ ne doit rien voir changer dans le corps.
          ...(input.francis_variante ? { francis_variante: input.francis_variante } : {}),
        },
      );
    },
    // Vrai optimistic update : la bulle apparait DES le clic Send (gris),
    // avant que l'API reponde. onSuccess remplace le placeholder par le
    // message reel renvoye par le backend (qui a le vrai id + delivered).
    onMutate: async (input, conversationId: string) => {
      await cancelConversationCaches(queryClient, conversationId);
      const tempId = `optimistic-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      const now = new Date().toISOString();
      const placeholder: ApiMessage = {
        id: tempId,
        direction: "out",
        message_type: "text",
        content: input.text,
        // Rien n'est parti : aucun accuse de transport. `onSuccess` remplace
        // ce placeholder par le message reel, qui portera le vrai.
        telegram_message_id: null,
        media_file_id: null,
        media_url: null,
        is_ai_generated: false,
        sender_source: "operator",
        created_at: now,
        reply_to_message_id: input.reply_to_message_id ?? null,
        delivered: false,
        payment_button: null,
        traduction: input.traduction ?? null,
        paid_media: null,
        manual_payment: null,
        game_pack: null,
        // Bulle pre-frappee avec l'identite de l'operateur pour eviter le
        // flash "Cyrano avatar" → "Bratz avatar" quand la response du POST
        // arrive ~300ms plus tard. Le backend ecrit les memes valeurs dans
        // Message.extra_metadata cote DB.
        operator_workos_user_id: user?.workosUserId ?? null,
        operator_email: user?.email ?? null,
        edited_at: null,
        deleted_at: null,
        bot_reaction: null,
        poll: null,
        flow_id: null,
        entities: (input.entities ?? []).map((e) => ({
          type: e.type,
          offset: e.offset,
          length: e.length,
          document_id: e.document_id ?? null,
          url: e.url ?? null,
          user_id: e.user_id ?? null,
          language: e.language ?? null,
        })),
        inline_keyboard: null,
      };
      patchConversationCaches(queryClient, conversationId, (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          last_message_at: now,
          message_count: (prev.message_count ?? 0) + 1,
          messages: [...prev.messages, placeholder],
        };
      });
      return { tempId };
    },
    onSuccess: (saved, _input, ctx, conversationId: string) => {
      // Shift gate : bump l'activité (throttle 30s).
      bumpShiftActivity();
      // Remplace le placeholder optimiste par le message reel renvoye par
      // l'API. Le backend fait un send synchrone vers Telegram avant de
      // repondre, donc `saved.delivered === true` des l'arrivee → la bulle
      // sort du gris immediatement, plus de "Envoi en cours…" qui traine.
      const tempId = ctx?.tempId;
      patchConversationCaches(queryClient, conversationId, (prev) => {
        if (!prev) return prev;
        let replaced = false;
        const messages = prev.messages.map((m) => {
          if (tempId && m.id === tempId) {
            replaced = true;
            return saved;
          }
          return m;
        });
        if (!replaced && !messages.some((m) => m.id === saved.id)) {
          messages.push(saved);
        }
        return {
          ...prev,
          last_message_at: saved.created_at,
          messages,
        };
      });
      rafraichirLaListe(queryClient);
    },
    onError: (_err, _input, ctx, conversationId: string) => {
      if (!conversationId || !ctx?.tempId) return;
      // Rollback : retire le placeholder si l'envoi a echoue.
      const tempId = ctx.tempId;
      patchConversationCaches(queryClient, conversationId, (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          messages: prev.messages.filter((m) => m.id !== tempId),
          message_count: Math.max(0, (prev.message_count ?? 1) - 1),
        };
      });
    },
  });
}

/**
 * Mutation : POST /api/conversations/{id}/send-media.
 *
 * Backend gere les 2 flows :
 *  - gratuit (price_cents absent ou 0) → Order(amount=0) + ContentDelivery + send;
 *  - payant (price_cents > 0) → Order PENDING + lien Dropp + message avec bouton.
 *
 * Le caller decide en passant ou non `price_cents`.
 */
export function useSendMedia(conversationIdAffichee: string | undefined): {
  mutateAsync: (input: SendMediaInput) => Promise<SendMediaResult>;
  isPending: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();
  const { user } = useSession();
  return useConversationMutation(conversationIdAffichee, {
    mutationFn: async (input: SendMediaInput, conversationId: string): Promise<SendMediaResult> => {
      return cyranoApi.post<SendMediaResult>(
        `/api/conversations/${conversationId}/send-media`,
        {
          content_item_id: input.content_item_id,
          caption: input.caption ?? null,
          price_cents: input.price_cents ?? null,
          offer_key: input.offer_key ?? null,
          currency: input.currency ?? "USD",
          reply_to_message_id: input.reply_to_message_id ?? null,
          resend_confirmed: input.resend_confirmed ?? false,
        },
      );
    },
    // Bulle optimiste immédiate (aperçu Vault si l'appelant fournit le hint).
    onMutate: async (input, conversationId: string) => {
      await cancelConversationCaches(queryClient, conversationId);
      const placeholder = buildOptimisticOutgoing({
        message_type: input.optimistic?.messageType ?? "photo",
        content: input.caption ?? null,
        media_file_id: input.optimistic ? input.content_item_id : null,
        media_url: input.optimistic?.mediaUrl ?? null,
        reply_to_message_id: input.reply_to_message_id ?? null,
        operator_workos_user_id: user?.workosUserId ?? null,
        operator_email: user?.email ?? null,
      });
      insertOptimisticMessage(queryClient, conversationId, placeholder);
      return { tempId: placeholder.id };
    },
    onSuccess: (result, _input, ctx, conversationId: string) => {
      reconcileOptimisticMessage(
        queryClient,
        conversationId,
        ctx?.tempId,
        result.message,
      );
      queryClient.invalidateQueries({
        queryKey: ["conversations", "media", conversationId],
      });
      rafraichirLaListe(queryClient);
    },
    onError: (_err, _input, ctx, conversationId: string) => {
      rollbackOptimisticMessage(queryClient, conversationId, ctx?.tempId);
    },
  });
}


/* ====================================================================== */
/* useSendMediaGroup — envoi album 2-10 items du Vault                     */
/* ====================================================================== */

export interface SendMediaGroupInput {
  content_item_ids: string[]; // 2-10 UUIDs
  caption?: string | null;
  reply_to_message_id?: string | null;
  /** Aperçu optimiste facultatif (1er item de l'album) — cf OptimisticMediaHint. */
  optimistic?: OptimisticMediaHint;
}

interface SendMediaGroupResult {
  messages: ApiMessage[];
}

/**
 * Mutation : POST /api/conversations/{id}/send-media-group.
 *
 * Envoie un album natif Telegram (2-10 items). Pas de paiement V1 — l'order
 * multi-item payant n'est pas modélisé côté backend. Les messages renvoyés
 * partagent un `extra_metadata.media_group_id` pour le regroupement UI.
 */
export function useSendMediaGroup(conversationIdAffichee: string | undefined): {
  mutateAsync: (input: SendMediaGroupInput) => Promise<SendMediaGroupResult>;
  isPending: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();
  const { user } = useSession();
  return useConversationMutation(conversationIdAffichee, {
    mutationFn: async (input: SendMediaGroupInput, conversationId: string): Promise<SendMediaGroupResult> => {
      return cyranoApi.post<SendMediaGroupResult>(
        `/api/conversations/${conversationId}/send-media-group`,
        {
          content_item_ids: input.content_item_ids,
          caption: input.caption ?? null,
          reply_to_message_id: input.reply_to_message_id ?? null,
        },
      );
    },
    // Un SEUL placeholder pour l'album (1er item) ; remplacé par les N messages
    // serveur au onSuccess. Évite N bulles fantômes pour un envoi groupé.
    onMutate: async (input, conversationId: string) => {
      await cancelConversationCaches(queryClient, conversationId);
      const placeholder = buildOptimisticOutgoing({
        message_type: input.optimistic?.messageType ?? "photo",
        content: input.caption ?? null,
        media_file_id: input.optimistic ? input.content_item_ids[0] : null,
        media_url: input.optimistic?.mediaUrl ?? null,
        reply_to_message_id: input.reply_to_message_id ?? null,
        operator_workos_user_id: user?.workosUserId ?? null,
        operator_email: user?.email ?? null,
      });
      insertOptimisticMessage(queryClient, conversationId, placeholder);
      return { tempId: placeholder.id };
    },
    onSuccess: (result, _input, ctx, conversationId: string) => {
      patchConversationCaches(queryClient, conversationId, (prev) => {
        if (!prev) return prev;
        // Retire le placeholder de l'album puis ajoute les messages serveur.
        const hadPlaceholder = ctx?.tempId
          ? prev.messages.some((m) => m.id === ctx.tempId)
          : false;
        const base = hadPlaceholder
          ? prev.messages.filter((m) => m.id !== ctx!.tempId)
          : prev.messages;
        const known = new Set(base.map((m) => m.id));
        const fresh = result.messages.filter((m) => !known.has(m.id));
        if (fresh.length === 0 && !hadPlaceholder) return prev;
        const last = fresh.length > 0 ? fresh[fresh.length - 1] : null;
        return {
          ...prev,
          last_message_at: last ? last.created_at : prev.last_message_at,
          message_count: Math.max(
            0,
            (prev.message_count ?? prev.messages.length) -
              (hadPlaceholder ? 1 : 0) +
              fresh.length,
          ),
          messages: [...base, ...fresh],
        };
      });
      rafraichirLaListe(queryClient);
    },
    onError: (_err, _input, ctx, conversationId: string) => {
      rollbackOptimisticMessage(queryClient, conversationId, ctx?.tempId);
    },
  });
}


/* ====================================================================== */
/* useEditMessage / useDeleteMessage / useReactMessage                     */
/* ====================================================================== */

export interface EditMessageInput {
  messageId: string;
  text: string;
}

export interface DeleteMessageInput {
  messageId: string;
}

export interface ReactMessageInput {
  messageId: string;
  /** Null ou "" => retire la réaction. */
  emoji: string | null;
  isBig?: boolean;
}

/** Mutation : POST /api/conversations/{id}/messages/{mid}/edit.
 *  Mise à jour optimiste de `content` + `edited_at` ; rollback sur erreur. */
export function useEditMessage(conversationIdAffichee: string | undefined): {
  mutateAsync: (input: EditMessageInput) => Promise<ApiMessage>;
  isPending: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();
  return useConversationMutation(conversationIdAffichee, {
    mutationFn: async (input: EditMessageInput, conversationId: string): Promise<ApiMessage> => {
      return cyranoApi.post<ApiMessage>(
        `/api/conversations/${conversationId}/messages/${input.messageId}/edit`,
        { text: input.text },
      );
    },
    onMutate: async (input, conversationId: string) => {
      await cancelConversationCaches(queryClient, conversationId);
      const now = new Date().toISOString();
      let previousContent: string | null = null;
      patchConversationCaches(queryClient, conversationId, (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          messages: prev.messages.map((m) => {
            if (m.id !== input.messageId) return m;
            previousContent = m.content;
            return { ...m, content: input.text, edited_at: now };
          }),
        };
      });
      return { previousContent };
    },
    onError: (_err, input, ctx, conversationId: string) => {
      if (!conversationId || !ctx) return;
      const previousContent = ctx.previousContent;
      if (previousContent === null) return;
      patchConversationCaches(queryClient, conversationId, (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          messages: prev.messages.map((m) =>
            m.id === input.messageId
              ? { ...m, content: previousContent, edited_at: null }
              : m,
          ),
        };
      });
    },
    onSuccess: (saved, _input, _ctx, conversationId: string) => {
      patchConversationCaches(queryClient, conversationId, (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          messages: prev.messages.map((m) => (m.id === saved.id ? saved : m)),
        };
      });
    },
  });
}

/** Mutation : POST /api/conversations/{id}/messages/{mid}/delete.
 *  Soft-delete backend ; cockpit affiche un placeholder "Message supprimé". */
export function useDeleteMessage(conversationIdAffichee: string | undefined): {
  mutateAsync: (input: DeleteMessageInput) => Promise<void>;
  isPending: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();
  return useConversationMutation(conversationIdAffichee, {
    mutationFn: async (input: DeleteMessageInput, conversationId: string): Promise<void> => {
      await cyranoApi.post(
        `/api/conversations/${conversationId}/messages/${input.messageId}/delete`,
        {},
      );
    },
    onMutate: async (input, conversationId: string) => {
      await cancelConversationCaches(queryClient, conversationId);
      const now = new Date().toISOString();
      let snapshot: ApiMessage | null = null;
      patchConversationCaches(queryClient, conversationId, (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          messages: prev.messages.map((m) => {
            if (m.id !== input.messageId) return m;
            snapshot = m;
            return { ...m, content: "", deleted_at: now };
          }),
        };
      });
      return { snapshot };
    },
    onError: (_err, _input, ctx, conversationId: string) => {
      if (!conversationId || !ctx?.snapshot) return;
      const snap: ApiMessage = ctx.snapshot;
      patchConversationCaches(queryClient, conversationId, (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          messages: prev.messages.map((m) => (m.id === snap.id ? snap : m)),
        };
      });
    },
  });
}

/** Mutation : POST /api/conversations/{id}/messages/{mid}/react.
 *  Pose ou retire une réaction emoji. Limitation Bot API : "as bot". */
export function useReactMessage(conversationIdAffichee: string | undefined): {
  mutateAsync: (input: ReactMessageInput) => Promise<void>;
  isPending: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();
  return useConversationMutation(conversationIdAffichee, {
    mutationFn: async (input: ReactMessageInput, conversationId: string): Promise<void> => {
      await cyranoApi.post(
        `/api/conversations/${conversationId}/messages/${input.messageId}/react`,
        { emoji: input.emoji, is_big: input.isBig ?? false },
      );
    },
    onMutate: async (input, conversationId: string) => {
      await cancelConversationCaches(queryClient, conversationId);
      let previousReaction: string | null = null;
      patchConversationCaches(queryClient, conversationId, (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          messages: prev.messages.map((m) => {
            if (m.id !== input.messageId) return m;
            previousReaction = m.bot_reaction;
            return { ...m, bot_reaction: input.emoji || null };
          }),
        };
      });
      return { previousReaction };
    },
    onError: (_err, input, ctx, conversationId: string) => {
      const previousReaction = ctx?.previousReaction ?? null;
      patchConversationCaches(queryClient, conversationId, (prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          messages: prev.messages.map((m) =>
            m.id === input.messageId
              ? { ...m, bot_reaction: previousReaction }
              : m,
          ),
        };
      });
    },
  });
}


/* ====================================================================== */
/* useSendVoice / sendChatAction — voice notes + indicateurs              */
/* ====================================================================== */

export interface SendVoiceInput {
  /** Blob brut du MediaRecorder (audio/ogg ; codecs=opus idéal, webm OK). */
  audio: Blob;
  durationSeconds: number;
  replyToMessageId?: string | null;
}

/**
 * Mutation : POST multipart /api/conversations/{id}/send-voice.
 *
 * Optimistic : bulle vocale locale immédiate (lecteur audio alimenté par un
 * blob `URL.createObjectURL` du Blob enregistré) → l'opérateur réécoute sans
 * attendre le réseau. `reconcileOptimisticMessage` remplace le placeholder par
 * le message serveur (pas de flicker, match par tempId), le blob est révoqué
 * `onSettled`.
 */
export function useSendVoice(conversationIdAffichee: string | undefined): {
  mutateAsync: (input: SendVoiceInput) => Promise<ApiMessage>;
  isPending: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();
  const { user } = useSession();
  return useConversationMutation(conversationIdAffichee, {
    mutationFn: async (input: SendVoiceInput, conversationId: string): Promise<ApiMessage> => {
      const form = new FormData();
      // Nom de fichier neutre : Telegram ne se sert pas du nom pour le voice
      // note, juste du content-type. On garde .ogg pour signaler le format
      // attendu côté backend, même si MediaRecorder Chrome produit du webm.
      form.append("audio", input.audio, "voice.ogg");
      form.append("duration_seconds", String(Math.max(0, input.durationSeconds)));
      if (input.replyToMessageId) {
        form.append("reply_to_message_id", input.replyToMessageId);
      }
      return cyranoApi.postForm<ApiMessage>(
        `/api/conversations/${conversationId}/send-voice`,
        form,
      );
    },
    onMutate: async (input, conversationId: string) => {
      await cancelConversationCaches(queryClient, conversationId);
      const blobUrl = URL.createObjectURL(input.audio);
      const placeholder = buildOptimisticOutgoing({
        message_type: "voice",
        media_file_id: "optimistic",
        media_url: blobUrl,
        reply_to_message_id: input.replyToMessageId ?? null,
        operator_workos_user_id: user?.workosUserId ?? null,
        operator_email: user?.email ?? null,
      });
      insertOptimisticMessage(queryClient, conversationId, placeholder);
      return { tempId: placeholder.id, blobUrl };
    },
    onSuccess: (saved, _input, ctx, conversationId: string) => {
      reconcileOptimisticMessage(queryClient, conversationId, ctx?.tempId, saved);
      rafraichirLaListe(queryClient);
    },
    onError: (_err, _input, ctx, conversationId: string) => {
      rollbackOptimisticMessage(queryClient, conversationId, ctx?.tempId);
    },
    onSettled: (_data, _err, _input, ctx) => {
      // Libère le blob une fois le placeholder remplacé/retiré (plus référencé).
      if (ctx?.blobUrl) URL.revokeObjectURL(ctx.blobUrl);
    },
  });
}

/* ====================================================================== */
/* useDictee — la voix de l'opérateur devient du TEXTE dans son composeur   */
/* ====================================================================== */

export interface DicteeResult {
  /** Peut être VIDE sans que ce soit une erreur : trois secondes de silence
   *  rendent `""`. C'est à l'appelant de le dire, pas au backend d'inventer
   *  une panne. */
  text: string;
  seconds?: number | null;
  /** Le coût RÉEL rendu par le fournisseur, pas une estimation. Mesuré le
   *  21-08-2026 : 0,0000251925 $ pour 3,359 s — 0,00045 $ la minute. */
  cost_usd?: number | null;
}

/**
 * Mutation : POST multipart /api/conversations/{id}/dictee.
 *
 * ⛔ Rien ne part chez le fan ici. La route transcrit et rend du texte, qui
 * atterrit dans le `<textarea>` : l'opérateur relit, corrige, puis envoie par
 * le `/send` habituel. L'envoi de la VOIX, lui, est `useSendVoice`.
 *
 * Pas d'optimistic : il n'y a rien à afficher tant qu'on ne sait pas ce que la
 * transcription a compris, et inventer une bulle qu'on remplacerait ensuite
 * serait pire que d'attendre une seconde.
 */
export function useDictee(conversationId: string | undefined): {
  mutateAsync: (audio: Blob) => Promise<DicteeResult>;
  isPending: boolean;
  error: Error | null;
} {
  const mutation = useMutation({
    mutationFn: async (audio: Blob): Promise<DicteeResult> => {
      if (!conversationId) throw new Error("conversationId required");
      const form = new FormData();
      // Nom neutre : le backend se sert du content-type, pas du nom.
      form.append("audio", audio, "dictee.ogg");
      return cyranoApi.postForm<DicteeResult>(
        `/api/conversations/${conversationId}/dictee`,
        form,
      );
    },
  });
  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error as Error | null,
  };
}

/* ====================================================================== */
/* useUploadMedia — upload depuis l'ordi (Photo/Vidéo) — pattern Spaces    */
/* ====================================================================== */

export interface UploadMediaInput {
  /** File natif drag-n-drop ou <input type="file">. mime image/* ou video/*. */
  file: File;
  caption?: string | null;
  replyToMessageId?: string | null;
}

/**
 * Mutation : POST multipart /api/conversations/{id}/messages/media.
 *
 * Symetrique de `useSendSpaceMedia` cote Spaces, mais sur l'API Bot Business.
 * Le backend route image/* → sendPhoto, video/* → sendVideo et refuse le
 * reste (415).
 *
 * Optimistic : bulle locale immédiate avec aperçu du fichier choisi (blob
 * `URL.createObjectURL`, type déduit du mime). Remplacée par le message serveur
 * au onSuccess, blob révoqué onSettled.
 */
export function useUploadMedia(conversationIdAffichee: string | undefined): {
  mutateAsync: (input: UploadMediaInput) => Promise<ApiMessage>;
  isPending: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();
  const { user } = useSession();
  return useConversationMutation(conversationIdAffichee, {
    mutationFn: async (input: UploadMediaInput, conversationId: string): Promise<ApiMessage> => {
      const form = new FormData();
      form.append("file", input.file, input.file.name || "upload");
      if (input.caption) form.append("caption", input.caption);
      if (input.replyToMessageId) {
        form.append("reply_to_message_id", input.replyToMessageId);
      }
      return cyranoApi.postForm<ApiMessage>(
        `/api/conversations/${conversationId}/messages/media`,
        form,
      );
    },
    onMutate: async (input, conversationId: string) => {
      await cancelConversationCaches(queryClient, conversationId);
      const blobUrl = URL.createObjectURL(input.file);
      const mime = (input.file.type || "").toLowerCase();
      const messageType = mime.startsWith("video/") ? "video" : "photo";
      const placeholder = buildOptimisticOutgoing({
        message_type: messageType,
        content: input.caption ?? null,
        media_file_id: "optimistic",
        media_url: blobUrl,
        reply_to_message_id: input.replyToMessageId ?? null,
        operator_workos_user_id: user?.workosUserId ?? null,
        operator_email: user?.email ?? null,
      });
      insertOptimisticMessage(queryClient, conversationId, placeholder);
      return { tempId: placeholder.id, blobUrl };
    },
    onSuccess: (saved, _input, ctx, conversationId: string) => {
      reconcileOptimisticMessage(queryClient, conversationId, ctx?.tempId, saved);
      rafraichirLaListe(queryClient);
    },
    onError: (_err, _input, ctx, conversationId: string) => {
      rollbackOptimisticMessage(queryClient, conversationId, ctx?.tempId);
    },
    onSettled: (_data, _err, _input, ctx) => {
      if (ctx?.blobUrl) URL.revokeObjectURL(ctx.blobUrl);
    },
  });
}

export interface SendPollInput {
  question: string;
  options: string[];
  type: "regular" | "quiz";
  isAnonymous: boolean;
  allowsMultipleAnswers?: boolean;
  correctOptionId?: number | null;
  explanation?: string | null;
  replyToMessageId?: string | null;
}

/** Mutation : POST /api/conversations/{id}/send-poll. */
export function useSendPoll(conversationIdAffichee: string | undefined): {
  mutateAsync: (input: SendPollInput) => Promise<ApiMessage>;
  isPending: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();
  return useConversationMutation(conversationIdAffichee, {
    mutationFn: async (input: SendPollInput, conversationId: string): Promise<ApiMessage> => {
      return cyranoApi.post<ApiMessage>(
        `/api/conversations/${conversationId}/send-poll`,
        {
          question: input.question,
          options: input.options,
          type: input.type,
          is_anonymous: input.isAnonymous,
          allows_multiple_answers: input.allowsMultipleAnswers ?? false,
          correct_option_id: input.correctOptionId ?? null,
          explanation: input.explanation ?? null,
          reply_to_message_id: input.replyToMessageId ?? null,
        },
      );
    },
    onSuccess: (saved, _input, _ctx, conversationId: string) => {
      appendMessageToConversationCaches(queryClient, conversationId, saved);
      rafraichirLaListe(queryClient);
    },
  });
}


/* ====================================================================== */
/* Transaction — le lien de paiement Dropp, seul, depuis le composer        */
/* ====================================================================== */

export interface SendTransactionInput {
  /** La clé du produit CHOISI dans le catalogue global — exigée par le
   *  backend depuis le 08-09-2026 (422 `produit_requis` sinon). */
  offerKey: string;
  /** Le nom du produit, pour le `label` de la commande. 🪦 Il a été la
   *  saisie libre (`offer_name`, get-or-create dans `offers`) jusqu'au
   *  08-09-2026 ; ce chemin n'existe plus côté backend. */
  offerName: string;
  /** Prix en centimes (ex: 150 USD = 15000). Doit etre > 0. */
  amountCents: number;
  /** Devise du lien Dropp : "USD" ou "EUR" uniquement (les 2 seules devises
   *  supportees par Dropp). Envoyee telle quelle comme `currency_code`. */
  currency: string;
  /** UUIDs de ContentItems a AUTO-LIVRER au paiement (optionnel, multi). Tous
   *  tiers (gratuit / non enrichi inclus). Pre-cree des ContentDelivery cote
   *  backend → livraison auto au paiement Dropp (chemin "bundle"). */
  deliveryContentItemIds?: string[];
  replyToMessageId?: string | null;
  /** 📡 Le canal CHOISI au composeur — `telegram` ou `twitter`. `null` =
   *  celui de la conversation (le serveur tranche, comme pour le texte). */
  canal?: string | null;
}

export interface SendTransactionResult {
  message: ApiMessage;
  order_id: string;
  payment_url: string | null;
  /** Le moyen effectivement employe, tel que le serveur l'a resolu. */
  payment_method?: string;
  offer_id: string | null;
  /** Renseigné quand la vente est partie sur une offre du catalogue (donc
   *  sans créer de ligne `offers`). Jamais rempli en même temps qu'`offer_id`
   *  — les deux disent par quel chemin le produit a été désigné. */
  catalog_offer_key?: string | null;
}

/** Mutation : POST /api/conversations/{id}/send-payment-link.
 *
 *  Le fan recoit un message texte dont le contenu EST le lien de paiement —
 *  pas de legende, pas de teaser, pas de bouton (Kayla, 13-09-2026). Le
 *  moyen est Dropp, et lui seul (07-09) : il part explicitement quand meme,
 *  parce que le backend accepte encore un moyen direct et qu'un champ
 *  optionnel ne se fait jamais reclamer par le compilateur.
 *
 *  🪦 Jusqu'au 13-09 ce hook dispatchait aussi sur une variante multipart
 *  (`/send-payment-link-with-upload`) quand un teaser venait de l'ordi. La
 *  route est morte avec le teaser. */
export function useSendTransaction(conversationIdAffichee: string | undefined): {
  mutateAsync: (input: SendTransactionInput) => Promise<SendTransactionResult>;
  isPending: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();
  const { user } = useSession();
  return useConversationMutation(conversationIdAffichee, {
    mutationFn: async (input: SendTransactionInput, conversationId: string): Promise<SendTransactionResult> =>
      cyranoApi.post<SendTransactionResult>(
        `/api/conversations/${conversationId}/send-payment-link`,
        {
          offer_key: input.offerKey.trim(),
          amount_cents: input.amountCents,
          currency: input.currency.toUpperCase(),
          payment_method: "dropp",
          label: input.offerName.trim() || null,
          delivery_content_item_ids: input.deliveryContentItemIds ?? [],
          reply_to_message_id: input.replyToMessageId ?? null,
          canal: input.canal ?? null,
        },
      ),
    // Bulle optimiste : un texte vide, le temps que le lien revienne du
    // serveur avec le message reel (le contenu de la bulle, c'est l'URL, et
    // on ne la connait pas avant).
    onMutate: async (input, conversationId: string) => {
      await cancelConversationCaches(queryClient, conversationId);
      const placeholder = buildOptimisticOutgoing({
        message_type: "text",
        content: null,
        media_file_id: null,
        media_url: null,
        reply_to_message_id: input.replyToMessageId ?? null,
        operator_workos_user_id: user?.workosUserId ?? null,
        operator_email: user?.email ?? null,
      });
      insertOptimisticMessage(queryClient, conversationId, placeholder);
      return { tempId: placeholder.id };
    },
    onSuccess: (result, _input, ctx, conversationId: string) => {
      reconcileOptimisticMessage(
        queryClient,
        conversationId,
        ctx?.tempId,
        result.message,
      );
      rafraichirLaListe(queryClient);
      // Page « Commande » du detail createur : la nouvelle order (et
      // l'eventuelle offre creee au passage) doivent apparaitre. Le creator_id
      // vient du detail deja en cache (meme source que le prop creatorId du
      // composer Transaction).
      const detail = readConversationDetailCache(queryClient, conversationId);
      const creatorId = detail?.creator_id;
      if (creatorId) {
        queryClient.invalidateQueries({ queryKey: ["creator-orders", creatorId] });
        queryClient.invalidateQueries({
          queryKey: ["creator-orders-summary", creatorId],
        });
        queryClient.invalidateQueries({ queryKey: ["creator-offers", creatorId] });
      }
    },
    onError: (_err, _input, ctx, conversationId: string) => {
      rollbackOptimisticMessage(queryClient, conversationId, ctx?.tempId);
    },
  });
}

export interface ResendGamePackResult {
  order_id: string;
  /** Le lien est-il reparti. `false` = aucun canal ou mint injoignable ; la
   *  commande reste payee, on peut recliquer. */
  livre: boolean;
}

/** Renvoie au fan le lien de SES parties, sur une vente deja encaissee.
 *
 *  Aucune invalidation de cache : la bulle du renvoi est ecrite par le backend
 *  et arrive par le flux SSE `new_message`, comme n'importe quel envoi. Forcer
 *  un refetch ici ferait clignoter le fil pour rien. */
export function useResendGamePack(conversationIdAffichee: string | undefined): {
  mutateAsync: (input: { orderId: string }) => Promise<ResendGamePackResult>;
  isPending: boolean;
  error: Error | null;
} {
  return useConversationMutation(conversationIdAffichee, {
    mutationFn: async (input: {
      orderId: string;
    }, conversationId: string): Promise<ResendGamePackResult> => {
      return cyranoApi.post<ResendGamePackResult>(
        `/api/conversations/${conversationId}/resend-game-pack`,
        { order_id: input.orderId },
      );
    },
  });
}

/** Ce que le bouton « Lancer le parcours du lot » a fait, et ce qui empeche
 *  encore le parcours de jouer.
 *
 *  ⛔ Les deux derniers champs ne sont PAS decoratifs. Un parcours ouvert dans
 *  une conversation en pause, ou dans un chat que Cyrano 2.0 ne recoit pas, est
 *  un parcours qui ne jouera JAMAIS — et rien n'echouerait pour le dire. */
export interface ParcoursDuLotResult {
  /** Le parcours vient-il d'etre ouvert par ce clic. */
  ouvert: boolean;
  /** Un parcours courait deja : rien n'a ete ouvert, et c'est normal. */
  deja_ouvert: boolean;
  /** Les messages du fan depuis l'ancre, et la longueur du parcours. */
  tours: number;
  sur: number;
  /** Le tour ou part la petite image. */
  tour_du_media: number;
  /** Ce que la pause ETAIT **avant** le clic — pas ce qu'elle est apres.
   *
   *  🩸 Ce n'est plus un empechement, c'est un accuse de REVEIL. Le backend
   *  leve la pause sans condition depuis le 22-08 : quand ce champ vaut
   *  `true`, la conversation etait muette et le clic vient de la rallumer. */
  en_pause: boolean;
  /** ⚠️ Ce chat n'est pas route chez Cyrano 2.0 : le parcours du lot n'existe
   *  QUE la, et l'ancien pipeline n'en a aucune notion. */
  route_vers_cyrano2: boolean;
  /** Les quatre lignes d'ouverture partent-elles derriere cette reponse ?
   *
   *  🩸 **Le champ manquait ici, et ce silence a coute une manche.** Le 22-08 a
   *  18:56, Kayla reclique « mode jeu » sur une conversation ou le fan etait
   *  DEJA au tour 1/15 et avait deja lu l'ouverture. Le backend a correctement
   *  refuse de la renvoyer — et le cockpit n'avait aucun moyen de le dire,
   *  parce qu'il ne lisait pas ce champ. A l'ecran : rien. « j'ai essaye de
   *  relancer sur Max et ca n'a pas marche. »
   *
   *  ⇒ `false` a deux causes, et le cockpit les distingue par `en_pause` :
   *    en pause ⇒ arme, ca partira au bouton « Reprendre » ;
   *    sinon    ⇒ le fan les a DEJA lues, il n'y a rien a renvoyer. */
  ouverture_envoyee: boolean;
}

/** Ce qui EMPECHE le parcours de jouer, ou `null` s'il va jouer.
 *
 *  🩸 Une seule copie pour DEUX boutons — celui de la bulle et celui de la
 *  barre. Ce sont deux surfaces qui posent la meme question ; deux copies de la
 *  reponse, c'est celle qu'on lit qui ment.
 *
 *  🩸 **`en_pause` N'EST PLUS UN EMPECHEMENT — 22-08-2026.** Il l'a ete tant
 *  que le bouton refusait d'envoyer sur une conversation en pause. Le backend
 *  leve desormais la pause sans condition : afficher « Cyrano est en pause ici
 *  — clique Reprendre » apres un clic qui vient de le REVEILLER enverrait
 *  l'operatrice cliquer ailleurs, puis conclure que le bouton ne marche pas.
 *  C'est le meme « il ne se passe rien » que ce chantier ferme, avec un autre
 *  chapeau. Le champ reste, et il se lit maintenant dans `views.tsx` /
 *  `ConvStatusBar` comme un accuse de reveil.
 *
 *  ⛔ Il ne reste donc qu'UN empechement, et il est reel : un chat non route
 *  chez 2.0 ne jouera jamais le parcours, quoi qu'on leve. */
export type EmpechementDuLot = "pas_route" | null;

export function empechementDuLot(r: ParcoursDuLotResult): EmpechementDuLot {
  if (!r.route_vers_cyrano2) return "pas_route";
  return null;
}

/** Met ce fan en « mode Cyrano lot » — 15 tours, image au 5e, CTA a la fin.
 *
 *  Kayla, 22-08-2026 : « j'aimerais bien qu'il y ait aussi un bouton dans le
 *  cockpit pour le lancer manuellement. » Le parcours part tout seul quand le
 *  fan colle son code ; ce bouton couvre les deux cas que l'automatique ne
 *  couvre pas — les fans deja bloques (34 codes colles le 21-08 pendant que le
 *  routage vers 2.0 n'etait pas servi, tous encore valides), et le fan qui
 *  gagne sans coller son code.
 *
 *  Aucune bulle optimiste, aucune invalidation : rien n'est envoye au fan ici.
 *  Le clic OUVRE le parcours ; il joue au prochain message du fan. */
export function useLancerLeParcoursDuLot(
  conversationIdAffichee: string | undefined,
): {
  mutateAsync: () => Promise<ParcoursDuLotResult>;
  isPending: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();
  const brut = useConversationMutation(conversationIdAffichee, {
    mutationFn: async (
      _input: void,
      conversationId: string,
    ): Promise<ParcoursDuLotResult> => {
      return cyranoApi.post<ParcoursDuLotResult>(
        `/api/conversations/${conversationId}/parcours-du-lot`,
        {},
      );
    },
    // 🩸 **L'INVALIDATION, ET SON ABSENCE ETAIT LE BUG.**
    //
    // Ce hook a ete ecrit « aucune invalidation : rien n'est envoye au fan
    // ici ». C'est vrai et c'etait le mauvais raisonnement : ce qui bouge,
    // c'est l'ETAT de la conversation. `parcours_du_lot` vient du detail, la
    // barre le lit, et sans refetch elle continuait d'afficher « Lancer le
    // lot » sur un parcours qui venait de s'ouvrir.
    //
    // Kayla, 22-08, apres avoir clique : « ca n'a pas mis Cyrano en actif sur
    // le workflow. Il ne se passait rien, ou alors ca se trouve qu'il se
    // passait quelque chose, mais c'est silencieux. » Il se passait bien
    // quelque chose — la ligne `jeu_sexto_ouvert` est en base a 14:24:07. Un
    // effet qu'on ne voit pas se lit comme une panne, et il coute plus cher
    // qu'une panne : on reclique.
    //
    // 🪤 Les DEUX cles, pas une : le chat ouvert n'hydrate que `chat-lite`,
    // donc n'invalider que `detail` ne rafraichirait rien a l'ecran. Ce
    // piege a deja mordu ailleurs.
    onSuccess: (_data, _input, _ctx, conversationId: string) => {
      for (const queryKey of conversationDetailKeys(conversationId)) {
        queryClient.invalidateQueries({ queryKey });
      }
    },
  });
  return {
    ...brut,
    mutateAsync: () => brut.mutateAsync(undefined as void),
  };
}

/** Ce que le retour au mode defaut a fait. Miroir de `SortieDuModeJeuResult`. */
export interface SortieDuModeJeuResult {
  /** Un parcours courait-il, et vient-il d'etre referme ? `false` = il n'y
   *  avait rien a fermer (le fan etait deja en mode defaut). */
  referme: boolean;
  tours: number;
  sur: number;
}

/** 🛑 Repasse ce fan en mode defaut : la SUITE du parcours ne joue plus.
 *
 *  🩸 Kayla, 22-08-2026 : « j'ai envie de rebasculer en mode par defaut et
 *  c'est grise. Je ne peux pas lancer le Cyrano par defaut. Ca m'enerve. »
 *
 *  La route existe cote Cyrano depuis #1742 (22-08, 19:50 UTC) — c'est le
 *  cockpit qui ne l'a jamais appelee : l'entree « mode defaut » du picker
 *  restait `disabled` avec l'ancienne infobulle (« il se termine tout seul »).
 *  Mesure le 23-08 a 00:15 UTC : **`jeu_lot_referme` n'a JAMAIS eu une seule
 *  ligne**, pour 122 parcours ouverts dans la journee. La moitie serveur du
 *  correctif etait partie, la moitie qu'on clique n'existait pas.
 *
 *  🪶 Ca ne reprend rien au fan : les tours joues sont joues, le media envoye
 *  est chez lui. Ca ferme la SUITE — la chauffe, l'image, le CTA du jeu.
 *
 *  ⚠️ Ne touche NI a la pause NI au `content_mode` : sortir du mode jeu n'est
 *  pas faire taire Cyrano, c'est le rendre au tour ordinaire.
 *
 *  🪤 L'invalidation est la MEME que celle de `useLancerLeParcoursDuLot`, et
 *  pour la meme raison : ce qui bouge est l'ETAT de la conversation. Les DEUX
 *  cles — le chat ouvert n'hydrate que `chat-lite`. */
/** 🎛️ LE TON de Cyrano sur CETTE conversation — `sexting` ou `relationnel`.
 *
 *  Kayla, 27-08-2026 : « le Cyrano qu'on appelait par défaut, maintenant que
 *  ça devienne le sexting, et que celui-là devienne le relationnel. »
 *
 *  🩸 **Les boutons « Relationnel » / « Sexting » ont DÉJÀ vécu ici, et ils
 *  ont été retirés le 01-08** parce qu'ils écrivaient `content_mode`, que
 *  `preparation_phase` bypasse pour toute créatrice orchestrée — « ils ne
 *  faisaient rien sur 100 % du trafic ». Ce qui change aujourd'hui n'est donc
 *  PAS le bouton : c'est qu'en face, Cyrano 2.0 lit enfin ce champ
 *  (`cyrano/mode.py`, geste `relationnel` rang 21), et que les trois
 *  créatrices sont routées chez lui depuis le 25-08. Le même clic mord.
 *
 *  ⚠️ `PATCH /content-mode` et pas `POST /cyrano/start` : celui-ci ARME et
 *  déclenche une réponse immédiate au fan. Choisir un ton ne doit rien
 *  envoyer — le prochain message du fan suffit. Deux gestes, deux portes.
 *
 *  🪤 Ne touche NI à la pause NI au parcours du lot : trois axes distincts sur
 *  la même barre. Mettre le ton en relationnel ne réveille pas une
 *  conversation coupée, et ne referme pas un lot en cours. */
export function useReglerLeTon(
  conversationIdAffichee: string | undefined,
): {
  mutateAsync: (mode: ContentMode) => Promise<{ mode: ContentMode }>;
  isPending: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();
  const brut = useConversationMutation(conversationIdAffichee, {
    mutationFn: async (
      mode: ContentMode,
      conversationId: string,
    ): Promise<{ mode: ContentMode }> => {
      return cyranoApi.patch<{ mode: ContentMode }>(
        `/api/conversations/${conversationId}/content-mode`,
        { mode },
      );
    },
    // 🪤 La MÊME invalidation que ses deux voisins, et pour la même raison :
    // ce qui bouge est l'ÉTAT de la conversation, et le chat ouvert n'hydrate
    // que `chat-lite`. Les DEUX clés, sinon la barre garde l'ancien mot.
    onSuccess: (_data, _input, _ctx, conversationId: string) => {
      for (const queryKey of conversationDetailKeys(conversationId)) {
        queryClient.invalidateQueries({ queryKey });
      }
    },
  });
  return brut;
}

export function useSortirDuModeJeu(
  conversationIdAffichee: string | undefined,
): {
  mutateAsync: () => Promise<SortieDuModeJeuResult>;
  isPending: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();
  const brut = useConversationMutation(conversationIdAffichee, {
    mutationFn: async (
      _input: void,
      conversationId: string,
    ): Promise<SortieDuModeJeuResult> => {
      return cyranoApi.post<SortieDuModeJeuResult>(
        `/api/conversations/${conversationId}/parcours-du-lot/fermer`,
        {},
      );
    },
    onSuccess: (_data, _input, _ctx, conversationId: string) => {
      for (const queryKey of conversationDetailKeys(conversationId)) {
        queryClient.invalidateQueries({ queryKey });
      }
    },
  });
  return {
    ...brut,
    mutateAsync: () => brut.mutateAsync(undefined as void),
  };
}

/* ══════════════════════════════════════════════════════════════════════
 * 🗂️ LE TRIAGE DU SHIFT — écarter 12 h · repousser
 * ══════════════════════════════════════════════════════════════════════
 *
 * Kayla, 23-08-2026 : « un bouton écarté — quand on clique dessus, il est
 * écarté de ce shift, on ne le voit pas dans les conversations, il est écarté
 * pendant 12 heures. Et un petit affichage : une personne qui a été écartée une
 * fois, deux fois, trois fois. »
 *
 * ⛔ Rien à voir avec le tag « Time Waster », même si elle le décrit par lui.
 * Le tag est DÉFINITIF, cache le fan pour toujours et coupe toute génération ;
 * l'écart s'éteint tout seul à son échéance. Poser le tag pour douze heures
 * ferait payer un ban à un fan qu'on voulait juste revoir ce soir — et une
 * assignation de tag n'a de toute façon pas de date d'expiration.
 */

/** L'état de triage d'une conversation, tel que le rend Cyrano.
 *
 *  🪤 **Tous les champs sont OPTIONNELS**, et ce n'est pas de la prudence
 *  décorative : le cockpit se déploie AVANT Cyrano (Dokploy ~3 min, ArgoCD
 *  bien plus), donc pendant la fenêtre le détail arrive SANS eux. Un accès
 *  direct rendrait la barre blanche — la faute a déjà été commise deux fois le
 *  même jour. */
export interface TriageDeLaConversation {
  /** Échéance de l'écart, ISO. `null`/absent = jamais écartée. Une date
   *  PASSÉE = écart consommé : la conversation est déjà revenue dans la liste,
   *  et le champ reste posé — c'est ce qui laisse dire « écarté 2× » après. */
  ecarte_jusqua?: string | null;
  ecarts_total?: number;
  repousses_total?: number;
  /** Le dernier report. Porte la garde des 24 h côté écran — le bouton se
   *  grise AVANT que le serveur refuse, sinon on propose un geste qui rend
   *  409. */
  repousse_le?: string | null;
}

/** La conversation est-elle DE CÔTÉ en ce moment ?
 *
 *  ⚠️ Ça se calcule à chaque rendu, jamais au montage : l'échéance passe pendant
 *  qu'on regarde l'écran, et un booléen figé au montage garderait la barre en
 *  « écarté » sur une conversation déjà revenue. */
export function estEcartee(
  triage: TriageDeLaConversation | undefined,
  maintenant: number = Date.now(),
): boolean {
  const jusqua = triage?.ecarte_jusqua;
  if (!jusqua) return false;
  const t = Date.parse(jusqua);
  return Number.isFinite(t) && t > maintenant;
}

/** Le report est-il possible aujourd'hui ? Miroir EXACT de la garde serveur
 *  (24 h glissantes) — si les deux divergent, c'est l'écran qui ment, parce
 *  que c'est lui qu'on regarde avant de cliquer. */
export function peutRepousser(
  triage: TriageDeLaConversation | undefined,
  maintenant: number = Date.now(),
): boolean {
  const dernier = triage?.repousse_le;
  if (!dernier) return true;
  const t = Date.parse(dernier);
  if (!Number.isFinite(t)) return true;
  return maintenant - t >= 24 * 60 * 60 * 1000;
}

export interface EcarterResult extends TriageDeLaConversation {
  /** L'état de Cyrano APRÈS le geste. Écarter le fait taire (décision du
   *  23-08) ; annuler ne le relance pas. */
  is_cyrano_paused: boolean;
}

/** 🗂️ Met la conversation de côté pour 12 h — et fait taire Cyrano.
 *
 *  Kayla, 23-08 : « non, il se tait aussi. »
 *
 *  ⛔ La pause posée est DÉLIBÉRÉE et ne se lève PAS à l'expiration : à 12 h la
 *  conversation revient dans la liste, Cyrano attend qu'un humain clique sur
 *  Start. C'est la règle du 19-08 — et `annuler` ne le relance pas non plus,
 *  pour la même raison.
 *
 *  🪤 L'invalidation porte la LISTE en plus du détail, et c'est elle qui compte
 *  ici : ce que le geste change d'abord, c'est la VISIBILITÉ de la carte. Sans
 *  elle, on écarte une conversation et elle reste sous les yeux jusqu'au poll
 *  suivant — exactement le « il ne s'est rien passé » qui fait recliquer. */
export function useEcarterLaConversation(
  conversationIdAffichee: string | undefined,
): {
  mutateAsync: (input?: { annuler?: boolean }) => Promise<EcarterResult>;
  isPending: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();
  const brut = useConversationMutation(conversationIdAffichee, {
    mutationFn: async (
      input: { annuler?: boolean } | undefined,
      conversationId: string,
    ): Promise<EcarterResult> => {
      return cyranoApi.post<EcarterResult>(
        `/api/conversations/${conversationId}/ecarter`,
        { annuler: input?.annuler ?? false },
      );
    },
    onSuccess: (_data, _input, _ctx, conversationId: string) => {
      for (const queryKey of conversationDetailKeys(conversationId)) {
        queryClient.invalidateQueries({ queryKey });
      }
      rafraichirLaListe(queryClient);
    },
  });
  return {
    ...brut,
    mutateAsync: (input?: { annuler?: boolean }) => brut.mutateAsync(input),
  };
}

export interface RepousserResult extends TriageDeLaConversation {
  /** `false` = la note opérateur était pleine, la ligne n'a PAS été écrite.
   *  Le report est compté quand même. L'écran le dit — un bouton qui prétend
   *  avoir noté sans avoir noté est pire qu'un bouton qui échoue. */
  note_ajoutee: boolean;
}

/** 🕐 Acte que le fan a dit « je paie plus tard », avec son motif.
 *
 *  Kayla : « c'est surtout pour ceux qui disent "je vais payer ce soir, je vais
 *  payer plus tard" […] ça nous demande de mettre la raison, très très simple,
 *  il faut que ce soit très rapide, et ensuite ça va l'ajouter dans les notes
 *  opérateur. »
 *
 *  ⛔ Ça ne touche NI Cyrano NI l'affichage : un fan qui promet de payer ce soir
 *  est exactement celui à qui on veut continuer de parler. Pas d'invalidation
 *  de la liste, donc — la carte ne bouge pas, et le détail suffit (il porte le
 *  compteur ET la note qu'on vient d'allonger).
 *
 *  409 `repousse_deja_aujourdhui` = un report a déjà été acté dans les 24 h.
 *  L'écran grise le bouton avant (cf `peutRepousser`) ; ce cas-là n'arrive que
 *  si deux chatteurs cliquent en même temps. */
export function useRepousserLaConversation(
  conversationIdAffichee: string | undefined,
): {
  mutateAsync: (input: { raison: string }) => Promise<RepousserResult>;
  isPending: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();
  return useConversationMutation(conversationIdAffichee, {
    mutationFn: async (
      input: { raison: string },
      conversationId: string,
    ): Promise<RepousserResult> => {
      return cyranoApi.post<RepousserResult>(
        `/api/conversations/${conversationId}/repousser`,
        { raison: input.raison },
      );
    },
    onSuccess: (_data, _input, _ctx, conversationId: string) => {
      for (const queryKey of conversationDetailKeys(conversationId)) {
        queryClient.invalidateQueries({ queryKey });
      }
    },
  });
}


/* ====================================================================== */
/* Game pack — pack de N parties Trista Slot vendu via Dropp                */
/* ====================================================================== */

export interface ApiTristaScenario {
  id: string;
  code: string;
  amount: number;
  /** Le prix réel du lot, en euros — la base du prix d'un match de
   *  Puissance 4 (`prixDuMatch`). `null`/absent = inconnu. */
  prix_eur?: number | null;
}

/** Query : GET /api/games/trista/scenarios — liste des lots Trista.
 *  Proxy Cyrano qui interroge Convex (`payments:listScenarios`). Cache 5 min :
 *  le catalogue change rarement. `enabled` permet de ne fetch qu'a l'ouverture
 *  de la modal Game. */
export function useTristaScenarios(enabled: boolean): {
  scenarios: ApiTristaScenario[];
  isLoading: boolean;
  error: Error | null;
} {
  const query = useReactQuery<ApiTristaScenario[]>({
    queryKey: ["games", "trista", "scenarios"],
    queryFn: () => cyranoApi.get<ApiTristaScenario[]>("/api/games/trista/scenarios"),
    enabled,
    staleTime: 5 * 60 * 1000,
  });
  return {
    scenarios: query.data ?? [],
    isLoading: query.isLoading,
    error: (query.error as Error | null) ?? null,
  };
}

/** Query : GET /api/games/creator/{creatorId}/scenarios — les lots du jeu de
 *  CETTE creatrice.
 *
 *  🩸 **Pourquoi pas `useTristaScenarios`.** Le composeur du fan lisait le
 *  catalogue Trista quelle que soit la creatrice de la conversation. Une
 *  operatrice ouvrant un pack chez Lilou choisissait donc des lots de Trista,
 *  dont les ids n'appartiennent pas au jeu de Lilou — Convex les rejette au
 *  mint, apres que le fan a paye. Le backend resout le jeu par
 *  `creator.name` ; le picker doit resoudre le MEME.
 *
 *  503 = pas de jeu configure pour cette creatrice : le composeur le dit et ne
 *  propose rien, plutot que de proposer les lots de quelqu'un d'autre. */
export function useCreatorGameScenarios(
  creatorId: string | undefined,
  enabled: boolean,
): {
  scenarios: ApiTristaScenario[];
  isLoading: boolean;
  error: Error | null;
} {
  const query = useReactQuery<ApiTristaScenario[]>({
    queryKey: ["games", "creator", creatorId, "scenarios"],
    queryFn: () =>
      cyranoApi.get<ApiTristaScenario[]>(
        `/api/games/creator/${creatorId}/scenarios`,
      ),
    enabled: enabled && Boolean(creatorId),
    staleTime: 5 * 60 * 1000,
  });
  return {
    scenarios: query.data ?? [],
    isLoading: query.isLoading,
    error: (query.error as Error | null) ?? null,
  };
}

/** ⏸️ Le jeu de CETTE créatrice est-il en pause — campagne finie, ou coupé ?
 *
 *  Kayla, 25-09-2026 : on ne propose plus de partie. Même règle que le jeu
 *  (`client/lib/campagne.ts`) et que Cyrano (`game_hub_client.jeu_en_pause`).
 *  Ne sait pas (404, réseau, pas encore chargé) ⇒ `false` : l'écran ne dit
 *  rien plutôt qu'une pause inventée. */
export function useJeuEnPause(creatorId: string | undefined, enabled: boolean): boolean {
  const query = useReactQuery<{ enabled?: boolean; campaignEndsAt?: string | null }>({
    queryKey: ["game-config", creatorId],
    queryFn: () => cyranoApi.get(`/api/creators/${creatorId}/game-config`),
    enabled: enabled && Boolean(creatorId),
    staleTime: 30_000,
    retry: false,
  });
  const cfg = query.data;
  if (!cfg) return false;
  if (!cfg.enabled) return true;
  const fin = cfg.campaignEndsAt ? Date.parse(cfg.campaignEndsAt) : NaN;
  return Number.isFinite(fin) && Date.now() >= fin;
}

/** 🏆 Le Puissance 4 est-il ALLUMÉ sur le jeu de cette créatrice ? C'est ce
 *  qui ouvre la vente d'un MATCH (Kayla, 26-10-2026). Même lecture et même
 *  cache que `useJeuEnPause`. Ne sait pas ⇒ `false` : pas de match proposé
 *  plutôt qu'une vente refusée (409). */
export function usePuissance4Actif(creatorId: string | undefined, enabled: boolean): boolean {
  const query = useReactQuery<{ puissance4?: boolean }>({
    queryKey: ["game-config", creatorId],
    queryFn: () => cyranoApi.get(`/api/creators/${creatorId}/game-config`),
    enabled: enabled && Boolean(creatorId),
    staleTime: 30_000,
    retry: false,
  });
  return query.data?.puissance4 === true;
}

/** 🎮 Ce que l'écran « Les jeux » dit de chaque jeu de cette créatrice. Même
 *  lecture et même cache que `useJeuEnPause` ; `charge` à faux tant qu'elle
 *  n'est pas revenue — l'écran ne dit alors ni « vendable » ni « pas ici ».
 *  `jeu` : « absent » (pas de jeu), « pause » (coupé ou campagne finie),
 *  « ouvert ». Une erreur se lit comme une absence : pas de vente proposée
 *  plutôt qu'une vente refusée. */
export function useEtatDesJeux(
  creatorId: string | undefined,
  enabled: boolean,
): { charge: boolean; jeu: "absent" | "pause" | "ouvert"; puissance4: boolean; slug: string | null } {
  const query = useReactQuery<{
    slug?: string;
    configured?: boolean;
    enabled?: boolean;
    campaignEndsAt?: string | null;
    puissance4?: boolean;
  }>({
    queryKey: ["game-config", creatorId],
    queryFn: () => cyranoApi.get(`/api/creators/${creatorId}/game-config`),
    enabled: enabled && Boolean(creatorId),
    staleTime: 30_000,
    retry: false,
  });
  const cfg = query.data;
  if (!cfg) return { charge: !query.isLoading, jeu: "absent", puissance4: false, slug: null };
  const fin = cfg.campaignEndsAt ? Date.parse(cfg.campaignEndsAt) : NaN;
  const jeu =
    cfg.configured === false
      ? "absent"
      : !cfg.enabled || (Number.isFinite(fin) && Date.now() >= fin)
        ? "pause"
        : "ouvert";
  return { charge: true, jeu, puissance4: cfg.puissance4 === true, slug: cfg.slug || null };
}

/** Un match de Puissance 4 : UN lot, `parties` manches (impair), un niveau
 *  sous son nom Convex — "douce" (Facile) · "sorciere" (Medium) ·
 *  "demoniaque" (Hard). */
export interface MatchDuPack {
  parties: number;
  niveau: "douce" | "sorciere" | "demoniaque";
  /** ✍️ Un lot ÉCRIT par le chatteur, à la place d'un lot du catalogue
   *  (`scenarioIds` vide). Il se livre à la main. */
  lot_libre?: string;
}

export interface SendGamePackInput {
  /** Ordered list of Trista scenario IDs — un id par partie (doublons OK).
   *  Long. 1..10 (cap Trista createMultiGameOffer + Telegram album style). */
  scenarioIds: string[];
  /** Le produit du catalogue sous lequel le pack se vend — exigé. */
  offerKey: string;
  amountCents: number;
  currency: string;
  message?: string | null;
  replyToMessageId?: string | null;
  /** Par ou le fan paie. Le composeur n'envoie plus que "dropp" (12-10-2026) :
   *  c'est le seul moyen dont le paiement se sait tout seul. */
  paymentMethod?: string;
  /** Un match de Puissance 4 sur l'unique lot de `scenarioIds`. */
  match?: MatchDuPack | null;
}

export interface SendGamePackResult {
  message: ApiMessage;
  order_id: string;
  /** `null` sur le virement : un IBAN n'est pas une URL, il part dans le
   *  texte du message et il n'y a pas de bouton. */
  payment_url: string | null;
  payment_method: string;
}

/** Mutation : POST /api/conversations/{id}/send-game-pack. Crée un Order
 *  + sa destination de paiement, et envoie le message au fan.
 *
 *  Sur `dropp`, le webhook mint le shortcode Trista et envoie le follow-up
 *  t.me/<bot>?start=<shortcode> tout seul. */
export function useSendGamePack(conversationIdAffichee: string | undefined): {
  mutateAsync: (input: SendGamePackInput) => Promise<SendGamePackResult>;
  isPending: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();
  return useConversationMutation(conversationIdAffichee, {
    mutationFn: async (input: SendGamePackInput, conversationId: string): Promise<SendGamePackResult> => {
      return cyranoApi.post<SendGamePackResult>(
        `/api/conversations/${conversationId}/send-game-pack`,
        {
          game: "trista",
          scenario_ids: input.scenarioIds,
          offer_key: input.offerKey,
          amount_cents: input.amountCents,
          currency: input.currency.toUpperCase(),
          message: input.message?.trim() || null,
          reply_to_message_id: input.replyToMessageId ?? null,
          payment_method: input.paymentMethod ?? "dropp",
          ...(input.match ? { match: input.match } : {}),
        },
      );
    },
    onSuccess: (result, _input, _ctx, conversationId: string) => {
      const saved = result.message;
      appendMessageToConversationCaches(queryClient, conversationId, saved);
      rafraichirLaListe(queryClient);
      // Les game packs apparaissent dans le tableau de commandes du creator.
      const detail = readConversationDetailCache(queryClient, conversationId);
      const creatorId = detail?.creator_id;
      if (creatorId) {
        queryClient.invalidateQueries({ queryKey: ["creator-orders", creatorId] });
        queryClient.invalidateQueries({
          queryKey: ["creator-orders-summary", creatorId],
        });
      }
    },
  });
}


/* ====================================================================== */
/* Phase 3 — Sticker + GIF                                                  */
/* ====================================================================== */

export interface SendStickerInput {
  /** Sticker file (.webp/.tgs/.webm). Limite Telegram 512KB / 256KB. */
  file: File;
  replyToMessageId?: string | null;
}

/** Mutation : POST multipart /api/conversations/{id}/send-sticker. */
export function useSendSticker(conversationIdAffichee: string | undefined): {
  mutateAsync: (input: SendStickerInput) => Promise<ApiMessage>;
  isPending: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();
  return useConversationMutation(conversationIdAffichee, {
    mutationFn: async (input: SendStickerInput, conversationId: string): Promise<ApiMessage> => {
      const form = new FormData();
      form.append("sticker", input.file, input.file.name || "sticker.webp");
      if (input.replyToMessageId) {
        form.append("reply_to_message_id", input.replyToMessageId);
      }
      return cyranoApi.postForm<ApiMessage>(
        `/api/conversations/${conversationId}/send-sticker`,
        form,
      );
    },
    onSuccess: (saved, _input, _ctx, conversationId: string) => {
      appendMessageToConversationCaches(queryClient, conversationId, saved);
      rafraichirLaListe(queryClient);
    },
  });
}

export interface SendGifInput {
  /** URL HTTPS du GIF (Tenor, Giphy, CDN externe). */
  url: string;
  caption?: string | null;
  replyToMessageId?: string | null;
}

/** Mutation : POST JSON /api/conversations/{id}/send-gif. */
export function useSendGif(conversationIdAffichee: string | undefined): {
  mutateAsync: (input: SendGifInput) => Promise<ApiMessage>;
  isPending: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();
  return useConversationMutation(conversationIdAffichee, {
    mutationFn: async (input: SendGifInput, conversationId: string): Promise<ApiMessage> => {
      return cyranoApi.post<ApiMessage>(
        `/api/conversations/${conversationId}/send-gif`,
        {
          url: input.url,
          caption: input.caption ?? null,
          reply_to_message_id: input.replyToMessageId ?? null,
        },
      );
    },
    onSuccess: (saved, _input, _ctx, conversationId: string) => {
      appendMessageToConversationCaches(queryClient, conversationId, saved);
      rafraichirLaListe(queryClient);
    },
  });
}

/* Scheduled messages (MTProto) retirés au cockpit MTProto removal 2026-06-21.
   Le backend ne supporte plus `schedule_message` Telethon ; useScheduleText /
   useScheduledMessages / useCancelScheduledMessage et leurs types sont
   supprimés. */


/** Type d'action Telegram que le cockpit peut envoyer (cf backend allowlist). */
export type ChatActionKind =
  | "typing"
  | "upload_photo"
  | "record_video"
  | "upload_video"
  | "record_voice"
  | "upload_voice"
  | "upload_document"
  | "choose_sticker";

/**
 * Ping fire-and-forget : POST /api/conversations/{id}/chat-action.
 * Pas un hook (pas de state à observer) — juste un util appelé toutes les
 * 4-5s pendant que l'opérateur tape ou enregistre.
 */
export async function sendChatAction(
  conversationId: string | undefined,
  action: ChatActionKind,
): Promise<void> {
  if (!conversationId) return;
  try {
    await cyranoApi.post(
      `/api/conversations/${conversationId}/chat-action`,
      { action },
    );
  } catch (e) {
    if (import.meta.env.DEV) {
      // eslint-disable-next-line no-console
      console.debug("chat-action swallowed:", e);
    }
  }
}


/* ====================================================================== */
/* Mark-as-read Telegram — reçu « vu » côté fan via le bot Business         */
/* ====================================================================== */

/**
 * Pose la vraie coche « vu » au fan via le bot Business (Bot API
 * `readBusinessMessage`, plus aucun MTProto depuis phase C). Backend :
 * POST /api/conversations/{id}/mark-seen-telegram → `mark_conversation_read_strict`.
 *
 * DISTINCT de `useMarkConversationsRead` (POST /mark-read) qui n'écrit que
 * l'état d'équipe `admin_read` (grisage cockpit, zéro Telegram, safe en masse).
 * Ici on touche Telegram → à n'appeler que sur action VOLONTAIRE et UNITAIRE
 * de l'opérateur (envoi d'un message, clic « Marquer comme lue »), jamais à la
 * simple ouverture d'une conv (mode fantôme) ni en action groupée (anti-ban).
 *
 * Fail-open : bcid mort / aucun message entrant / réseau → best-effort, le
 * backend renvoie 204 et on avale toute erreur. Jamais bloquant pour l'UI.
 */
export async function markReadOnTelegram(
  conversationId: string | undefined,
): Promise<void> {
  if (!conversationId) return;
  try {
    await cyranoApi.post<void>(
      `/api/conversations/${encodeURIComponent(conversationId)}/mark-seen-telegram`,
      {},
    );
  } catch (e) {
    // eslint-disable-next-line no-console
    console.debug("mark-seen-telegram swallowed:", e);
  }
}


/* ====================================================================== */
/* Brouillon Cyrano en attente — relecture opérateur (send-now/edit/cancel) */
/* ====================================================================== */

/** GET du brouillon retenu (hydratation quand l'event SSE initial a été manqué). */
/** 🏦 La suggestion de la BANQUE pour ce fil (Kayla, 12-09-2026 : « quand les
 *  chatteurs écrivent, une suggestion de réponse automatique à chaque fois »).
 *  La même brique que le tour de Cyrano 2.0, rejouée sur le dernier message
 *  du fan : des bulles validées par Kayla, ou la raison pour laquelle rien ne
 *  tient. Rien n'est envoyé — le composeur pré-remplit, le chatteur valide. */
export interface BanqueSuggestion {
  bulles: string[];
  situation: string | null;
  titre: string | null;
  cle: string | null;
  /** `brute` (le GET, avant toute retouche) · `aucune` (mot pour mot) ·
   *  `retouchee` · `refusee` (l'original) · `cases` (une [case] est restée
   *  entre crochets après le remplissage : le chatteur la remplit). */
  retouche: string | null;
  /** Les [crochets] encore dans `bulles`. Non vide ⇒ « Insérer » passe par
   *  `remplirBanqueSuggestion` avant de poser le texte dans le composeur. */
  cases: string[];
  raison: string | null;
  /** Le message du fan sur lequel la suggestion a été calculée. */
  message: string | null;
  /** 🔊 Les vocaux de la créatrice qui DISENT la bulle (Kayla, 16-09 : « on a
   *  les suggestions de messages, mais on a aussi des suggestions de vocaux »),
   *  du plus proche au plus loin, trois au plus. Ancrés sur la bulle, jamais
   *  sur le message du fan. Optionnel : le champ n'existe côté API qu'après le
   *  déploiement Cyrano — le cockpit se déploie AVANT, donc absent = aucun. */
  vocaux?: VocalSuggere[];
  /** 🏅 La suggestion vient d'une famille VARIÉE de la banque (`yuki`, Kayla
   *  18-10-2026 : « des variantes adaptées à la discussion ») : « Insérer »
   *  passe par `remplirBanqueSuggestion` même sans [case], et ce qui revient
   *  est une variante écrite pour ce fan (`retouche=variante`) ou l'original
   *  (`refusee`). Optionnel : absent côté API = comme avant. */
  variante?: boolean;
}

/** Un vocal du Vault qui dit à peu près la bulle suggérée. Le strict
 *  nécessaire pour l'écouter (`buildPreviewUrl(id, added_at)`) et l'envoyer
 *  (`send-media`, gratuit, sans légende) — pas un `VaultItem` entier. */
export interface VocalSuggere {
  id: string;
  /** Ce qu'il dit — la transcription, coupée pour tenir sur une puce. */
  texte: string;
  duree_s: number | null;
  /** ISO — la version du média pour l'URL d'écoute. */
  added_at: string;
  /** 🌍 La langue dans laquelle il parle (code ISO), absente sur un backend
   *  d'avant ou un gémissement. */
  langue?: string | null;
}

export async function getBanqueSuggestion(conversationId: string): Promise<BanqueSuggestion> {
  return cyranoApi.get(`/api/conversations/${encodeURIComponent(conversationId)}/banque`);
}

/** 🎙️ « Dis-le en vocal » — pas la bulle de la banque, le texte que le
 *  chatteur est en train de TAPER (17-09-2026, Kayla : « on recommande trop
 *  peu de messages vocaux [aux chatteurs]. Améliore la recommandation […]
 *  pas en envoyant de la merde »). Mesuré : Trista a 1 817 vocaux transcrits
 *  dans son Vault et les chatteurs n'en envoient que ~113/mois contre 34 000
 *  bulles texte — la seule suggestion existante (`suggestion.vocaux`
 *  ci-dessus) n'apparaît que quand la banque a une bulle pour le DERNIER
 *  message du FAN, donc rarement. Ici la source est le composeur : ça vaut à
 *  chaque frappe, banque ou pas.
 *
 *  Même contrat optionnel que `BanqueSuggestion.vocaux` : la route peut ne
 *  pas encore exister côté Cyrano (déployé par une autre session, en
 *  parallèle) — un 4xx/5xx se lit comme une liste vide, jamais une panne du
 *  composeur. */
export async function getVocauxQuiDisent(
  conversationId: string,
  texte: string,
): Promise<{ texte: string; vocaux: VocalSuggere[] }> {
  return cyranoApi.get(
    `/api/conversations/${encodeURIComponent(conversationId)}/vocaux-qui-disent` +
      `?texte=${encodeURIComponent(texte)}`,
  );
}

/** À l'insertion : les [cases] de la suggestion remplies depuis la
 *  conversation (prénom, délai, ce qu'il avait dit). Ce qui ne se remplit pas
 *  reste entre crochets (`retouche === "cases"`). Kayla, 13-09 : « ça sert à
 *  rien de la pré-remplir […] une fois qu'ils cliquent sur Insérer, ça charge
 *  deux secondes, le temps de la remplir, et ensuite ça le met en bas ». */
export async function remplirBanqueSuggestion(
  conversationId: string,
  situation: string,
  cle: string,
): Promise<BanqueSuggestion> {
  return cyranoApi.post(
    `/api/conversations/${encodeURIComponent(conversationId)}/banque/remplir`,
    { situation, cle },
  );
}

export async function getCyranoDraft(
  conversationId: string,
): Promise<{ pending: boolean; text: string | null; deliver_at: string | null }> {
  return cyranoApi.get(
    `/api/conversations/${encodeURIComponent(conversationId)}/cyrano-draft`,
  );
}

/** Livre le brouillon immédiatement (texte édité optionnel). */
export async function sendCyranoDraftNow(
  conversationId: string,
  text?: string,
): Promise<void> {
  await cyranoApi.post(
    `/api/conversations/${encodeURIComponent(conversationId)}/cyrano-draft/send-now`,
    { text: text ?? null },
  );
}

/** Remplace le texte du brouillon ; le compte à rebours continue. */
export async function editCyranoDraft(
  conversationId: string,
  text: string,
): Promise<void> {
  await cyranoApi.post(
    `/api/conversations/${encodeURIComponent(conversationId)}/cyrano-draft/edit`,
    { text },
  );
}

/** Annule le brouillon : rien n'est envoyé. */
export async function cancelCyranoDraft(conversationId: string): Promise<void> {
  await cyranoApi.post(
    `/api/conversations/${encodeURIComponent(conversationId)}/cyrano-draft/cancel`,
    {},
  );
}


/* ====================================================================== */
/* Décision proposée — actions (confirm/reject, mode PROPOSE)             */
/* ====================================================================== */

/**
 * Confirme une proposition de décision : exécute le variant choisi côté
 * backend (court-circuit send_menu / arm_flow_run / SalesEngine selon
 * l'action, cf PLAN_CYRANO_MODE_PROPOSE.md §2.1) — le message/l'offre part
 * RÉELLEMENT dans le thread. 409 si la proposition n'est déjà plus
 * "proposed" (double-clic / double-chatteur / expirée) — le caller éteint
 * alors l'atom localement (cf `DecisionProposalCard`).
 */
export async function confirmDecisionProposal(
  proposalId: string,
  variantId: string,
  edits?: {
    message?: string;
    price_cents?: number;
    media_id?: string;
    /** Multi-média (retour Kayla 07-10 « on devrait pouvoir en choisir
     *  plusieurs ») — liste d'ids quand l'opérateur groupe plusieurs médias
     *  dans une offre. Envoyé À LA PLACE de `media_id` dès qu'il y en a >1 (un
     *  seul média continue d'utiliser `media_id`, inchangé). ⚠️ CÔTÉ BACKEND À
     *  IMPLÉMENTER : le worker de confirm doit lire `media_ids` (envoi groupé)
     *  — non branché à ce jour ; en démo les edits sont ignorés (résolution
     *  locale), donc la sélection multiple s'exerce sans backend. */
    media_ids?: string[];
    /** Motif de correction (mission A2, `edits.reason`) — envoyé UNIQUEMENT
     *  quand `message` a réellement changé côté opérateur (cf
     *  `@/regie-test/correctionReasons.needsCorrectionReason`) : négatif
     *  DPO au même titre que `reason` de `rejectDecisionProposal`, jamais
     *  renommé. */
    reason?: string;
  },
  opts?: {
    /** Temps d'affichage de la carte côté décideur (ms) avant la décision —
     *  métrique J1 anti-rubber-stamping. Le backend l'accepte au TOP-LEVEL du
     *  body (`dwell_ms`, int ≥ 0, nullable) et le journalise dans `edits`
     *  (JSONB) sans jamais l'appliquer à l'envoi. */
    dwellMs?: number;
  },
): Promise<void> {
  // Le backend accepte `{ variant_id, edits?, dwell_ms? }` et APPLIQUE
  // `edits.message` (string) + `edits.price_cents` (int, cents, clampé aux
  // `price_bounds` du variant côté serveur) + `edits.media_id` (swap média,
  // dispatch v2 — cf CONTRAT RÉGIE DISPATCH "Confirm body") + `edits.reason`
  // (motif de correction, mission A2 — journalisé, n'affecte pas l'envoi) à
  // l'exécution (cf PLAN_CYRANO_MODE_PROPOSE.md §5). On n'inclut `edits` que
  // si au moins un champ a été fourni — la carte Conversations historique
  // appelle sans edits et poste donc exactement `{ variant_id }` comme avant
  // (rétro-compat).
  const body: {
    variant_id: string;
    edits?: {
      message?: string;
      price_cents?: number;
      media_id?: string;
      reason?: string;
    };
    dwell_ms?: number;
  } = { variant_id: variantId };
  if (
    edits &&
    (edits.message !== undefined ||
      edits.price_cents !== undefined ||
      edits.media_id !== undefined ||
      edits.reason !== undefined)
  ) {
    body.edits = edits;
  }
  if (opts?.dwellMs !== undefined && Number.isFinite(opts.dwellMs)) {
    body.dwell_ms = Math.max(0, Math.round(opts.dwellMs));
  }
  await cyranoApi.post(
    `/api/decisions/${encodeURIComponent(proposalId)}/confirm`,
    body,
  );
}

/** Rejette une proposition (« Passer » / « Refuser ») : verdict `rejected`
 *  journalisé côté backend (négatif DPO), rien n'est envoyé. `reason` optionnel
 *  (slug stable, ex. `trop_pushy`) — omis quand le caller ne motive pas (la
 *  carte Conversations historique poste `{}` comme avant). Même sémantique 409
 *  que `confirmDecisionProposal`. */
export async function rejectDecisionProposal(
  proposalId: string,
  reason?: string,
): Promise<void> {
  const body: { reason?: string } = {};
  if (reason) body.reason = reason;
  await cyranoApi.post(
    `/api/decisions/${encodeURIComponent(proposalId)}/reject`,
    body,
  );
}


/**
 * Marque une conversation « lue » de façon EXPLICITE. À appeler UNIQUEMENT sur
 * une action VOLONTAIRE de l'opérateur — clic « Marquer comme lue » ou envoi
 * d'un message — JAMAIS à la simple ouverture d'une conversation.
 *
 * Décision produit 2026-06-20 : « lu » = action explicite. Ouvrir une conv ne la
 * marque plus lue (ni grisé équipe, ni reçu au fan) — un opérateur (ou un
 * manager en mode fantôme) peut consulter sans laisser de trace. Le grisage et
 * le reçu « Vue » ne partent qu'au clic « Marquer comme lue » ou à l'envoi.
 *
 * Deux effets :
 *   1. État serveur PARTAGÉ (`admin_read`) → la card passe grisée pour TOUTE
 *      l'équipe (écriture Redis, zéro risque de ban).
 *   2. Reçu « Vue » au fan via le bot Business (`readBusinessMessage`, Bot API
 *      native, plus de MTProto) — POST /mark-seen-telegram. Fail-open.
 *
 * NB : l'action GROUPÉE (sélection multiple) reste sur `useMarkConversationsRead`
 * seul (grisé équipe SANS reçu Telegram — pas de risque de ban en masse).
 */
export function useMarkConversationRead(): (
  conversationId: string | undefined,
) => Promise<void> {
  const markReadShared = useMarkConversationsRead();
  return useCallback(
    async (conversationId: string | undefined) => {
      if (!conversationId) return;
      await Promise.allSettled([
        markReadShared([conversationId]),
        markReadOnTelegram(conversationId),
      ]);
    },
    [markReadShared],
  );
}


/* ====================================================================== */
/* useUpdateFanNote — note operateur libre (Info > Identite)               */
/* ====================================================================== */

/**
 * Mutation : PUT /api/conversations/{id}/notes.
 *
 * Optimistic update sur la conversation detail (le body apparait sauve
 * instantanement). Sur succes, le DTO renvoye contient updated_by + updated_at
 * du serveur → on remplace le patch optimiste par la verite.
 */
export function useUpdateFanNote(conversationIdAffichee: string | undefined): {
  mutateAsync: (body: string) => Promise<ApiFanNote>;
  isPending: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();
  return useConversationMutation(conversationIdAffichee, {
    mutationFn: async (body: string, conversationId: string): Promise<ApiFanNote> => {
      return cyranoApi.put<ApiFanNote>(
        `/api/conversations/${conversationId}/notes`,
        { body },
      );
    },
    onMutate: async (body, conversationId: string) => {
      await queryClient.cancelQueries({
        queryKey: ["conversations", "detail", conversationId],
      });
      const prev = queryClient.getQueryData<ApiConversationDetail>([
        "conversations",
        "detail",
        conversationId,
      ]);
      queryClient.setQueryData<ApiConversationDetail | undefined>(
        ["conversations", "detail", conversationId],
        (old) => {
          if (!old) return old;
          return {
            ...old,
            notes: {
              body,
              // Garde fan_info intacte — ce mutation ne touche QUE le body.
              fan_info: old.notes?.fan_info ?? {},
              // updated_by/at sont temporaires — overwritten au onSuccess
              // avec les valeurs serveur (qui ont le timestamp DB authoritatif).
              updated_by_workos_user_id:
                old.notes?.updated_by_workos_user_id ?? null,
              updated_at: new Date().toISOString(),
            },
          };
        },
      );
      return { prev };
    },
    onSuccess: (saved, _input, _ctx, conversationId: string) => {
      queryClient.setQueryData<ApiConversationDetail | undefined>(
        ["conversations", "detail", conversationId],
        (old) => {
          if (!old) return old;
          return { ...old, notes: saved };
        },
      );
    },
    onError: (_err, _input, ctx, conversationId: string) => {
      if (!conversationId || !ctx?.prev) return;
      // Rollback : remet la note d'avant edit pour pas afficher "sauve"
      // alors qu'on a perdu la modif. L'editeur cote UI affichera l'erreur
      // via mutation.error.
      queryClient.setQueryData(
        ["conversations", "detail", conversationId],
        ctx.prev,
      );
    },
  });
}


/* ====================================================================== */
/* useUpdateFanInfo — fiche structuree fan (Info > Identite, bloc Fan)     */
/* ====================================================================== */

/** Patch partiel : ne touche QUE les cles fournies. Passer `null` ou `""`
 *  efface la cle cote serveur (le JSONB ne garde pas les vides). */
export type FanInfoPatch = Partial<Record<keyof ApiFanInfo, string | null>>;

/**
 * Mutation : PATCH /api/conversations/{id}/fan-info.
 *
 * Patch partiel : un seul champ a la fois OU plusieurs. Le serveur merge
 * dans le JSONB existant (ne clobbe pas les autres champs). Optimistic
 * update sur la conversation detail (le champ apparait sauve instantanement).
 */
export function useUpdateFanInfo(conversationIdAffichee: string | undefined): {
  mutateAsync: (patch: FanInfoPatch) => Promise<ApiFanNote>;
  isPending: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();
  return useConversationMutation(conversationIdAffichee, {
    mutationFn: async (patch: FanInfoPatch, conversationId: string): Promise<ApiFanNote> => {
      return cyranoApi.patch<ApiFanNote>(
        `/api/conversations/${conversationId}/fan-info`,
        patch,
      );
    },
    onMutate: async (patch, conversationId: string) => {
      await queryClient.cancelQueries({
        queryKey: ["conversations", "detail", conversationId],
      });
      const prev = queryClient.getQueryData<ApiConversationDetail>([
        "conversations",
        "detail",
        conversationId,
      ]);
      queryClient.setQueryData<ApiConversationDetail | undefined>(
        ["conversations", "detail", conversationId],
        (old) => {
          if (!old) return old;
          // Merge le patch dans fan_info (null / "" => efface la cle).
          const nextInfo: ApiFanInfo = { ...(old.notes?.fan_info ?? {}) };
          for (const [k, v] of Object.entries(patch) as [
            keyof ApiFanInfo,
            string | null | undefined,
          ][]) {
            if (v === null || v === undefined || v === "") {
              delete nextInfo[k];
            } else {
              nextInfo[k] = v;
            }
          }
          return {
            ...old,
            notes: {
              // Garde le body inchange.
              body: old.notes?.body ?? "",
              fan_info: nextInfo,
              updated_by_workos_user_id:
                old.notes?.updated_by_workos_user_id ?? null,
              updated_at: new Date().toISOString(),
            },
          };
        },
      );
      return { prev };
    },
    onSuccess: (saved, _input, _ctx, conversationId: string) => {
      queryClient.setQueryData<ApiConversationDetail | undefined>(
        ["conversations", "detail", conversationId],
        (old) => {
          if (!old) return old;
          return { ...old, notes: saved };
        },
      );
    },
    onError: (_err, _input, ctx, conversationId: string) => {
      if (!conversationId || !ctx?.prev) return;
      queryClient.setQueryData(
        ["conversations", "detail", conversationId],
        ctx.prev,
      );
    },
  });
}


/* ====================================================================== */
/* useToggleCyranoPause — pause/resume Cyrano per conversation             */
/* ====================================================================== */

/** Ce que rend `PATCH /cyrano-paused`. */
export interface ReponseDeLaPause {
  is_paused: boolean;
  /** Un tour a-t-il été relancé en levant la pause ? */
  enqueued?: boolean;
  /** 🩸 Est-ce CYRANO 2.0 qui joue ce tour, et pas l'ancien pipeline ?
   *
   *  Le 25-08, l'opératrice ne pouvait pas le savoir : 2.0 se taisait (un
   *  filet vidait le texte), elle coupait et reprenait pour forcer une
   *  réponse — et c'est l'ANCIEN Cyrano qui écrivait, dans une conversation
   *  qui ne devait jamais l'entendre. Le portier backend a fermé cette porte ;
   *  ce champ dit pourquoi le bouton ne promet plus la même chose. Un tour de
   *  2.0 n'est pas un job en file : il porte sa propre cadence, et **il peut
   *  décider de ne rien dire**.
   *
   *  ⚠️ Optionnel, et pas par politesse : le cockpit se déploie AVANT Cyrano.
   *  Absent ⇒ on n'annonce rien, ce qui est le comportement d'avant, mot pour
   *  mot. */
  par_cyrano2?: boolean;
}

/**
 * PATCH /api/conversations/{id}/cyrano-paused — toggle la pause Cyrano.
 *
 * Quand paused=True, l'AgentWorker skip silencieusement les AgentJobs
 * pour cette conv (le fan peut continuer a ecrire, ses messages sont
 * persistes, mais Cyrano ne repondra pas tant que la pause n'est pas
 * levee).
 *
 * Optimistic update sur la conversation detail.
 */
export function useToggleCyranoPause(conversationIdAffichee: string | undefined): {
  mutateAsync: (isPaused: boolean) => Promise<ReponseDeLaPause>;
  isPending: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();
  return useConversationMutation(conversationIdAffichee, {
    mutationFn: async (isPaused: boolean, conversationId: string) => {
      return cyranoApi.patch<ReponseDeLaPause>(
        `/api/conversations/${conversationId}/cyrano-paused`,
        { is_paused: isPaused },
      );
    },
    onMutate: async (isPaused, conversationId: string) => {
      await cancelConversationCaches(queryClient, conversationId);
      await queryClient.cancelQueries({ queryKey: ["conversations", "list"] });
      const prev = queryClient.getQueryData<ApiConversationDetail>([
        "conversations",
        "detail",
        conversationId,
      ]);
      const prevList = queryClient.getQueryData<ApiConversationListResponse>([
        "conversations",
        "list",
      ]);
      // Bascule is_cyrano_paused dans les DEUX caches : la ConvStatusBar du
      // ChatView lit ce flag depuis le payload monté, qui peut être le
      // chat-lite (la sous-vue chat n'hydrate que lui).
      patchConversationCaches(queryClient, conversationId, (old) =>
        old ? { ...old, is_cyrano_paused: isPaused } : old,
      );
      // Patch optimiste de la liste : la card grid doit basculer GO/STOP
      // immediatement, sans attendre le polling 15s.
      queryClient.setQueryData<ApiConversationListResponse | undefined>(
        ["conversations", "list"],
        (old) => {
          if (!old || !Array.isArray(old.conversations)) return old;
          return {
            ...old,
            conversations: old.conversations.map((c) =>
              c.id === conversationId ? { ...c, is_cyrano_paused: isPaused } : c,
            ),
          };
        },
      );
      return { prev, prevList };
    },
    onError: (_err, isPaused, ctx, conversationId: string) => {
      if (ctx?.prev) {
        queryClient.setQueryData(
          ["conversations", "detail", conversationId],
          ctx.prev,
        );
      }
      // Rollback du flag dans le chat-lite : pas de snapshot complet (seul
      // is_cyrano_paused a été touché optimistement). La valeur d'avant toggle
      // = !isPaused (le toggle dérive de l'état courant).
      queryClient.setQueryData<ApiConversationDetail | undefined>(
        ["conversations", "chat-lite", conversationId],
        (old) => (old ? { ...old, is_cyrano_paused: !isPaused } : old),
      );
      if (ctx?.prevList) {
        queryClient.setQueryData(["conversations", "list"], ctx.prevList);
      }
    },
    onSuccess: (data, _input, _ctx, conversationId: string) => {
      patchConversationCaches(queryClient, conversationId, (old) =>
        old ? { ...old, is_cyrano_paused: data.is_paused } : old,
      );
      // Sync final list — au cas ou l'optimistic a divergé d'un autre op
      // concurrent. Invalidate force un refetch propre.
      rafraichirLaListe(queryClient);
    },
  });
}


/* ====================================================================== */
/* useToggleConversationBot                                                */
/* ====================================================================== */

interface BotPauseStatus {
  conversation_id: string;
  telegram_chat_id: number;
  is_paused: boolean;
}

/**
 * Mutation : POST /api/conversations/{id}/pause | /resume.
 *
 * Optimistic patch sur le cache `conversations/list` (bot_is_paused flip),
 * rollback en erreur. Le polling 5s rafraichira le statut du serveur.
 */
export function useToggleConversationBot(): {
  toggle: (input: { conversationId: string; nextPaused: boolean }) => Promise<BotPauseStatus>;
  isPending: boolean;
} {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (input: {
      conversationId: string;
      nextPaused: boolean;
    }): Promise<BotPauseStatus> => {
      const endpoint = input.nextPaused ? "pause" : "resume";
      return cyranoApi.post<BotPauseStatus>(
        `/api/conversations/${input.conversationId}/${endpoint}`,
        {},
      );
    },
    onMutate: async ({ conversationId, nextPaused }) => {
      await queryClient.cancelQueries({ queryKey: ["conversations", "list"] });
      const prev = queryClient.getQueryData<ApiConversationListResponse>([
        "conversations",
        "list",
      ]);
      queryClient.setQueryData<ApiConversationListResponse | undefined>(
        ["conversations", "list"],
        (old) => {
          if (!old || !Array.isArray(old.conversations)) return old;
          return {
            ...old,
            conversations: old.conversations.map((c) =>
              c.id === conversationId ? { ...c, bot_is_paused: nextPaused } : c,
            ),
          };
        },
      );
      return { prev };
    },
    onError: (_err, _input, ctx) => {
      if (ctx?.prev) {
        queryClient.setQueryData(["conversations", "list"], ctx.prev);
      }
    },
    onSettled: () => {
      rafraichirLaListe(queryClient);
    },
  });

  return {
    toggle: mutation.mutateAsync,
    isPending: mutation.isPending,
  };
}


/* ====================================================================== */
/* Fan tags — catalogue creator-scope + assignations fan-scope             */
/* ====================================================================== */

interface ApiFanTagListResponse {
  tags: ApiFanTag[];
}

interface ApiFanTagAssignmentsResponse {
  assignments: ApiFanTagAssignment[];
}

/** Une suggestion de tag PROPOSÉE par Cyrano (A10 « proposer→confirmer »).
 *  Source : GET /api/conversations/{id}/tag-suggestions. */
export interface ApiTagSuggestion {
  id: string;
  /** Clé stable du tag proposé ("whale" | "time_waster"). */
  tag_key: string;
  /** Nom d'affichage résolu (ex "Whale"). */
  name: string;
  /** Couleur hex de la puce. */
  color: string;
  /** Confiance [0..1] calculée par le proposeur. */
  confidence: number;
  /** Raison courte lisible (ex "a dépensé 250$ sur 4 achats"). */
  rationale: string | null;
  status: string;
  created_at: string;
}

interface ApiTagSuggestionsResponse {
  suggestions: ApiTagSuggestion[];
}

const FAN_TAGS_POLL_MS = 30_000;

/**
 * Nom du tag système « Time Waster ». Un fan tagué est exclu (caché) des
 * conversations côté serveur, et plus aucune génération ne part pour lui.
 * Doit matcher TIME_WASTER_TAG_NAME côté Cyrano
 * (app/domain/models/conversation_tag.py).
 *
 * 🪤 Son attribution n'est PLUS conditionnée depuis le 17-08-2026 : l'UI
 * avertit (cf `timeWaster.ts`) mais ne grise plus rien.
 */
export const TIME_WASTER_TAG_NAME = "Time Waster";

/** True si ce tag est le tag système « Time Waster ». */
export function isTimeWasterTag(tag: {
  name: string;
  is_system: boolean;
}): boolean {
  return tag.is_system && tag.name === TIME_WASTER_TAG_NAME;
}

/**
 * Ce tag peut-il être posé/retiré à la main ?
 *
 * ⛔ **Ce n'est pas `!is_system`**, et c'est tout l'enjeu. « Système » veut dire
 * « le backend gère le cycle de vie de ce tag » — PIN et Time Waster le sont
 * aussi, alors qu'ils sont posés UNIQUEMENT à la main. Les vingt autres (les
 * paliers « Canal … » / « DM … », « Déjà proposé », « Menu envoyé »,
 * « Résiste », « Qualifié », « PAY »…) sont dérivés de faits en base et
 * réconciliés en continu : cocher l'un d'eux ne tenait pas une passe.
 *
 * La règle vit côté Cyrano (`est_posable_a_la_main`) et arrive dans le champ
 * `assignable` — on ne la recopie PAS ici : deux copies divergeraient, et le
 * picker finirait par offrir un tag que le PUT refuse.
 *
 * 🪤 Le repli quand le champ est absent (vieux backend, réponse en cache) est
 * l'ANCIENNE règle, `!is_system` — volontairement plus restrictif que la
 * nouvelle : il n'offre jamais un tag que le serveur refuserait. Il coûte PIN
 * et Time Waster le temps de la fenêtre, ce qui se voit ; l'inverse ne se
 * verrait pas.
 */
export function estPosableALaMain(tag: {
  is_system: boolean;
  assignable?: boolean;
}): boolean {
  return tag.assignable ?? !tag.is_system;
}

/** Réponse de GET /api/conversations/{id}/time-waster-eligibility. */
export interface TimeWasterEligibility {
  eligible: boolean;
  has_purchase: boolean;
  payment_attempts: number;
  min_payment_attempts: number;
  reasons: string[];
}

/**
 * Éligibilité d'un fan au tag « Time Waster ». Sert à griser l'option dans le
 * picker / le menu contextuel AVANT le clic. Chargé uniquement quand `enabled`
 * (ex: picker ouvert) pour éviter une requête par card de la grille.
 */
export function useTimeWasterEligibility(
  conversationId: string | undefined,
  enabled: boolean,
): {
  eligibility: TimeWasterEligibility | undefined;
  isLoading: boolean;
} {
  const q = useReactQuery({
    queryKey: ["time-waster-eligibility", conversationId] as const,
    queryFn: async () => {
      if (!conversationId) throw new Error("conversationId required");
      return cyranoApi.get<TimeWasterEligibility>(
        `/api/conversations/${conversationId}/time-waster-eligibility`,
      );
    },
    enabled: enabled && !!conversationId,
    staleTime: FAN_TAGS_POLL_MS,
  });
  return { eligibility: q.data, isLoading: q.isLoading };
}

/** Liste les tags du createur (catalogue). Par defaut, exclut les archives. */
export function useFanTagsCatalog(
  creatorId: string | undefined,
  options?: { includeArchived?: boolean },
): {
  tags: ApiFanTag[] | undefined;
  isLoading: boolean;
  error: Error | null;
} {
  const includeArchived = options?.includeArchived ?? false;
  const q = useReactQuery({
    queryKey: ["fan-tags", "catalog", creatorId, includeArchived] as const,
    queryFn: async () => {
      if (!creatorId) throw new Error("creatorId required");
      const url = `/api/creators/${creatorId}/fan-tags${
        includeArchived ? "?include_archived=true" : ""
      }`;
      const data = await cyranoApi.get<ApiFanTagListResponse>(url);
      return data.tags;
    },
    enabled: !!creatorId,
    staleTime: FAN_TAGS_POLL_MS,
  });
  return {
    tags: q.data,
    isLoading: q.isLoading,
    error: q.error as Error | null,
  };
}

/** Cree un nouveau tag dans le catalogue du createur. */
export function useCreateFanTag(creatorId: string | undefined): {
  mutateAsync: (input: { name: string; color: string }) => Promise<ApiFanTag>;
  isPending: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (input: { name: string; color: string }) => {
      if (!creatorId) throw new Error("creatorId required");
      return cyranoApi.post<ApiFanTag>(
        `/api/creators/${creatorId}/fan-tags`,
        input,
      );
    },
    onSuccess: (tag) => {
      queryClient.invalidateQueries({
        queryKey: ["fan-tags", "catalog", tag.creator_id],
      });
    },
  });
  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error as Error | null,
  };
}

/** Edite name/color d'un tag. */
export function useUpdateFanTag(creatorId: string | undefined): {
  mutateAsync: (input: {
    tagId: string;
    name?: string;
    color?: string;
  }) => Promise<ApiFanTag>;
  isPending: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (input: {
      tagId: string;
      name?: string;
      color?: string;
    }) => {
      if (!creatorId) throw new Error("creatorId required");
      const { tagId, ...patch } = input;
      return cyranoApi.patch<ApiFanTag>(
        `/api/creators/${creatorId}/fan-tags/${tagId}`,
        patch,
      );
    },
    onSuccess: (tag) => {
      queryClient.invalidateQueries({
        queryKey: ["fan-tags", "catalog", tag.creator_id],
      });
      queryClient.invalidateQueries({ queryKey: ["fan-tags", "assignments"] });
    },
  });
  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error as Error | null,
  };
}

/** Soft delete : archive un tag (disparait du picker). */
export function useArchiveFanTag(creatorId: string | undefined): {
  mutateAsync: (tagId: string) => Promise<ApiFanTag>;
  isPending: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (tagId: string) => {
      if (!creatorId) throw new Error("creatorId required");
      return cyranoApi.post<ApiFanTag>(
        `/api/creators/${creatorId}/fan-tags/${tagId}/archive`,
        {},
      );
    },
    onSuccess: (tag) => {
      queryClient.invalidateQueries({
        queryKey: ["fan-tags", "catalog", tag.creator_id],
      });
      // Le backend cascade-delete les FanTagAssignment a l'archive →
      // invalide tous les caches d'assignments pour que les chips
      // disparaissent immediatement chez tous les fans ouverts.
      queryClient.invalidateQueries({
        queryKey: ["fan-tags", "assignments"],
      });
    },
  });
  return {
    mutateAsync: mutation.mutateAsync,
    isPending: mutation.isPending,
    error: mutation.error as Error | null,
  };
}

/** Liste les tags assignes au fan de cette conversation. */
export function useFanTagAssignments(conversationId: string | undefined): {
  assignments: ApiFanTagAssignment[] | undefined;
  isLoading: boolean;
  error: Error | null;
} {
  const q = useReactQuery({
    queryKey: ["fan-tags", "assignments", conversationId] as const,
    queryFn: async () => {
      if (!conversationId) throw new Error("conversationId required");
      const data = await cyranoApi.get<ApiFanTagAssignmentsResponse>(
        `/api/conversations/${conversationId}/fan-tags`,
      );
      return data.assignments;
    },
    enabled: !!conversationId,
    staleTime: FAN_TAGS_POLL_MS,
  });
  return {
    assignments: q.data,
    isLoading: q.isLoading,
    error: q.error as Error | null,
  };
}

/**
 * Reconcilie l'ensemble des tags assignes au fan vers la liste fournie.
 * Optimistic update : la nouvelle liste apparait instantanement, rollback
 * sur erreur. Le PUT renvoie l'etat serveur autoritaire en onSuccess.
 */
export function useUpdateFanTagAssignments(
  conversationIdAffichee: string | undefined,
): {
  mutateAsync: (tagIds: string[]) => Promise<ApiFanTagAssignment[]>;
  isPending: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();
  const cleDesTags = (id: string) => ["fan-tags", "assignments", id] as const;

  return useConversationMutation(conversationIdAffichee, {
    mutationFn: async (tagIds: string[], conversationId: string) => {
      const res = await cyranoApi.put<ApiFanTagAssignmentsResponse>(
        `/api/conversations/${conversationId}/fan-tags`,
        { tag_ids: tagIds },
      );
      return res.assignments;
    },
    onMutate: async (tagIds, conversationId: string) => {
      const queryKey = cleDesTags(conversationId);
      await queryClient.cancelQueries({ queryKey });
      const prev =
        queryClient.getQueryData<ApiFanTagAssignment[]>(queryKey) ?? [];

      const catalogQueries = queryClient.getQueriesData<ApiFanTag[]>({
        queryKey: ["fan-tags", "catalog"],
      });
      const knownTags = new Map<string, ApiFanTag>();
      for (const [, tags] of catalogQueries) {
        if (Array.isArray(tags)) {
          for (const tag of tags) knownTags.set(tag.id, tag);
        }
      }
      const optimistic: ApiFanTagAssignment[] = tagIds
        .map((id) => knownTags.get(id))
        .filter((t): t is ApiFanTag => !!t)
        .map((tag) => ({
          tag,
          assigned_at: new Date().toISOString(),
          assigned_by_workos_user_id: null,
        }))
        .sort((a, b) => a.tag.name.localeCompare(b.tag.name));

      queryClient.setQueryData<ApiFanTagAssignment[]>(queryKey, optimistic);
      return { prev };
    },
    onSuccess: (assignments, _input, _ctx, conversationId: string) => {
      queryClient.setQueryData(cleDesTags(conversationId), assignments);
      // Poser/retirer « Time Waster » change la visibilité de la conversation
      // dans la liste (masquage serveur) → invalide la liste pour refléter
      // l'apparition / disparition sans attendre le poll.
      void queryClient.invalidateQueries({
        queryKey: ["conversations", "list"],
      });
    },
    onError: (_err, _input, ctx, conversationId: string) => {
      if (ctx?.prev !== undefined) {
        queryClient.setQueryData(cleDesTags(conversationId), ctx.prev);
      }
    },
  });
}

/* ====================================================================== */
/* Tag suggestions — « Cyrano propose, le décideur confirme » (A10)        */
/* ====================================================================== */

/** Suggestions de tags EN ATTENTE pour le fan de cette conversation. Le
 *  backend (re)calcule les candidats à chaque GET (gaté par
 *  FAN_TAG_SUGGESTIONS_ENABLED — répond vide si OFF). */
export function useTagSuggestions(conversationId: string | undefined): {
  suggestions: ApiTagSuggestion[] | undefined;
  isLoading: boolean;
  error: Error | null;
} {
  const q = useReactQuery({
    queryKey: ["tag-suggestions", conversationId] as const,
    queryFn: async () => {
      if (!conversationId) throw new Error("conversationId required");
      const data = await cyranoApi.get<ApiTagSuggestionsResponse>(
        `/api/conversations/${conversationId}/tag-suggestions`,
      );
      return data.suggestions;
    },
    enabled: !!conversationId,
    staleTime: FAN_TAGS_POLL_MS,
  });
  return {
    suggestions: q.data,
    isLoading: q.isLoading,
    error: q.error as Error | null,
  };
}

/** Confirme (✓) ou écarte (✗) une suggestion. `decision` route vers l'endpoint
 *  confirm/dismiss. Un ✓ crée la FanTagAssignment côté backend → on invalide
 *  aussi les assignations + la liste (le tag « Time Waster » change la
 *  visibilité de la conv). Optimistic : la suggestion décidée quitte la carte
 *  immédiatement. */
export function useDecideTagSuggestion(conversationIdAffichee: string | undefined): {
  mutateAsync: (input: {
    suggestionId: string;
    decision: "confirm" | "dismiss";
  }) => Promise<ApiTagSuggestion>;
  isPending: boolean;
  error: Error | null;
} {
  const queryClient = useQueryClient();
  const cleDesSuggestions = (id: string) => ["tag-suggestions", id] as const;

  return useConversationMutation(conversationIdAffichee, {
    mutationFn: async (input: {
      suggestionId: string;
      decision: "confirm" | "dismiss";
    }, conversationId: string) => {
      return cyranoApi.post<ApiTagSuggestion>(
        `/api/conversations/${conversationId}/tag-suggestions/${input.suggestionId}/${input.decision}`,
        {},
      );
    },
    onMutate: async ({ suggestionId }, conversationId: string) => {
      const queryKey = cleDesSuggestions(conversationId);
      await queryClient.cancelQueries({ queryKey });
      const prev = queryClient.getQueryData<ApiTagSuggestion[]>(queryKey) ?? [];
      queryClient.setQueryData<ApiTagSuggestion[]>(
        queryKey,
        prev.filter((s) => s.id !== suggestionId),
      );
      return { prev };
    },
    onSuccess: (_res, { decision }, _ctx, conversationId: string) => {
      if (decision === "confirm") {
        void queryClient.invalidateQueries({ queryKey: ["fan-tags", "assignments"] });
        rafraichirLaListe(queryClient);
      }
      void queryClient.invalidateQueries({
        queryKey: cleDesSuggestions(conversationId),
      });
    },
    onError: (_err, _input, ctx, conversationId: string) => {
      if (ctx?.prev !== undefined) {
        queryClient.setQueryData(cleDesSuggestions(conversationId), ctx.prev);
      }
    },
  });
}
